<template>
  <div class="weather-map-container">
    <!-- Mobile-friendly hamburger menu -->
    <div class="map-controls" :class="{ 'controls-open': controlsOpen }">

      <button class="controls-toggle" @click="controlsOpen = !controlsOpen">
        <svg v-if="controlsOpen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
        </svg>
      </button>
      
      <div class="controls-panel" @click.stop @dblclick.stop @mousedown.stop @touchstart.stop>

        <!-- Weather Layers - only visible in advanced mode -->
        <div v-if="advancedEnabled" class="control-group">
          <div class="d-flex justify-content-between align-items-center">
            <label class="fw-bold mb-0">Layers</label>
            <button @click="showAddLayerModal = true" class="btn btn-sm btn-outline-primary py-0 px-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
              </svg>
            </button>
          </div>
          <div class="layer-list">
            <!-- API Key warning -->
            <div v-if="!hasValidApiKey && activeLayers.length > 0" class="api-key-warning">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
              <span>No API key - tiles won't load</span>
            </div>
            <div v-for="(layer, index) in activeLayers" :key="layer.id" class="layer-item-compact">
              <div class="d-flex align-items-center gap-1">
                <input type="checkbox" class="form-check-input m-0" v-model="layer.visible" @change="toggleLayerVisibility(layer)">
                <span class="layer-label flex-grow-1">
                  {{ getElementDisplayName(layer.element) }}
                  <span v-if="layer.model !== 'default'" class="model-badge">{{ getModelDisplayName(layer.model) }}</span>
                </span>
                <input v-if="layer.visible" type="range" v-model.number="layer.opacity" min="0" max="1" step="0.1"
                       @input="updateLayerOpacity(layer)" class="form-range form-range-sm opacity-slider">
                <span v-if="layerErrors[layer.id]" class="layer-error-badge" :title="layerErrors[layer.id] + ' tile errors'">!</span>
                <button @click="removeLayer(index)" class="btn btn-link text-danger p-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Overlays -->
        <div  v-if="advancedEnabled" class="control-group compact">
          <label class="fw-bold">Overlays</label>
          <div class="d-flex gap-3 align-items-center flex-wrap">
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="cities-toggle" v-model="showGeoJson" @change="toggleGeoJsonLayer">
              <label class="form-check-label small" for="cities-toggle">Wx</label>
            </div>
            <select v-if="showGeoJson" v-model="geoJsonElement" @change="refreshGeoJsonTiles" class="form-control form-control-sm" style="width: auto; min-width: 80px;">
              <option value="temp">Temp</option>
              <option value="precip">Precip</option>
              <option value="wind">Wind</option>
            </select>
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="labels-toggle" v-model="showLabels" @change="toggleLabels" :disabled="!showGeoJson">
              <label class="form-check-label small" :class="{ 'text-muted': !showGeoJson }" for="labels-toggle">Location</label>
            </div>
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="tilegrid-toggle" v-model="showTileGrid" @change="toggleTileGrid">
              <label class="form-check-label small" for="tilegrid-toggle">Tiles</label>
            </div>
          </div>
        </div>

        <!-- Advanced section - collapsed by default, only visible with URL param showadvanced=true -->
        <div v-if="advancedEnabled" class="control-group compact">
          <div class="advanced-header" @click="showAdvanced = !showAdvanced">
            <label class="fw-bold mb-0" style="cursor: pointer;">Advanced</label>
            <svg v-if="showAdvanced" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          <div v-if="showAdvanced" class="advanced-content">
            <div class="control-group compact">
              <label>Units</label>
              <select v-model="effectiveUnitGroup" class="form-control form-control-sm">
                <option value="us">US</option>
                <option value="metric">Metric</option>
              </select>
            </div>
            <div class="mb-2">
              <label >Server</label>
              <select v-model="baseApiUrl" class="form-control form-control-sm">
                 <option value="https://maps.visualcrossing.com">Maps</option>
                  <option value="http://localhost:8080">Localhost</option>
                  <option value="https://weather6.visualcrossing.com">Weather6</option>
                 <!--
                <option value="https://weather5.visualcrossing.com">Weather5</option>
                <option value="http://localhost:8080">Localhost</option>
                -->
              </select>
            </div>
            <!-- Base map -->
            <div class="control-group compact">
              <label>Base Map</label>
              <select v-model="selectedBaseMap" @change="changeBaseMap" class="form-control form-control-sm">
                <option value="carto-light">Light</option>
                <option value="carto-dark">Dark</option>
                <!--<option value="osm">OpenStreetMap</option>-->
                <option value="vcosm1">VC OSM 1</option>
                <option value="vcosm2">VC OSM 2</option>
              </select>
            </div>
            <div class="form-check mb-2">
              <input type="checkbox" class="form-check-input" id="strict-toggle" v-model="strictMode" @change="saveSettings">
              <label class="form-check-label" for="strict-toggle">Strict Mode</label>
            </div>
            <div class="form-check mb-2">
              <input type="checkbox" class="form-check-input" id="force-refresh-toggle" v-model="forceRefresh" @change="onForceRefreshChange">
              <label class="form-check-label" for="force-refresh-toggle">Force Refresh</label>
            </div>
            <div class="d-flex align-items-center gap-2">
              <small class="text-muted">{{ viewInfo.centerPair }}</small>
              <small class="text-muted">z{{ viewInfo.zoom }}</small>
              <button @click="copyCenterToClipboard" class="btn btn-sm btn-link p-0" title="Copy coordinates">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                  <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Layer Modal -->
    <div v-if="showAddLayerModal" class="add-layer-modal" @click.self="showAddLayerModal = false">
      <div class="modal-content-custom" @click.stop @mousedown.stop @touchstart.stop>
        <h6>Add Weather Layer</h6>
        
        <div class="mb-3">
          <label class="form-label small">Model / Source</label>
          <select v-model="newLayerModel" @change="onModelChange" class="form-control form-control-sm">
            <option v-for="(config, modelId) in modelOptions" :key="modelId" :value="modelId">
              {{ getModelDisplayName(modelId) }}
            </option>
          </select>
        </div>
        
        <div class="mb-3">
          <label class="form-label small">Element</label>
          <select v-model="newLayerElement" class="form-control form-control-sm">
            <option v-for="element in availableElements" :key="element" :value="element">
              {{ getElementDisplayName(element) }}
            </option>
          </select>
        </div>
        
        <div class="d-flex gap-2">
          <button @click="showAddLayerModal = false" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
          <button @click="addLayer" class="btn btn-primary btn-sm flex-fill" :disabled="!newLayerElement">Add</button>
        </div>
      </div>
    </div>

    <!-- Markers Modal -->
    <div v-if="showMarkersModal" class="add-layer-modal" @click.self="showMarkersModal = false">
      <div class="modal-content-custom" style="max-width: 400px;" @click.stop @mousedown.stop @touchstart.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Markers</h6>
          <button @click="showMarkersModal = false" class="btn btn-sm btn-link p-0 text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
            </svg>
          </button>
        </div>
        
        <div class="d-flex gap-1 align-items-center mb-2">
          <input type="text" v-model="newMarkerLatLng" @keyup.enter="addMarkerFromInput" 
                 class="form-control form-control-sm flex-grow-1" placeholder="latitude, longitude">
          <button @click="addMarkerFromInput" class="btn btn-sm btn-outline-primary py-0 px-1" title="Add marker">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
            </svg>
          </button>
          <button @click="addMarkerFromGPS" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Add from GPS">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 0a.5.5 0 0 1 .5.5v.518A7.001 7.001 0 0 1 14.982 7.5h.518a.5.5 0 0 1 0 1h-.518A7.001 7.001 0 0 1 8.5 14.982v.518a.5.5 0 0 1-1 0v-.518A7.001 7.001 0 0 1 1.018 8.5H.5a.5.5 0 0 1 0-1h.518A7.001 7.001 0 0 1 7.5 1.018V.5A.5.5 0 0 1 8 0zM8 3a5 5 0 1 0 0 10A5 5 0 0 0 8 3z"/>
            </svg>
          </button>
          <button @click="goToUserLocation" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Go to my location">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
            </svg>
          </button>
        </div>
        
        <div v-if="userMarkers.length === 0" class="text-muted small text-center py-3">
          No markers added yet. Enter coordinates or use GPS to add one.
        </div>
        
        <div v-else class="marker-list" style="max-height: 300px; overflow-y: auto;">
          <div v-for="(m, i) in userMarkers" :key="m.id" class="marker-item" @click="panToMarker(m); showMarkersModal = false;" title="Click to center on marker">
            <span class="marker-dot" :style="{ background: m.color }"></span>
            <span class="marker-coords">{{ m.lat.toFixed(4) }}, {{ m.lng.toFixed(4) }}</span>
            <button @click.stop="copyMarkerCoords(m)" class="marker-copy-btn" title="Copy coordinates">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
                <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
              </svg>
            </button>
            <button @click.stop="removeMarker(i)" class="marker-remove-btn" title="Remove marker">×</button>
          </div>
        </div>
      </div>
    </div>

    <!-- API Integration Modal -->
    <div v-if="showApiIntegrationModal" class="add-layer-modal" @click.self="showApiIntegrationModal = false">
      <div class="modal-content-custom" style="max-width: 600px; max-height: 80vh; overflow-y: auto;" @click.stop @mousedown.stop @touchstart.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">API Integration</h6>
          <button @click="showApiIntegrationModal = false" class="btn btn-sm btn-link p-0 text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
            </svg>
          </button>
        </div>
        
        <p class="small text-muted mb-3">Use these URL patterns to integrate weather tiles into your own mapping application.</p>
        
        <!-- API Key section -->
        <div class="control-group compact mb-3 p-2 bg-light rounded">
          <label class="small fw-bold">API Key</label>
          <div class="d-flex align-items-center gap-1">
            <input 
              type="text" 
              v-model="displayApiKey" 
              class="form-control form-control-sm" 
              placeholder="Enter your API key"
            >
            <button 
              v-if="apiKeyOverride" 
              @click="clearApiKeyOverride" 
              class="btn btn-sm btn-outline-secondary py-0 px-1" 
              title="Clear manual key"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
              </svg>
            </button>
          </div>
          <small class="text-muted">
            <span v-if="hasSession && !apiKeyOverride">Using key from session</span>
            <span v-else-if="apiKeyOverride">Using custom key</span>
            <span v-else>Enter your API key or sign in</span>
          </small>
        </div>
        
        <!-- Current layer URL patterns -->
        <div v-if="activeLayers.length === 0 && !quickSelectActive" class="text-muted small mb-3">
          <em>Select a weather layer to see its URL pattern.</em>
        </div>
        
        <div v-for="layer in visibleLayersForApi" :key="'url-' + layer.id" class="url-pattern-item mb-3">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <strong class="small">{{ getElementDisplayName(layer.element) }} ({{ getModelDisplayName(layer.quickSelectModelId || layer.model) }})</strong>
          </div>
          <div class="mb-2">
            <div class="d-flex justify-content-between align-items-center">
              <label class="small text-muted mb-0">Tile URL:</label>
              <button @click="copyUrlPattern(layer, 'tile')" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Copy Tile URL">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                  <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                </svg>
              </button>
            </div>
            <code class="url-pattern-code">{{ getLayerUrlPattern(layer) }}</code>
          </div>
          <div>
            <div class="d-flex justify-content-between align-items-center">
              <label class="small text-muted mb-0">Legend URL:</label>
              <button @click="copyUrlPattern(layer, 'legend')" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Copy Legend URL">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                  <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                </svg>
              </button>
            </div>
            <code class="url-pattern-code">{{ getLegendUrlPattern(layer) }}</code>
          </div>
        </div>

        <div class="mt-2 pt-2 border-top">
          <strong class="small d-block mb-1">URL Parameters:</strong>
          <ul class="small text-muted mb-0 ps-3">
            <li><code>{z}/{x}/{y}</code> - Tile coordinates</li>
            <li><code>time</code> - ISO datetime or "now"</li>
            <li><code>unitGroup</code> - "us" or "metric"</li>
            <li><code>source</code> - Model/data source (optional)</li>
            <li><code>strict</code> - Strict time matching</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Map container -->
    <div id="map" class="map-view">
      
      <!-- Quick-select layer control (top-left, next to zoom buttons) -->
      <!-- Quick-select layer control - always visible element and model dropdowns -->
      <div class="quick-select-control leaflet-bar" :class="{ 'controls-panel-open': controlsOpen, 'mobile-collapsed': isMobileView && !mobileLayerExpanded }" @click.stop @dblclick.stop @mousedown.stop @touchstart.stop>
        <!-- Mobile toggle button -->
        <button v-if="isMobileView" class="mobile-expand-btn" @click="toggleMobileLayerPanel" :title="mobileLayerExpanded ? 'Collapse layers' : 'Expand layers'">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
            <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
          </svg>
          <span class="mobile-expand-label">{{ elementNames[quickSelectElement] || 'Layers' }}</span>
          <svg v-if="mobileLayerExpanded" xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
          </svg>
        </button>
        <div class="quick-select-row" v-show="!isMobileView || mobileLayerExpanded">
          <div class="d-flex flex-column align-items-start justify-content-between">
            <select v-model="quickSelectElement" @change="onQuickSelectElementChange" class="quick-select-dropdown" title="Weather Element">
              <option v-for="el in quickSelectElements" :key="el" :value="el">
                {{ elementNames[el] || el }}
              </option>
            </select>
            <!-- Opacity slider -->
            <div class="quick-opacity-control mt-2" title="Layer opacity">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1v14zm0 1A8 8 0 1 1 8 0a8 8 0 0 1 0 16z"/>
              </svg>
              <input type="range" v-model.number="quickSelectOpacity" min="0.1" max="1" step="0.1" 
                    @input="updateQuickSelectOpacity" class="quick-opacity-slider">
            </div>
          </div>
          <!-- Model selector: dropdown on narrow screens, button bar on wide screens -->
          <select v-if="!isWideMap" v-model="quickSelectModel" @change="onQuickSelectModelChange" class="quick-select-dropdown" title="Data Model">
            <option v-for="m in quickSelectModelsForElement" :key="m.id" :value="m.id" :disabled="!m.inBounds" :class="{ 'out-of-bounds': !m.inBounds }">
              {{ modelNames[m.id] || m.id }}{{ !m.inBounds ? ' ⊘' : '' }}
            </option>
          </select>
          <div v-else class="model-button-bar" :class="{ 'multi-mode': multiLayerMode }">
            <!-- Multi-layer mode toggle -->
            <button 
              @click="toggleMultiLayerMode" 
              class="model-btn multi-toggle-btn" 
              :class="{ active: multiLayerMode }"
              :title="multiLayerMode ? 'Multi-layer mode ON (' + activeQuickSelectModels.length + ' layers) - click to exit' : 'Toggle multi-layer mode (compare layers across elements)'">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8.235 1.559a.5.5 0 0 0-.47 0l-7.5 4a.5.5 0 0 0 0 .882L3.188 8 .264 9.559a.5.5 0 0 0 0 .882l7.5 4a.5.5 0 0 0 .47 0l7.5-4a.5.5 0 0 0 0-.882L12.813 8l2.922-1.559a.5.5 0 0 0 0-.882l-7.5-4zM8 9.433 1.562 6 8 2.567 14.438 6 8 9.433z"/>
              </svg>
              <span v-if="multiLayerMode && activeQuickSelectModels.length > 0" class="layer-count-badge">{{ activeQuickSelectModels.length }}</span>
            </button>
            <template v-for="(m, index) in quickSelectModelsForElement" :key="m.id">
              <button 
                @click="m.inBounds && selectModel(m.id)" 
                class="model-btn" 
                :class="{ active: isModelActive(m.id), 'out-of-bounds': !m.inBounds, 'priority-btn': m.isHybrid || m.isRadar }"
                :title="(modelNames[m.id] || m.id) + (!m.inBounds ? ' (outside coverage area)' : '') + (multiLayerMode ? ' (click to toggle)' : '')"
                :disabled="!m.inBounds"
              >
                {{ modelNames[m.id] || m.id }}
              </button>
              <span v-if="m.isRadar && index < quickSelectModelsForElement.length - 1" class="model-separator"></span>
            </template>
          </div>
          <!--
          <button v-if="quickSelectActive" @click="clearQuickSelectLayer" class="quick-select-clear-btn" title="Clear layer">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
            </svg>
          </button>
          -->

        </div>
        <div class="quick-select-secondary" v-show="!isMobileView || mobileLayerExpanded">
          <!-- Tool buttons -->
          <button @click="showMarkersModal = true" class="quick-tool-btn" title="Markers">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
            </svg>
            <span v-if="userMarkers.length" class="tool-badge">{{ userMarkers.length }}</span>
          </button>
          <button @click="showApiIntegrationModal = true" class="quick-tool-btn" title="API Integration">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M10.478 1.647a.5.5 0 1 0-.956-.294l-4 13a.5.5 0 0 0 .956.294l4-13zM4.854 4.146a.5.5 0 0 1 0 .708L1.707 8l3.147 3.146a.5.5 0 0 1-.708.708l-3.5-3.5a.5.5 0 0 1 0-.708l3.5-3.5a.5.5 0 0 1 .708 0zm6.292 0a.5.5 0 0 0 0 .708L14.293 8l-3.147 3.146a.5.5 0 0 0 .708.708l3.5-3.5a.5.5 0 0 0 0-.708l-3.5-3.5a.5.5 0 0 0-.708 0z"/>
            </svg>
          </button>
          
          <div class="quick-select-divider"></div>
          
          <select v-model="quickOverlay" @change="onQuickOverlayChange" class="quick-overlay-select" title="Weather Overlay">
            <option value="none">Overlay: None</option>
            <option value="temp">Overlay: Temp</option>
            <option value="precip">Overlay: Precip</option>
            <option value="wind">Overlay: Wind</option>
            <option value="lightning">Overlay: Lightning</option>
            <option value="hail">Overlay: Hail</option>
          </select>
          <label v-if="quickOverlay !== 'none'" class="quick-location-toggle" title="Show location labels">
            <input type="checkbox" v-model="showLabels" @change="toggleLabels">
            <span>Labels</span>
          </label>
          
          <!-- Spacer to push errors to the right -->
          <div class="quick-select-spacer"></div>
          
          <!-- Error indicators -->
          <div v-if="!hasValidApiKey" class="quick-error-indicator" title="No API key - tiles won't load">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zM8 4a.905.905 0 0 1 .9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 4zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
            </svg>
            <span>No Key</span>
          </div>
          <div v-if="tileErrorCount > 0" class="quick-error-indicator error" @click="dismissTileError" title="Tile loading errors - click to dismiss">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
            </svg>
            <span>{{ tileErrorCount }}</span>
          </div>
        </div>
      </div>
      
      <!-- Animation Control Bar (compact, near time display) -->
      <div class="animation-control-bar" :class="{ 'with-timeline': showTimelinePanel && animationTimeKeys.length > 0, 'mobile-collapsed': isMobileView && !mobileAnimExpanded }" @click.stop @dblclick.stop @mousedown.stop @touchstart.stop>
        <!-- Mobile toggle button -->
        <button v-if="isMobileView" class="mobile-expand-btn anim-toggle" @click="toggleMobileAnimPanel" :title="mobileAnimExpanded ? 'Collapse animation' : 'Expand animation'">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
          </svg>
          <span class="mobile-time-preview">{{ shortFormattedTime }}</span>
          <svg v-if="mobileAnimExpanded" xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
          </svg>
        </button>
        <div class="control-bar-main" v-show="!isMobileView || mobileAnimExpanded">
          <!-- Time display -->
          <div class="time-display-inline" @click="showDateTimePicker = true" title="Click to set time">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
            </svg>
            <span class="time-text">{{ formattedTime }}</span>
          </div>
          
          <!-- Reset to now button (separate from date picker) -->
          <button @click="resetToNow" class="anim-btn reset-now-btn-separate" title="Reset to current time">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
            </svg>
          </button>
          
          <!-- Playback buttons -->
          <div class="playback-buttons">
            <button @click="decrementTime" class="anim-btn" title="Previous frame">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M.5 3.5A.5.5 0 0 1 1 4v3.248l6.267-3.636c.52-.302 1.233.043 1.233.696v2.94l6.267-3.636c.52-.302 1.233.043 1.233.696v7.384c0 .653-.713.998-1.233.696L8.5 8.752v2.94c0 .653-.713.998-1.233.696L1 8.752V12a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5z"/>
              </svg>
            </button>
            <button @click="toggleMoviePlay" class="anim-btn play-btn" :disabled="isPreloadingFrames" :title="isPlaying ? 'Pause' : 'Play'">
              <svg v-if="isPreloadingFrames" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="spin" viewBox="0 0 16 16">
                <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
              </svg>
              <svg v-else-if="isPlaying" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
              </svg>
            </button>
            <button @click="incrementTime" class="anim-btn" title="Next frame">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M15.5 3.5a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-1 0V8.752l-6.267 3.636c-.52.302-1.233-.043-1.233-.696v-2.94l-6.267 3.636C.713 12.69 0 12.345 0 11.692V4.308c0-.653.713-.998 1.233-.696L7.5 7.248v-2.94c0-.653.713-.998 1.233-.696L15 7.248V4a.5.5 0 0 1 .5-.5z"/>
              </svg>
            </button>
          </div>
          
          <!-- Expand timeline toggle -->
          <button @click="toggleTimelinePanel" class="anim-btn expand-btn" :class="{ active: showTimelinePanel }" title="Show timeline">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zM1 4a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 4zm0 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 12z"/>
            </svg>
          </button>
          
          <!-- Settings toggle -->
          <button @click="showAnimationSettings = !showAnimationSettings" class="anim-btn settings-btn" :class="{ active: showAnimationSettings }" title="Animation settings">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z"/>
            </svg>
          </button>
        </div>
        
        <!-- Timeline row (inside control bar, shows when toggled) -->
        <div v-if="showTimelinePanel && animationTimeKeys.length > 0 && (!isMobileView || mobileAnimExpanded)" class="control-bar-timeline">
          <span class="timeline-label start">{{ timelineStartLabel }}</span>
          <div class="timeline-slider-container">
            <input type="range" 
                   class="timeline-slider"
                   :min="0" 
                   :max="animationTimeKeys.length - 1" 
                   :value="animationFrameIndex"
                   @input="seekToFrame(parseInt($event.target.value))"
                   @mousedown="onTimelineDragStart"
                   @mouseup="onTimelineDragEnd"
                   @touchstart="onTimelineDragStart"
                   @touchend="onTimelineDragEnd">
            <div class="timeline-current-time">{{ timelineCurrentLabel }}</div>
          </div>
          <span class="timeline-label end">{{ timelineEndLabel }}</span>
        </div>
      </div>
      
      <!-- Animation Settings Panel (expandable) -->
      <div v-if="showAnimationSettings" class="animation-settings-panel" @click.stop @dblclick.stop @mousedown.stop @touchstart.stop>
        <div class="settings-header">
          <strong>Animation Settings</strong>
          <button @click="showAnimationSettings = false" class="close-btn">×</button>
        </div>
        
        <!-- Speed control -->
        <div class="setting-row">
          <label>Speed: {{ playbackSpeed }}x</label>
          <input type="range" v-model.number="playbackSpeed" min="0.5" max="3" step="0.5" 
                 @input="adjustPlaybackSpeed" class="form-range">
        </div>
        
        <!-- Time step control -->
        <div class="setting-row">
          <div class="d-flex justify-content-between align-items-center">
            <label class="mb-0">Time Step: {{ effectiveTimeStepLabel }}</label>
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="anim-auto-skip" v-model="autoTimeSkip">
              <label class="form-check-label small" for="anim-auto-skip">Auto</label>
            </div>
          </div>
          <div v-if="!autoTimeSkip" class="d-flex align-items-center gap-2 mt-1">
            <button @click="decreaseTimeSkip" class="btn btn-sm btn-outline-secondary py-0 px-2">−</button>
            <span class="small">{{ manualTimeSkipLabel }}</span>
            <button @click="increaseTimeSkip" class="btn btn-sm btn-outline-secondary py-0 px-2">+</button>
          </div>
        </div>
        
        <!-- Loop window control (frames) -->
        <div class="setting-row">
          <!-- Dual slider row: Start (back) and End (forward) -->
          <div class="dual-slider-row">
            <div class="slider-col slider-col-start">
              <input type="range" 
                     :value="effectiveLoopSteps.stepsBack" 
                     @input="customStepsBack = parseInt($event.target.value)"
                     :min="0" 
                     :max="maxFramesBack" 
                     step="1" 
                     class="form-range slider-reversed">
              <span class="slider-time-label-below">{{ loopStartTimeLabel }}</span>
            </div>
            <div class="slider-col slider-col-end">
              <input type="range" 
                     :value="effectiveLoopSteps.stepsForward" 
                     @input="customStepsForward = parseInt($event.target.value)"
                     :min="0" 
                     :max="maxFramesFwd" 
                     step="1" 
                     class="form-range">
              <span class="slider-time-label-below">{{ loopEndTimeLabel }}</span>
            </div>
          </div>
          
          <div class="loop-footer">
            <span class="loop-debug-info">{{ activeLoopSteps.stepsBack }}/{{ activeLoopSteps.stepsForward }}</span>
            <button v-if="customStepsBack !== null || customStepsForward !== null" 
                    @click="resetLoopWindow" 
                    class="loop-reset-btn" 
                    title="Reset to auto">
              reset
            </button>
          </div>
        </div>
        
        <!-- Advanced section (collapsible) -->
        <div class="setting-row advanced-section">
          <div class="advanced-toggle" @click="showAdvancedAnimation = !showAdvancedAnimation">
            <span class="advanced-toggle-label">Advanced</span>
            <svg v-if="showAdvancedAnimation" xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          
          <div v-if="showAdvancedAnimation" class="advanced-content-anim">
            <!-- Animation interpolation mode -->
            <div class="setting-row-inner">
              <div class="d-flex justify-content-between align-items-center">
                <label class="mb-0">Interpolation</label>
                <div class="btn-group btn-group-sm" role="group">
                  <button type="button" class="btn btn-outline-secondary py-0 px-2" 
                          :class="{ active: smoothAnimationMode === 'off' }" 
                          @click="setSmoothMode('off')" title="No interpolation">Off</button>
                  <button type="button" class="btn btn-outline-secondary py-0 px-2" 
                          :class="{ active: smoothAnimationMode === 'crossfade' }" 
                          @click="setSmoothMode('crossfade')" title="Simple alpha crossfade">Fade</button>
                  <button type="button" class="btn btn-outline-secondary py-0 px-2" 
                          :class="{ active: smoothAnimationMode === 'morph' }" 
                          @click="setSmoothMode('morph')" title="Face-to-face morphing with motion">Morph</button>
                  <button type="button" class="btn btn-outline-secondary py-0 px-2" 
                          :class="{ active: smoothAnimationMode === 'motion' }" 
                          @click="setSmoothMode('motion')" title="Pure motion extrapolation">Motion</button>
                </div>
              </div>
              <div v-if="smoothAnimationMode === 'crossfade'" class="mt-1 small text-muted">
                Simple alpha blend between frames
              </div>
              <div v-if="smoothAnimationMode === 'morph'" class="mt-1 small text-muted">
                WebGL face-to-face morph (smooth transitions)
              </div>
              <div v-if="smoothAnimationMode === 'motion'" class="mt-1 small text-muted">
                WebGL motion extrapolation (fluid but may jump)
              </div>
            </div>
            
            <!-- Morph Debug Mode -->
            <div v-if="smoothAnimationMode === 'morph' || smoothAnimationMode === 'motion'" class="setting-row-inner">
              <div class="d-flex justify-content-between align-items-center">
                <label class="mb-0">Debug Morph</label>
                <div>
                  <button v-if="canvasFrameCache.length < 2" 
                          type="button" class="btn btn-sm btn-outline-primary py-0 px-2 me-1"
                          @click="preloadCanvasFrames"
                          :disabled="isPreloadingFrames">
                    {{ isPreloadingFrames ? 'Loading...' : 'Load Frames' }}
                  </button>
                  <button v-if="canvasFrameCache.length > 1"
                          type="button" class="btn btn-sm py-0 px-2"
                          :class="morphDebugMode ? 'btn-warning' : 'btn-outline-secondary'"
                          @click="toggleMorphDebug">
                    {{ morphDebugMode ? 'Exit Debug' : 'Debug' }}
                  </button>
                </div>
              </div>
              <div v-if="canvasFrameCache.length > 0" class="small text-muted">
                {{ canvasFrameCache.length }} frames loaded
              </div>
              <div v-if="morphDebugMode && canvasFrameCache.length > 1" class="mt-2">
                <div class="small mb-1">Frame pair: {{ morphDebugFrameIndex }} → {{ (morphDebugFrameIndex + 1) % canvasFrameCache.length }}</div>
                <div class="d-flex gap-1 mb-2">
                  <button @click="morphDebugFrameIndex = Math.max(0, morphDebugFrameIndex - 1)" 
                          class="btn btn-sm btn-outline-secondary py-0">◀ Prev</button>
                  <button @click="morphDebugFrameIndex = Math.min(canvasFrameCache.length - 1, morphDebugFrameIndex + 1)" 
                          class="btn btn-sm btn-outline-secondary py-0">Next ▶</button>
                </div>
                <div class="small mb-1">t = {{ morphDebugT.toFixed(2) }}</div>
                <input type="range" v-model.number="morphDebugT" 
                       min="0" max="1" step="0.01" 
                       class="form-range"
                       @input="renderMorphDebugFrame">
                <div class="d-flex gap-1 mt-1">
                  <button @click="setMorphDebugT(0)" class="btn btn-sm btn-outline-secondary py-0 flex-fill">t=0</button>
                  <button @click="setMorphDebugT(0.5)" class="btn btn-sm btn-outline-secondary py-0 flex-fill">0.5</button>
                  <button @click="setMorphDebugT(1)" class="btn btn-sm btn-outline-secondary py-0 flex-fill">t=1</button>
                </div>
                <div class="mt-2">
                  <button @click="showFramePairBoundary" class="btn btn-sm btn-outline-info py-0 w-100">
                    Show Boundary (t=1 vs next t=0)
                  </button>
                </div>
                <div class="small text-muted mt-2">
                  <div>Motion: {{ getMorphDebugMotionInfo() }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- DateTime picker overlay -->
      <div v-if="showDateTimePicker" class="datetime-picker-modal" @click.stop @dblclick.stop @mousedown.stop @touchstart.stop>
        <div class="modal-content-custom">
          <h6>Select Date & Time (UTC)</h6>
          <input type="datetime-local" :value="dateTimeLocalValue" 
                 :min="modelTimeRangeMin" :max="modelTimeRangeMax"
                 @change="onDateTimeInput" class="form-control">
          <div v-if="modelTimeRange" class="small text-muted mt-1">
            Model run: {{ formatModelRunTime }} | Valid: {{ formatModelTimeRangeLabel }}
          </div>
          <div class="d-flex gap-2 mt-2">
            <button @click="showDateTimePicker = false" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
            <button @click="applyDateTime" class="btn btn-primary btn-sm flex-fill">Apply</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tile Error Banner -->
    <div v-if="tileError || tileErrorCount > 0" class="tile-error-banner alert alert-danger d-flex align-items-center">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="me-2 flex-shrink-0" viewBox="0 0 16 16">
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
      </svg>
      <div class="flex-grow-1">
        <strong>Map Tile Error</strong>
        <span v-if="tileErrorCount > 0" class="badge bg-danger ms-1">{{ tileErrorCount }}</span>
        <p class="mb-0 small">{{ tileError || 'Failed to load map tiles. Check your API key or connection.' }}</p>
      </div>
      <button @click="dismissTileError" class="btn btn-sm btn-outline-danger ms-2" title="Dismiss">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
        </svg>
      </button>
    </div>

    <!-- Animation Preload Overlay -->
    <div v-if="isPreloadingFrames && showPreloadContent" class="preload-overlay">
      <div class="preload-content">
        <svg class="spin" xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
        </svg>
        <div class="preload-text">Loading animation frames...</div>
        <div class="preload-progress">
          <div class="preload-progress-bar" :style="{ width: preloadProgress + '%' }"></div>
        </div>
        <div class="preload-percent">{{ preloadProgress }}%</div>
      </div>
    </div>

    <!-- Legend (positioned on map) - now shows all visible layers -->
    <div v-if="visibleLayerLegends.length > 0" class="legend-container" :class="{ 'legend-collapsed': legendCollapsed }" @click.stop @dblclick.stop @mousedown.stop @touchstart.stop>
      <div class="legend-header" @click="legendCollapsed = !legendCollapsed">
        <strong>Legend</strong>
        <svg v-if="legendCollapsed" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
        </svg>
      </div>
      <div v-if="!legendCollapsed" class="legend-content">
        <div v-for="legend in visibleLayerLegends" :key="legend.layerId" class="legend-item">
          <div class="legend-item-header" @click="toggleLegendExpand(legend.layerId)">
            <div class="legend-item-title-container">
              <span class="legend-item-title">{{ legend.title }}</span>
              <span v-if="legend.modelRunInfo" class="legend-model-run" v-html="legend.modelRunInfo"></span>
            </div>
            <svg v-if="expandedLegends[legend.layerId]" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          <div v-if="expandedLegends[legend.layerId]" class="legend-item-content" v-html="legend.html"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { patchLeaflet } from "/src/utils/leaflet-patches.js";
patchLeaflet();
import { 
  MODEL_OPTIONS, 
  MODEL_NAMES, 
  QUICK_SELECT_MODELS, 
  loadModelsFromApi,
  getIntervalForHoursOffset,
  getModelTimeRange,
  snapTimeToModelInterval,
  isTimeValidForModel
} from "/src/data/weatherMapLayers.js";
import { createAlertVectorLayer } from "/src/weather-map/WxAlertVectorLayer.js";

export default {
  name: "WeatherMapAi",
  
  props: {
    // Filter to only show these models (null/undefined means show all)
    allowedModels: {
      type: Array,
      default: null
    },
    // Filter to only show these elements (null/undefined means show all)
    allowedElements: {
      type: Array,
      default: null
    },
    // Initial element to select
    initialElement: {
      type: String,
      default: null
    },
    // Initial model to select
    initialModel: {
      type: String,
      default: null
    },
    // Unit group (metric, us, uk, base) - syncs with parent component
    unitGroup: {
      type: String,
      default: null  // null means use internal state
    },
    // Skip loading default layers/settings - use when controlled by parent component
    skipDefaultLoad: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      // ---- Session/Auth State ----
      // These are populated from window.vcCommunicator on mount
      sessionId: null,
      sessionState: null,
      apiKeyOverride: '', // User-provided API key override
      
      // ---- Tile Error State ----
      tileError: null, // Error message to display when tiles fail
      tileErrorTimeout: null, // Timeout to auto-dismiss error
      tileErrorCount: 0, // Total count of tile errors since last clear
      layerErrors: {}, // Track error count per layer id
      
      map: null,
      mapReady: false,
      controlsOpen: false,
      legendCollapsed: false,
      showAdvanced: false,
      advancedEnabled: false, // Only show Advanced section when URL has showadvanced=true
      showIntegration: false,
      strictMode: true, // Send strict=true/false to map tile API
      forceRefresh: false, // Send refresh=true to force tile regeneration
      expandedLegends: {}, // Track which legend items are expanded
      
      // Model definitions with elements and time settings
      // Each model has default timeSkip/loopSteps, elements can override
      // Model/element configuration based on weather_map_layers.json
      // Each model defines available elements with animation settings (timeSkip in minutes)
      // Model options - imported from weatherMapLayers.js
      modelOptions: MODEL_OPTIONS,
      
      // Element display names
      elementNames: {
        precipcomposite: "Precipitation Composite",
        preciprate: "Precipitation Rate",
        preciptype: "Precipitation Type",
        precip: "Precipitation",
        hailsize: "Hail Size",
        temp: "Temperature",
        feelslike: "Feels Like",
        dew: "Dew Point",
        humidity: "Humidity",
        cloudcover: "Cloud Cover",
        sealevelpressure: "Sea Level Pressure",
        windspeed: "Wind Speed",
        winddir: "Wind Direction",
        wind: "Wind",
        windgust: "Wind Gust",
        alerts: "Weather Alerts",
        alertswebp: "Weather Alerts (Image)",
        cloudtopheight: "Cloud Top Height",
        cloudtoptemp: "Cloud Top Temperature",
        cloudopticaldepth: "Cloud Optical Depth",
        cloudphase: "Cloud Phase",
        solarradiation: "Solar Radiation",
        abi_rad_c02: "ABI Radiance C02",
        abi_rad_c07: "ABI Radiance C07",
        reflectivity: "Radar Reflectivity",
        snowdepth: "Snow Depth",
        visibility: "Visibility",
        uvindex: "UV Index",
        cape: "CAPE",
        cin: "CIN",
        liftedindex: "Lifted Index",
        precipprob: "Precipitation Probability",
        snow: "Snowfall",
        severerisk: "Severe Weather Risk",
        lightning: "Lightning",
        hail: "Hail",
        fog: "Fog",
        freezinglevel: "Freezing Level",
        ceiling: "Ceiling",
        surfacepressure: "Surface Pressure",
        accprecip: "Accumulated Precipitation",
        accsnow: "Accumulated Snow",
        stormtracks: "Storm Tracks",
        height500mb: "500mb Height",
        temp_spread: "Temperature Spread",
        sealevelpressure_spread: "Sea Level Pressure Spread",
        height500mb_spread: "500mb Height Spread",
        winddir_spread: "Wind Direction Spread",
        windspeed_spread: "Wind Speed Spread"
      },
      
      // Model display names - imported from weatherMapLayers.js
      modelNames: MODEL_NAMES,
      
      // Quick-select model list - updated from API when available
      quickSelectModels: QUICK_SELECT_MODELS,
      
      // Flag to track if models were loaded from API
      modelsLoadedFromApi: false,
      
      // Quick-select layer control state
      quickSelectElement: 'precipcomposite',  // Currently selected element in quick-select
      quickSelectModel: 'hybrid',             // Currently selected model in quick-select
      quickSelectActive: false,               // Whether quick-select layer is visible
      quickSelectOpacity: 0.8,                // Opacity of the quick-select layer (0-1)
      quickOverlay: 'none',                   // Quick overlay selection (none, temp, precip, wind)
      multiLayerMode: false,                  // Multi-layer mode: model buttons act as toggles
      activeQuickSelectModels: [],            // List of active {element, model} pairs in multi-layer mode
      isWideMap: false,                       // True when map width > 800px (show button bar vs dropdown)
      isMobileView: false,                    // True when in mobile view (< 600px width)
      mobileLayerExpanded: false,             // Mobile: whether layer controls are expanded (default collapsed)
      mobileAnimExpanded: false,              // Mobile: whether animation controls are expanded (default collapsed)
      
      // Active layers on the map
      activeLayers: [],
      nextLayerId: 1,
      
      // Add layer modal
      showAddLayerModal: false,
      newLayerModel: 'Default',
      newLayerElement: 'precipcomposite',
      
      // Markers modal
      showMarkersModal: false,
      
      // API Integration modal
      showApiIntegrationModal: false,
      
      // Base maps
      selectedBaseMap: 'carto-light',
      baseMaps: {
        'carto-light': {
          url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
          attribution: '&copy; <a href="https://carto.com">CARTO</a>'
        },
        'carto-dark': {
          url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
          attribution: '&copy; <a href="https://carto.com">CARTO</a>'
        },
        'osm': {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        },
        'satellite': {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: 'Tiles &copy; Esri'
        },
        'vcosm1': {
          url: 'https://osm1.visualcrossing.com/osm/{z}/{x}/{y}.png',
          attribution: 'Tiles &copy; VC'
        },
        'vcosm2': {
          url: 'https://osm2.visualcrossing.com/osm/{z}/{x}/{y}.png',
          attribution: 'Tiles &copy; VC'
        }
      },
      baseLayer: null,
      labelLayer: null,
      showLabels: false,
      
      // Tile grid debug overlay
      showTileGrid: false,
      tileGridLayer: null,
      
      // GeoJSON cities layer
      showGeoJson: false,
      geoJsonElement: 'temp',
      geoJsonLayer: null,
      geoJsonTiles: {},
      geoJsonAbortControllers: new Map(),
      geoJsonMarkerData: [], // Store marker data for global decluttering
      geoJsonDeclutterTimeout: null, // Debounce timer for declutter
      
      // Time management
      currentTime: null,
      isPlaying: false,
      playbackSpeed: 1.0,
      movieInterval: null,
      showDateTimePicker: false,
      tempDateTimeValue: '',
      autoTimeSkip: true,
      manualTimeSkip: 10,
      timeSkipPresets: [1, 2, 5, 10, 15, 30, 60, 180, 360],
      
      // Animation UI
      showAnimationSettings: false, // Expandable panel for speed/loop settings
      showAdvancedAnimation: false, // Collapsed advanced section in animation settings
      showTimelinePanel: false, // Expandable panel for timeline slider
      // Custom loop window as step counts (user-adjustable)
      // null means use the auto-calculated activeLoopSteps
      customStepsBack: null,    // Number of steps back from current time
      customStepsForward: null, // Number of steps forward from current time
      // Max bounds for frame sliders (number of animation frames)
      maxFramesBack: 100,   // Max frames back in animation
      maxFramesFwd: 100,    // Max frames forward in animation
      // Legacy aliases for backward compatibility
      maxStepsBack: 100,     // Max steps back (alias for maxFramesBack)
      maxStepsForward: 168,  // Max steps forward
      // Timeline scrubber state
      isTimelineDragging: false, // True while user is dragging the timeline slider
      wasPlayingBeforeDrag: false, // Remember if animation was playing before drag
      
      // Animation double-buffering
      // True double-buffer: only 2 layers per element (front + back)
      // { layerId: { front: leafletLayer, back: leafletLayer, frontTimeKey: string, backTimeKey: string } }
      animationBuffers: {},
      // Current frame index for animation
      animationFrameIndex: 0,
      // List of time keys for current animation loop
      animationTimeKeys: [],
      // Whether animation frames are being preloaded
      isPreloadingFrames: false,
      showPreloadContent: false,
      // Maximum number of frames to preload (memory management)
      // Needs to accommodate large loop windows with small time steps
      maxAnimationFrames: 120,
      // Preload progress (0-100)
      preloadProgress: 0,
      // Preview speed multiplier during progressive loading (0-1, where 1 = normal speed)
      previewSpeedMultiplier: 1,
      // Flag for preview oscillation mode (ping-pong between first and last frames)
      previewOscillating: false,
      // Index of last frame for oscillation preview
      previewLastFrameIndex: 0,
      // Maximum frame index available during progressive loading
      progressiveMaxFrame: 0,
      // Count of frames loaded (for blur calculation)
      progressiveLoadedCount: 0,
      // Total frames being loaded
      progressiveTotalFrames: 0,
      // Debounce timer for animation restart (avoids rapid restarts during slider drag)
      animationRestartTimer: null,
      // Flag to track if next frame is ready in double-buffer mode
      nextFrameReady: false,
      
      // ---- Animation Mode Switch ----
      // true = use canvas rasterization (lower memory, smoother)
      // false = use L.tileLayer opacity swapping (current approach)
      // NOTE: Canvas mode requires CORS headers from tile server - disabled for now
      useCanvasAnimation: true,
      
      // Canvas animation state
      // Stores rasterized frame images as data URLs or ImageBitmap objects
      canvasFrameCache: [], // Array of { timeKey, imageData, img } objects (img = preloaded Image)
      // Reference to the animation canvas element
      animationCanvas: null,
      // Offscreen canvas for double-buffering (prevents flicker)
      offscreenCanvas: null,
      // Animation frame request ID for requestAnimationFrame
      animationFrameRequestId: null,
      // Crossfade animation state
      crossfadeProgress: 0, // 0-1 progress between current and next frame
      crossfadeLastTime: 0, // Last timestamp for animation timing
      crossfadeEnabled: true, // Enable/disable crossfade blending
      smoothAnimationMode: 'morph', // 'off', 'crossfade', 'morph', 'motion' - animation interpolation mode
      animationSmoothness: 100, // 0-100 percentage for blend intensity
      // Loop transition state (pause at end, then fade back to start)
      loopPauseRemaining: 0, // Remaining pause time at end of loop (ms)
      isLoopFading: false, // True during fade from last to first frame
      // WebGL context and resources for GPU-accelerated morphing
      webglCanvas: null,
      webglContext: null,
      webglProgram: null,
      webglUniforms: null, // Cached uniform locations
      webglTextures: {}, // Cached textures for frames
      webglMotionTexture: null, // Motion field texture
      webglInitialized: false,
      // Morph debug mode
      morphDebugMode: false,
      morphDebugFrameIndex: 0, // Which frame pair to debug (0 = frames 0→1)
      morphDebugT: 0, // Manual t value 0-1
      // Optical flow cache: stores motion vectors between frame pairs
      // Key: "frameIdx1-frameIdx2", Value: { vectors: Float32Array, blockSize: number }
      opticalFlowCache: {},
      // Block size for optical flow computation (smaller = smoother warping but more computation)
      opticalFlowBlockSize: 16, // Smaller blocks for smoother mesh warping
      // Search radius for block matching (larger = can detect faster motion)
      opticalFlowSearchRadius: 24, // Larger to detect weather system movement,
      
      // Settings
      internalUnitGroup: 'metric',  // Internal state, used when prop not provided
      baseApiUrl: 'https://maps.visualcrossing.com', //'http://location:8080', //'https://maps.visualcrossing.com',
      defaultApiKey: '', // Fallback API key when no session or override
      
      // UI state
      viewInfo: {
        lat: null,
        lng: null,
        zoom: null,
        centerPair: '',
        // Map viewport bounds for intersection checking
        bounds: null // { north, south, east, west }
      },
      
      // Legend - now stores legends for all visible layers
      visibleLayerLegends: [],
      elementBinCache: {},

      // User markers
      userMarkers: [],
      nextMarkerId: 1,
      markerColors: ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'],
      newMarkerLatLng: '',
      userLocationMarker: null,
      userLocation: null,
      // Throttle timer for updateViewInfo to prevent excessive calls during rapid pan
      updateViewInfoTimer: null
    };
  },
  
  computed: {
    /**
     * Effective unit group - uses prop if provided, otherwise internal state
     */
    effectiveUnitGroup: {
      get() {
        return this.unitGroup !== null ? this.unitGroup : this.internalUnitGroup;
      },
      set(val) {
        this.internalUnitGroup = val;
        // Emit change event for v-model support
        this.$emit('update:unitGroup', val);
      }
    },
    
    /**
     * Check if user has an active session
     */
    hasSession() {
      return !!this.sessionId && !!this.sessionState;
    },
    
    /**
     * Get the effective API key to use for map tile requests
     * Priority: 1) User override, 2) Session key, 3) Default key
     */
    weatherApiKey() {
      if (this.apiKeyOverride && this.apiKeyOverride.trim()) {
        return this.apiKeyOverride.trim();
      }
      if (this.sessionState?.apiKey) {
        return this.sessionState.apiKey;
      }
      return this.defaultApiKey;
    },
    
    /**
     * Check if using a custom API key (not the default)
     * Used to determine whether to show key in UI and sample URLs
     */
    hasCustomApiKey() {
      // True if we have an override or a session key (i.e., not just using the default)
      return !!this.apiKeyOverride?.trim() || !!this.sessionState?.apiKey;
    },
    
    /**
     * Check if we have a valid API key available (any non-empty key)
     */
    hasValidApiKey() {
      return !!this.weatherApiKey;
    },
    
    /**
     * Get the API key to display in the text box
     * Shows: manual override or session key (but NOT the default key)
     * Returns empty string if only using default key
     */
    displayApiKey: {
      get() {
        // If user has typed something, show that
        if (this.apiKeyOverride && this.apiKeyOverride.trim()) {
          return this.apiKeyOverride;
        }
        // If session has a key, show it (so user can see/edit it)
        if (this.sessionState?.apiKey) {
          return this.sessionState.apiKey;
        }
        // Otherwise show empty (using default key behind the scenes)
        return '';
      },
      set(value) {
        // When user types, update the override
        this.apiKeyOverride = value;
      }
    },
    
    formattedTime() {
      return this.currentTime ? this.formatFullDateTime(this.currentTime) : '';
    },
    /**
     * Short formatted time for mobile collapsed view (just time, no date)
     */
    shortFormattedTime() {
      if (!this.currentTime) return '';
      const pad = n => String(n).padStart(2, '0');
      const h = pad(this.currentTime.getUTCHours());
      const min = pad(this.currentTime.getUTCMinutes());
      return `${h}:${min}Z`;
    },
    dateTimeLocalValue() {
      if (!this.currentTime) return '';
      const pad = n => String(n).padStart(2, '0');
      const y = this.currentTime.getUTCFullYear();
      const m = pad(this.currentTime.getUTCMonth() + 1);
      const d = pad(this.currentTime.getUTCDate());
      const h = pad(this.currentTime.getUTCHours());
      const min = pad(this.currentTime.getUTCMinutes());
      return `${y}-${m}-${d}T${h}:${min}`;
    },
    
    /**
     * Get the minimum datetime value for the datetime picker based on model time range.
     */
    modelTimeRangeMin() {
      const range = this.modelTimeRange;
      if (!range) return null;
      return this.formatDateTimeLocal(range.minTime);
    },
    
    /**
     * Get the maximum datetime value for the datetime picker based on model time range.
     */
    modelTimeRangeMax() {
      const range = this.modelTimeRange;
      if (!range) return null;
      return this.formatDateTimeLocal(range.maxTime);
    },
    
    /**
     * Format the model run time for display.
     */
    formatModelRunTime() {
      const range = this.modelTimeRange;
      if (!range?.modelRunTime) return '';
      return this.formatFullDateTime(range.modelRunTime);
    },
    
    /**
     * Format the model valid time range for display.
     */
    formatModelTimeRangeLabel() {
      const range = this.modelTimeRange;
      if (!range) return '';
      const minStr = this.formatShortDateTime(range.minTime);
      const maxStr = this.formatShortDateTime(range.maxTime);
      return `${minStr} to ${maxStr}`;
    },
    
    availableElements() {
      const model = this.modelOptions[this.newLayerModel];
      return model ? Object.keys(model.elements) : [];
    },
    
    /**
     * Get visible layers for API integration modal
     * Includes both regular active layers and quick-select layers
     */
    visibleLayersForApi() {
      return this.activeLayers.filter(l => l.visible);
    },
    
    /**
     * All unique elements available across all quick-select models
     * Returns array of element IDs sorted alphabetically by display name
     */
    quickSelectElements() {
      const elementsSet = new Set();
      for (const modelId of this.quickSelectModels) {
        const model = this.modelOptions[modelId];
        if (model?.elements) {
          Object.keys(model.elements).forEach(el => elementsSet.add(el));
        }
      }
      // Sort by display name
      return Array.from(elementsSet).sort((a, b) => {
        const nameA = this.elementNames[a] || a;
        const nameB = this.elementNames[b] || b;
        return nameA.localeCompare(nameB);
      });
    },
    
    /**
     * Models available for the currently selected quick-select element
     * Returns array of objects with modelId and inBounds status
     * Sorted with Hybrid first, Radar second, then alphabetically by display name
     * Filtered by allowedModels prop if provided
     */
    quickSelectModelsForElement() {
      const element = this.quickSelectElement;
      const models = this.quickSelectModels
        .filter(modelId => {
          // Filter by allowedModels prop if provided
          if (this.allowedModels && this.allowedModels.length > 0) {
            if (!this.allowedModels.includes(modelId)) {
              return false;
            }
          }
          const model = this.modelOptions[modelId];
          return model?.elements && element in model.elements;
        })
        .map(modelId => ({
          id: modelId,
          inBounds: this.isModelInBounds(modelId),
          displayName: this.modelNames[modelId] || modelId,
          isHybrid: modelId === 'hybrid',
          isRadar: modelId === 'radar'
        }));
      
      // Sort: Hybrid first, Radar second, then alphabetically by display name
      models.sort((a, b) => {
        if (a.isHybrid) return -1;
        if (b.isHybrid) return 1;
        if (a.isRadar) return -1;
        if (b.isRadar) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
      
      return models;
    },
    
    /**
     * Get just the model IDs that are in bounds (for auto-selection)
     */
    inBoundsModels() {
      return this.quickSelectModelsForElement
        .filter(m => m.inBounds)
        .map(m => m.id);
    },
    
    /**
     * Check if selected model supports the selected element
     */
    isQuickSelectValid() {
      const model = this.modelOptions[this.quickSelectModel];
      return model?.elements && this.quickSelectElement in model.elements;
    },
    
    // Get the smallest timeSkip from visible layers for playback
    activeTimeSkip() {
      const visibleLayers = this.activeLayers.filter(l => l.visible);
      if (visibleLayers.length === 0) return 10;
      return Math.min(...visibleLayers.map(l => this.getElementConfig(l.model, l.element).timeSkip));
    },
    currentTimeSkip() {
      return this.autoTimeSkip ? this.activeTimeSkip : this.manualTimeSkip;
    },
    
    /**
     * Get the effective time step label for display.
     * Shows the current model-based interval if available.
     * Formats as: <2min = seconds, <60min = minutes, >=60min = hours
     */
    effectiveTimeStepLabel() {
      const modelConfig = this.primaryModelConfig;
      let intervalMinutes;
      
      if (modelConfig?.temporalAvailability?.intervals && modelConfig.modelRunTimeEpoch) {
        intervalMinutes = this.getTimeIntervalMinutes(this.currentTime, modelConfig, 1);
      } else {
        intervalMinutes = this.currentTimeSkip;
      }
      
      return this.formatTimeStepLabel(intervalMinutes);
    },
    
    /**
     * Format the manual time skip value for display in the stepper.
     */
    manualTimeSkipLabel() {
      return this.formatTimeStepLabel(this.manualTimeSkip);
    },
    /**
     * Calculate the effective loop steps based on all visible layers.
     * Uses the intersection (most restrictive) of all layer loop steps.
     * This ensures playback stays within bounds valid for all layers.
     */
    activeLoopSteps() {
      const visibleLayers = this.activeLayers.filter(l => l.visible);
      if (visibleLayers.length === 0) {
        return { stepsBack: 12, stepsForward: 12 };
      }
      
      // Get all loop steps from visible layers
      const loopSteps = visibleLayers.map(l => this.getElementConfig(l.model, l.element).loopSteps);
      
      // Find the intersection: min of all stepsBack and stepsForward
      const stepsBack = Math.min(...loopSteps.map(s => s.stepsBack));
      const stepsForward = Math.min(...loopSteps.map(s => s.stepsForward));
      
      return { stepsBack, stepsForward };
    },
    
    /**
     * The effective loop steps used for animation.
     * Uses custom values if set, otherwise falls back to activeLoopSteps.
     */
    effectiveLoopSteps() {
      return {
        stepsBack: this.customStepsBack ?? this.activeLoopSteps.stepsBack,
        stepsForward: this.customStepsForward ?? this.activeLoopSteps.stepsForward
      };
    },
    
    /**
     * Get the primary model config from visible layers.
     * Uses the first visible layer's model for temporal availability calculations.
     * Returns null if no visible layers or model has no temporal data.
     */
    primaryModelConfig() {
      const visibleLayers = this.activeLayers.filter(l => l.visible);
      if (visibleLayers.length === 0) return null;
      
      const layer = visibleLayers[0];
      // Use quickSelectModelId if available (the original model ID like 'ecmwf'),
      // otherwise use model (which might be the sourceModel mapping like 'gfsecmwf')
      const modelId = layer.quickSelectModelId || layer.model || 'default';
      return this.modelOptions[modelId] || null;
    },
    
    /**
     * Get the valid time range based on the primary visible model.
     * Returns { minTime, maxTime, modelRunTime } or null if no constraints.
     */
    modelTimeRange() {
      const modelConfig = this.primaryModelConfig;
      if (!modelConfig) return null;
      return getModelTimeRange(modelConfig);
    },
    
    /**
     * Format loop steps as human-readable label showing total frames.
     */
    loopWindowLabel() {
      const { stepsBack, stepsForward } = this.effectiveLoopSteps;
      const totalFrames = stepsBack + stepsForward + 1; // +1 for current time frame
      return `${stepsBack} back, ${stepsForward} forward (${totalFrames} frames)`;
    },
    
    /**
     * Calculate the start time label for the loop window slider.
     * Shows the datetime that would result from going back N steps from NOW.
     * Uses real time (not animated currentTime) so labels stay stable during playback.
     */
    loopStartTimeLabel() {
      const now = new Date();
      const stepsBack = this.effectiveLoopSteps.stepsBack;
      const timeSkipMinutes = this.currentTimeSkip;
      const offsetMinutes = stepsBack * timeSkipMinutes;
      const startTime = new Date(now.getTime() - offsetMinutes * 60 * 1000);
      return this.formatDateTimeCompact(startTime);
    },
    
    /**
     * Calculate the end time label for the loop window slider.
     * Shows the datetime that would result from going forward N steps from NOW.
     * Uses real time (not animated currentTime) so labels stay stable during playback.
     */
    loopEndTimeLabel() {
      const now = new Date();
      const stepsForward = this.effectiveLoopSteps.stepsForward;
      const timeSkipMinutes = this.currentTimeSkip;
      const offsetMinutes = stepsForward * timeSkipMinutes;
      const endTime = new Date(now.getTime() + offsetMinutes * 60 * 1000);
      return this.formatDateTimeCompact(endTime);
    },
    
    /**
     * Format a short time label (HH:mm) from an ISO time key string.
     */
    timelineStartLabel() {
      if (this.animationTimeKeys.length === 0) return '';
      return this.formatShortTime(this.animationTimeKeys[0]);
    },
    
    timelineEndLabel() {
      if (this.animationTimeKeys.length === 0) return '';
      return this.formatShortTime(this.animationTimeKeys[this.animationTimeKeys.length - 1]);
    },
    
    timelineCurrentLabel() {
      if (this.animationTimeKeys.length === 0 || this.animationFrameIndex < 0) return '';
      const timeKey = this.animationTimeKeys[this.animationFrameIndex];
      if (!timeKey) return '';
      return this.formatShortTime(timeKey);
    }
  },
  
  watch: {
    // Watch props for changes from parent component
    initialElement(newVal) {
      if (newVal && this.mapReady) {
        this.quickSelectElement = newVal;
        this.onQuickSelectElementChange();
      }
    },
    initialModel(newVal) {
      if (newVal && this.mapReady) {
        this.quickSelectModel = newVal;
        this.onQuickSelectModelChange();
      }
    },
    allowedModels: {
      handler() {
        // When allowed models change, ensure current selection is still valid
        if (this.mapReady && this.allowedModels && this.allowedModels.length > 0) {
          if (!this.allowedModels.includes(this.quickSelectModel)) {
            // Select first allowed model
            this.quickSelectModel = this.allowedModels[0] || 'hybrid';
            this.onQuickSelectModelChange();
          }
        }
      },
      deep: true
    },
    effectiveUnitGroup() {
      if (!this.mapReady || !this.map) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
      this.saveSettings();
    },
    baseApiUrl() {
      if (!this.mapReady || !this.map) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
      this.saveSettings();
      // Reload model configuration from new API endpoint
      this.loadModelConfiguration();
    },
    // Watch sessionState to reload models when user logs in
    'sessionState.apiKey'() {
      if (this.mapReady) {
        // Reload model configuration with new session API key
        this.loadModelConfiguration();
      }
    },
    apiKeyOverride() {
      // When API key changes, clear errors and refresh layers
      this.dismissTileError();
      if (!this.mapReady || !this.map) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
      // Reload model configuration with new API key
      this.loadModelConfiguration();
    },
    selectedBaseMap() {
      this.saveSettings();
    },
    showLabels() {
      this.saveSettings();
    },
    showGeoJson() {
      this.saveSettings();
      // Sync quick overlay with advanced panel
      if (!this.showGeoJson) {
        this.quickOverlay = 'none';
      } else if (this.quickOverlay === 'none') {
        this.quickOverlay = this.geoJsonElement;
      }
    },
    geoJsonElement() {
      this.saveSettings();
      // Sync quick overlay with advanced panel
      if (this.showGeoJson) {
        this.quickOverlay = this.geoJsonElement;
      }
    },
    playbackSpeed() {
      this.saveSettings();
    },
    autoTimeSkip() {
      this.saveSettings();
      // Time skip changed - snap current time to new interval
      if (this.currentTime) {
        this.currentTime = this.snapTimeToInterval(this.currentTime);
      }
      // Time skip affects frame generation - restart animation if playing
      this.restartAnimationIfPlaying();
    },
    manualTimeSkip() {
      this.saveSettings();
      // Time skip changed - snap current time to new interval
      if (!this.autoTimeSkip && this.currentTime) {
        this.currentTime = this.snapTimeToInterval(this.currentTime);
      }
      // Time skip affects frame generation - restart animation if playing
      if (!this.autoTimeSkip) {
        this.restartAnimationIfPlaying();
      }
    },
    // Watch for custom loop steps changes - invalidate cache so next play uses new settings
    customStepsBack() {
      this.invalidateAnimationCache();
      this.restartAnimationIfPlaying();
    },
    customStepsForward() {
      this.invalidateAnimationCache();
      this.restartAnimationIfPlaying();
    },
    activeLayers: {
      deep: true,
      handler(newVal, oldVal) {
        // Skip during animation to prevent infinite restart loops
        // The deep watcher can fire when we access layers during frame display
        if (this.isPlaying || this.isPreloadingFrames) {
          return;
        }
        
        if (this.mapReady) {
          this.saveSettings();
          // Layer changes affect animation - but only restart if animation was playing before this change
          // Don't call restartAnimationIfPlaying here - it's checked above
        }
      }
    }
  },
  
  mounted() {
    /**
     * Session/Auth Initialization
     * 
     * Uses window.vcCommunicator to:
     * 1. Get initial session state if already logged in
     * 2. Listen for 'createsession' events when user logs in
     * 3. Listen for 'terminatesession' events when user logs out
     * 
     * The sessionState object contains:
     * - apiKey: Used for map tile API requests
     * - userId: User identifier
     */
    const vcCommunicator = window.vcCommunicator;
    
    if (vcCommunicator) {
      // Get initial session state
      this.sessionId = vcCommunicator.sessionId;
      this.sessionState = vcCommunicator.sessionState;
      
      // Listen for session creation
      vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
        this.sessionId = sessionId;
        this.sessionState = sessionState;
        // Clear any override when logging in with a new session
        if (!this.apiKeyOverride) {
          this.refreshAllLayers();
        }
      });
      
      // Listen for session termination
      vcCommunicator.addHandler('terminatesession', () => {
        this.sessionId = null;
        this.sessionState = null;
        // Keep any user-provided override
        if (!this.apiKeyOverride) {
          this.refreshAllLayers();
        }
      });
    }
    
    // Check URL parameter for advanced mode
    const urlParams = new URLSearchParams(window.location.search);
    this.advancedEnabled = urlParams.get('showadvanced') === 'true';
    
    this.loadSettings(); // Load saved settings first
    
    // Apply initial element and model from props if provided
    if (this.initialElement) {
      this.quickSelectElement = this.initialElement;
    }
    if (this.initialModel) {
      this.quickSelectModel = this.initialModel;
    }
    
    // Load model configuration from API (non-blocking)
    // This will update modelOptions, modelNames, and quickSelectModels
    // after the initial render with static defaults
    this.loadModelConfiguration();
    
    this.initializeMap();
    this.currentTime = this.getLastInterval();
    this.mapReady = true;
    
    // Add a default layer only if none were loaded from storage
    // Skip if controlled by parent component (skipDefaultLoad prop)
    if (this.activeLayers.length === 0 && !this.skipDefaultLoad) {
      this.addDefaultLayers();
    } else if (!this.skipDefaultLoad) {
      // Create Leaflet layers for loaded layers
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          this.createLeafletLayer(layer);
          this.expandedLegends[layer.id] = true;
        }
      });
      this.updateAllLegends();
    }
    
    // Apply saved overlay settings
    // Note: showLabels now controls weather location names on markers, not a separate layer
    if (this.showGeoJson) {
      this.addGeoJsonTileLayer();
    }
    
    this.$nextTick(() => {
      this.updateViewInfo();
      this.checkMapWidth();
    });
    
    // Listen for resize to toggle button bar vs dropdown
    this._resizeHandler = () => this.checkMapWidth();
    window.addEventListener('resize', this._resizeHandler);
  },
  
  beforeUnmount() {
    this.stopMovie();
    this.mapReady = false;
    
    // Remove resize listener
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
    
    // Clear canvas frame cache explicitly
    this.clearCanvasCache();
    
    // Clear any pending tile error timeout
    if (this.tileErrorTimeout) {
      clearTimeout(this.tileErrorTimeout);
      this.tileErrorTimeout = null;
    }
    
    // Clear any pending animation restart timer
    if (this.animationRestartTimer) {
      clearTimeout(this.animationRestartTimer);
      this.animationRestartTimer = null;
    }
    
    // Clear any pending updateViewInfo timer
    if (this.updateViewInfoTimer) {
      clearTimeout(this.updateViewInfoTimer);
      this.updateViewInfoTimer = null;
    }
    
    // Remove all event listeners from map first
    if (this.map) {
      this.map.off('moveend', this.updateViewInfo);
      this.map.off('zoomend', this.updateViewInfo);
      this.map.off('movestart', this.handleMapMoveForAnimation);
      this.map.off('zoomstart', this.handleMapMoveForAnimation);
      this.map.off('click'); // Remove inline click handler
      // Stop any ongoing zoom animation
      this.map.stop();
    }
    
    // Clean up GeoJSON layer FIRST (before other layers) to prevent animation errors
    // Remove event listeners and detach from map before cleanup
    if (this.geoJsonLayer) {
      this.geoJsonLayer.off();
      if (this.geoJsonLayer._map) {
        this.map.removeLayer(this.geoJsonLayer);
      }
      this.geoJsonLayer = null;
    }
    
    // Abort all pending GeoJSON requests
    this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
    this.geoJsonAbortControllers.clear();
    
    // Clean up GeoJSON tile groups
    Object.keys(this.geoJsonTiles).forEach(key => {
      this.safeRemoveLayer(this.geoJsonTiles[key]);
    });
    this.geoJsonTiles = {};
    
    // Clean up all active weather layers
    this.activeLayers.forEach(layer => {
      this.safeRemoveLayer(layer.leafletLayer);
      layer.leafletLayer = null;
    });
    
    // Clean up user markers and their tooltips
    this.userMarkers.forEach(marker => {
      if (marker.leafletMarker) {
        marker.leafletMarker.unbindTooltip();
        this.safeRemoveLayer(marker.leafletMarker);
        marker.leafletMarker = null;
      }
    });
    
    // Clean up labels layer
    this.safeRemoveLayer(this.labelLayer);
    this.labelLayer = null;
    
    // Clean up base layer
    this.safeRemoveLayer(this.baseLayer);
    this.baseLayer = null;
    
    // Finally remove the map
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  
  methods: {
    /**
     * Load weather model configuration from API
     * Updates modelOptions, modelNames, and quickSelectModels with API data
     * Falls back to static config on error
     */
    async loadModelConfiguration() {
      try {
        const result = await loadModelsFromApi(this.baseApiUrl, this.weatherApiKey);
        
        this.modelOptions = result.modelOptions;
        this.modelNames = result.modelNames;
        this.quickSelectModels = result.quickSelectModels;
        this.modelsLoadedFromApi = result.fromApi;

        // Inject alertswebp into any model that has alerts
        for (const modelId of Object.keys(this.modelOptions)) {
          const model = this.modelOptions[modelId];
          if (model.elements && model.elements.alerts && !model.elements.alertswebp) {
            model.elements.alertswebp = { ...model.elements.alerts };
          }
        }
        
        if (result.fromApi) {
          console.log('Weather models loaded from API successfully');
        }
      } catch (error) {
        console.warn('Failed to load model configuration:', error);
        // Keep static defaults
      }
    },
    
    /**
     * Get effective config for a model+element combination
     * Element-level settings override model-level defaults
     */
    getElementConfig(modelId, elementId) {
      const model = this.modelOptions[modelId] || this.modelOptions.default;
      const elementConfig = model.elements?.[elementId] || {};
      
      return {
        timeSkip: elementConfig.timeSkip ?? model.timeSkip ?? 10,
        timeSkipOverride: elementConfig.timeSkipOverride ?? model.timeSkipOverride ?? false,
        loopSteps: elementConfig.loopSteps ?? model.loopSteps ?? { stepsBack: 12, stepsForward: 12 }
      };
    },
    
    /**
     * Check if the current map center is within a model's geographic bounds
     * @param {string} modelId - The model ID to check
     * @returns {boolean} - True if map center is within bounds, or if model has no bounds
     */
    isModelInBounds(modelId) {
      const model = this.modelOptions[modelId];
      if (!model?.bounds) return true; // No bounds = global coverage
      
      const mapBounds = this.viewInfo.bounds;
      if (!mapBounds) return true; // No bounds yet, assume in bounds
      
      const { minLat, maxLat, minLon, maxLon } = model.bounds;
      const { north, south, east, west } = mapBounds;
      
      // Check if model bounds intersect with map viewport
      // Two rectangles intersect if they overlap on both axes
      const latOverlap = maxLat >= south && minLat <= north;
      const lonOverlap = maxLon >= west && minLon <= east;
      
      return latOverlap && lonOverlap;
    },
    
    /**
     * Check map width and update isWideMap for responsive model selector
     */
    checkMapWidth() {
      const mapEl = document.getElementById('map');
      this.isWideMap = mapEl ? mapEl.offsetWidth > 800 : false;
      this.isMobileView = mapEl ? mapEl.offsetWidth < 1000 : false;
    },
    
    /**
     * Toggle mobile layer panel expanded/collapsed
     */
    toggleMobileLayerPanel() {
      this.mobileLayerExpanded = !this.mobileLayerExpanded;
      // Collapse the other panel to avoid overlap
      if (this.mobileLayerExpanded) {
        this.mobileAnimExpanded = false;
      }
    },
    
    /**
     * Toggle mobile animation panel expanded/collapsed
     */
    toggleMobileAnimPanel() {
      this.mobileAnimExpanded = !this.mobileAnimExpanded;
      // Collapse the other panel to avoid overlap
      if (this.mobileAnimExpanded) {
        this.mobileLayerExpanded = false;
      }
    },
    
    /**
     * Toggle multi-layer mode on/off
     */
    toggleMultiLayerMode() {
      this.multiLayerMode = !this.multiLayerMode;
      
      if (this.multiLayerMode) {
        // Entering multi-layer mode: initialize activeQuickSelectModels with current element+model
        if (this.quickSelectActive) {
          this.activeQuickSelectModels = [{ element: this.quickSelectElement, model: this.quickSelectModel }];
        } else {
          this.activeQuickSelectModels = [];
        }
      } else {
        // Exiting multi-layer mode: clear all layers except the last selected one
        if (this.activeQuickSelectModels.length > 0) {
          // Keep only the last active element+model
          const last = this.activeQuickSelectModels[this.activeQuickSelectModels.length - 1];
          this.removeAllQuickSelectLayers();
          this.quickSelectElement = last.element;
          this.quickSelectModel = last.model;
          this.activeQuickSelectModels = [];
          this.applyQuickSelectLayer();
        } else {
          this.activeQuickSelectModels = [];
        }
      }
    },
    
    /**
     * Check if a model is currently active (for button highlighting)
     * In multi-layer mode, checks if the current element + this model is active
     */
    isModelActive(modelId) {
      if (this.multiLayerMode) {
        return this.activeQuickSelectModels.some(item => 
          item.element === this.quickSelectElement && item.model === modelId
        );
      }
      return this.quickSelectModel === modelId && this.quickSelectActive;
    },
    
    /**
     * Get count of active layers across all elements (for multi-layer mode indicator)
     */
    getActiveLayerCount() {
      return this.activeQuickSelectModels.length;
    },
    
    /**
     * Select model from button bar (wide mode)
     * In multi-layer mode: toggles the model layer on/off
     * In single mode: clears other layers and shows only this model
     */
    selectModel(modelId) {
      if (this.multiLayerMode) {
        // Multi-layer mode: toggle this model
        this.toggleModelLayer(modelId);
      } else {
        // Single layer mode: switch to this model
        if (this.quickSelectModel !== modelId) {
          this.quickSelectModel = modelId;
          this.applyQuickSelectLayer();
        }
      }
    },
    
    /**
     * Toggle a specific model layer on/off (multi-layer mode)
     * Uses current quickSelectElement as the element for this model
     */
    toggleModelLayer(modelId) {
      const element = this.quickSelectElement;
      const index = this.activeQuickSelectModels.findIndex(item => 
        item.element === element && item.model === modelId
      );
      
      if (index >= 0) {
        // Model is active for this element, remove it
        this.activeQuickSelectModels.splice(index, 1);
        this.removeQuickSelectLayerForModel(element, modelId);
      } else {
        // Model is not active for this element, add it
        this.activeQuickSelectModels.push({ element, model: modelId });
        this.addQuickSelectLayerForModel(element, modelId);
      }
      
      // Update quickSelectModel to the last active one for current element (for dropdown sync)
      const activeForElement = this.activeQuickSelectModels.filter(item => item.element === element);
      if (activeForElement.length > 0) {
        this.quickSelectModel = activeForElement[activeForElement.length - 1].model;
      }
      
      if (this.activeQuickSelectModels.length > 0) {
        this.quickSelectActive = true;
      } else {
        this.quickSelectActive = false;
      }
    },
    
    /**
     * Add a quick-select layer for a specific element+model (multi-layer mode)
     * @param {string} element - The weather element (e.g., 'precipcomposite', 'temp')
     * @param {string} modelId - The model ID
     */
    addQuickSelectLayerForModel(element, modelId) {
      // Check if layer already exists
      const existingLayer = this.activeLayers.find(l => 
        l.isQuickSelect && 
        l.element === element && 
        l.quickSelectModelId === modelId
      );
      if (existingLayer) return;
      
      // Hide all non-quick-select layers
      this.activeLayers.forEach(l => {
        if (!l.isQuickSelect && l.leafletLayer && l.visible) {
          this.safeRemoveLayer(l.leafletLayer);
          l.wasVisibleBeforeQuickSelect = true;
          l.visible = false;
        }
      });
      
      // Create the new quick-select layer
      const newLayer = {
        id: this.nextLayerId++,
        element: element,
        model: modelId,
        quickSelectModelId: modelId,
        visible: true,
        opacity: this.quickSelectOpacity,
        leafletLayer: null,
        isQuickSelect: true
      };
      
      this.activeLayers.push(newLayer);
      this.createLeafletLayer(newLayer);
      
      // Update legend
      this.expandedLegends[newLayer.id] = true;
      this.updateAllLegends();
      
      this.quickSelectActive = true;
      this.restartAnimationIfPlaying();
    },
    
    /**
     * Remove a quick-select layer for a specific element+model (multi-layer mode)
     * @param {string} element - The weather element
     * @param {string} modelId - The model ID
     */
    removeQuickSelectLayerForModel(element, modelId) {
      const layer = this.activeLayers.find(l => 
        l.isQuickSelect && 
        l.element === element &&
        l.quickSelectModelId === modelId
      );
      
      if (layer) {
        this.safeRemoveLayer(layer.leafletLayer);
        delete this.expandedLegends[layer.id];
        this.activeLayers = this.activeLayers.filter(l => l.id !== layer.id);
        this.updateAllLegends();
        this.restartAnimationIfPlaying();
      }
      
      // If no more quick-select layers, restore previous layers
      if (!this.activeLayers.some(l => l.isQuickSelect)) {
        this.quickSelectActive = false;
        this.activeLayers.forEach(l => {
          if (l.wasVisibleBeforeQuickSelect) {
            this.createLeafletLayer(l);
            l.visible = true;
            delete l.wasVisibleBeforeQuickSelect;
          }
        });
      }
    },
    
    /**
     * Remove all quick-select layers (used when exiting multi-layer mode)
     */
    removeAllQuickSelectLayers() {
      const quickLayers = this.activeLayers.filter(l => l.isQuickSelect);
      quickLayers.forEach(layer => {
        this.safeRemoveLayer(layer.leafletLayer);
        delete this.expandedLegends[layer.id];
      });
      this.activeLayers = this.activeLayers.filter(l => !l.isQuickSelect);
      this.quickSelectActive = false;
      this.updateAllLegends();
    },
    
    /**
     * Handle quick-select element change
     * Auto-switches model if needed and applies the layer
     * In multi-layer mode: keeps existing layers and just changes the element dropdown context
     */
    onQuickSelectElementChange() {
      const modelsForElement = this.quickSelectModelsForElement;
      const modelIds = modelsForElement.map(m => m.id);
      const inBoundsModelIds = modelsForElement.filter(m => m.inBounds).map(m => m.id);
      
      if (this.multiLayerMode) {
        // In multi-layer mode: just change the element dropdown context
        // Existing layers for other elements are KEPT - you can add layers across elements
        
        // Find if we have any active models for this element
        const activeForElement = this.activeQuickSelectModels.filter(item => item.element === this.quickSelectElement);
        
        // Update quickSelectModel to first active for this element, or first available
        if (activeForElement.length > 0) {
          this.quickSelectModel = activeForElement[0].model;
        } else if (!modelIds.includes(this.quickSelectModel)) {
          this.quickSelectModel = inBoundsModelIds[0] || modelIds[0];
        }
      } else {
        // Single layer mode
        // If current model doesn't support this element, switch to first available
        if (!modelIds.includes(this.quickSelectModel)) {
          // Prefer an in-bounds model, otherwise fall back to first available
          this.quickSelectModel = inBoundsModelIds[0] || modelIds[0];
        } else if (!this.isModelInBounds(this.quickSelectModel) && inBoundsModelIds.length > 0) {
          // Current model is out of bounds, switch to an in-bounds one
          this.quickSelectModel = inBoundsModelIds[0];
        }
        this.applyQuickSelectLayer();
      }
    },
    
    /**
     * Handle quick-select model change - auto applies the layer
     */
    onQuickSelectModelChange() {
      this.applyQuickSelectLayer();
    },
    
    /**
     * Handle quick overlay change - toggles Wx overlay on/off with selected element
     */
    onQuickOverlayChange() {
      if (this.quickOverlay === 'none') {
        // Turn off the Wx overlay
        if (this.showGeoJson) {
          this.showGeoJson = false;
          this.toggleGeoJsonLayer();
        }
      } else {
        // Set the element and turn on the overlay
        this.geoJsonElement = this.quickOverlay;
        if (!this.showGeoJson) {
          this.showGeoJson = true;
          this.toggleGeoJsonLayer();
        } else {
          // Already on, just refresh with new element
          this.refreshGeoJsonTiles();
        }
      }
    },
    
    /**
     * Apply the quick-select layer to the map
     * Hides all other weather layers and shows only the selected element+model
     */
    applyQuickSelectLayer() {
      if (!this.isQuickSelectValid) return;
      
      // Get the time interval for the new element+model combination
      const newElementConfig = this.getElementConfig(this.quickSelectModel, this.quickSelectElement);
      const newTimeSkip = newElementConfig.timeSkip;
      
      // Snap current time to the new interval if it changed
      // This ensures times like 10:15 snap to 10:00 when going from 5-min to 60-min intervals
      if (this.currentTime) {
        const snappedTime = this.snapTimeToInterval(this.currentTime, newTimeSkip);
        if (snappedTime.getTime() !== this.currentTime.getTime()) {
          this.currentTime = snappedTime;
        }
      }
      
      // Check if we already have this exact layer active as quick-select
      if (this.quickSelectActive) {
        const existingQuickLayer = this.activeLayers.find(l => l.isQuickSelect);
        if (existingQuickLayer && 
            existingQuickLayer.element === this.quickSelectElement && 
            existingQuickLayer.quickSelectModelId === this.quickSelectModel) {
          // Same layer already active, nothing to do
          return;
        }
      }
      
      // Remove any existing quick-select layer from the map
      const existingQuick = this.activeLayers.find(l => l.isQuickSelect);
      if (existingQuick) {
        this.safeRemoveLayer(existingQuick.leafletLayer);
        this.activeLayers = this.activeLayers.filter(l => !l.isQuickSelect);
      }
      
      // Hide all other weather layers (but don't remove from activeLayers)
      this.activeLayers.forEach(l => {
        if (l.leafletLayer && l.visible) {
          this.safeRemoveLayer(l.leafletLayer);
          l.wasVisibleBeforeQuickSelect = true;
          l.visible = false;
        }
      });
      
      // Create the new quick-select layer
      const newLayer = {
        id: this.nextLayerId++,
        element: this.quickSelectElement,
        model: this.quickSelectModel,  // Use model ID for tile URL source parameter
        quickSelectModelId: this.quickSelectModel,  // Store for comparison (same as model)
        visible: true,
        opacity: this.quickSelectOpacity,
        leafletLayer: null,
        isQuickSelect: true  // Mark as quick-select layer
      };
      
      this.activeLayers.push(newLayer);
      
      // Add the tile layer to the map using existing method
      this.createLeafletLayer(newLayer);
      
      // Update legend for quick-select layer
      this.expandedLegends[newLayer.id] = true;
      this.updateAllLegends();
      
      this.quickSelectActive = true;
      this.showQuickSelectDropdown = false;
      
      // Restart animation if playing to include new layer
      this.restartAnimationIfPlaying();
    },
    
    /**
     * Clear the quick-select layer and restore previous layers
     */
    clearQuickSelectLayer() {
      // Remove quick-select layer
      const quickLayer = this.activeLayers.find(l => l.isQuickSelect);
      if (quickLayer) {
        this.safeRemoveLayer(quickLayer.leafletLayer);
        delete this.expandedLegends[quickLayer.id];
        this.activeLayers = this.activeLayers.filter(l => !l.isQuickSelect);
      }
      
      this.quickSelectActive = false;
      
      // Restore visibility of previously hidden layers
      this.activeLayers.forEach(l => {
        if (l.wasVisibleBeforeQuickSelect) {
          this.createLeafletLayer(l);
          l.visible = true;
          delete l.wasVisibleBeforeQuickSelect;
        }
      });
      
      this.updateAllLegends();
      
      // Restart animation if playing
      this.restartAnimationIfPlaying();
    },
    
     initializeMap() {
      this.map = L.map("map", {
        zoomControl: false
      }).setView([0, 0], 7);
      
      L.control.zoom({ position: 'topleft' }).addTo(this.map);

      // Add custom layers toggle control (only in advanced mode)
      const self = this;
      if (this.advancedEnabled) {
        const LayersToggle = L.Control.extend({
          options: {
            position: 'topleft'
          },
          onAdd: function() {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
            const button = L.DomUtil.create('a', 'layers-control-btn', container);
            button.href = '#';
            button.title = 'Toggle Layers';
            button.setAttribute('role', 'button');
            button.setAttribute('aria-label', 'Toggle Layers');
            button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8.235 1.559a.5.5 0 0 0-.47 0l-7.5 4a.5.5 0 0 0 0 .882L3.188 8 .264 9.559a.5.5 0 0 0 0 .882l7.5 4a.5.5 0 0 0 .47 0l7.5-4a.5.5 0 0 0 0-.882L12.813 8l2.922-1.559a.5.5 0 0 0 0-.882l-7.5-4zM8 9.433 1.562 6 8 2.567 14.438 6 8 9.433z"/>
            </svg>`;
            
            L.DomEvent.on(button, 'click', function(e) {
              L.DomEvent.preventDefault(e);
              L.DomEvent.stopPropagation(e);
              self.controlsOpen = !self.controlsOpen;
            });
            
            return container;
          }
        });
        
        new LayersToggle().addTo(this.map);
      }
      
      // Add center on current location control
      const CenterLocationControl = L.Control.extend({
        options: {
          position: 'topleft'
        },
        onAdd: function() {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
          const button = L.DomUtil.create('a', 'center-location-btn', container);
          button.href = '#';
          button.title = 'Center on current location';
          button.setAttribute('role', 'button');
          button.setAttribute('aria-label', 'Center on current location');
          button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
          </svg>`;
          
          L.DomEvent.on(button, 'click', function(e) {
            L.DomEvent.preventDefault(e);
            L.DomEvent.stopPropagation(e);
            self.goToUserLocation();
          });
          
          return container;
        }
      });
      
      new CenterLocationControl().addTo(this.map);
      
      // Create panes FIRST
      this.map.createPane('weatherPane');
      this.map.getPane('weatherPane').style.zIndex = 400;
      // Add smooth opacity transition for tile updates during playback
      this.map.getPane('weatherPane').style.transition = 'opacity 0.15s ease-in-out';
      
      this.map.createPane('labelsPane');
      this.map.getPane('labelsPane').style.zIndex = 500;
      
      this.map.createPane('citiesPane');
      this.map.getPane('citiesPane').style.zIndex = 600;
      
      this.map.createPane('markersPane');
      this.map.getPane('markersPane').style.zIndex = 650;

      // Then initialize base map
      this.changeBaseMap();
      
      this.map.on('moveend', this.updateViewInfo);
      this.map.on('zoomend', this.updateViewInfo);
      
      // Save map position when it changes
      this.map.on('moveend', this.saveMapPosition);
      this.map.on('zoomend', this.saveMapPosition);
      
      // Declutter GeoJSON markers when map moves
      this.map.on('moveend', this.scheduleGeoJsonDeclutter);
      this.map.on('zoomend', this.scheduleGeoJsonDeclutter);
      
      // Stop animation when map is panned or zoomed (tiles need to reload)
      this.map.on('movestart', this.handleMapMoveForAnimation);
      this.map.on('zoomstart', this.handleMapMoveForAnimation);
      
      // Ctrl+click to add a marker
      this.map.on('click', (e) => {
        if (e.originalEvent.ctrlKey) {
          this.addCustomMarker(e.latlng.lat, e.latlng.lng);
        }
      });
      
      // Try to restore saved map position, otherwise use geolocation
      const savedPosition = this.loadMapPosition();
      if (savedPosition) {
        this.map.setView([savedPosition.lat, savedPosition.lng], savedPosition.zoom);
        // Still get user location for the marker, but don't move the map
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              this.userLocation = { lat: latitude, lng: longitude };
              this.addCustomMarker(latitude, longitude, "#4285f4");
            },
            (error) => console.error("Geolocation error:", error)
          );
        }
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.userLocation = { lat: latitude, lng: longitude };
            this.map.setView([latitude, longitude], 8);
            this.addCustomMarker(latitude, longitude, "#4285f4");
          },
          (error) => console.error("Geolocation error:", error)
        );
      }
    },
    
    /**
     * Save map position to localStorage
     */
    saveMapPosition() {
      if (!this.map) return;
      const center = this.map.getCenter();
      const zoom = this.map.getZoom();
      const position = {
        lat: center.lat,
        lng: center.lng,
        zoom: zoom
      };
      try {
        localStorage.setItem('weatherMapPosition', JSON.stringify(position));
      } catch (e) {
        // localStorage might be unavailable
      }
    },
    
    /**
     * Load map position from localStorage
     */
    loadMapPosition() {
      try {
        const saved = localStorage.getItem('weatherMapPosition');
        if (saved) {
          const position = JSON.parse(saved);
          if (position.lat && position.lng && position.zoom) {
            return position;
          }
        }
      } catch (e) {
        // localStorage might be unavailable
      }
      return null;
    },
    
    
    addDefaultLayers() {
      // Add MRMS Radar as default
      this.activeLayers.push({
        id: this.nextLayerId++,
        model: 'default',
        element: 'precipcomposite',
        visible: true,
        opacity: 0.7,
        leafletLayer: null
      });
      
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          this.createLeafletLayer(layer);
          // Expand first layer's legend by default
          this.expandedLegends[layer.id] = true;
        }
      });
      
      this.updateAllLegends();
    },
    /*
     addUserLocationMarker(lat, lng) {
      if (this.userLocationMarker) {
        this.map.removeLayer(this.userLocationMarker);
      }
      
      const icon = L.divIcon({
        className: 'user-location-marker',
        html: `<div style="width:16px;height:16px;background:#4285f4;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      
      this.userLocationMarker = L.marker([lat, lng], { 
        icon, 
        pane: 'markersPane',
        zIndexOffset: 1000 
      }).addTo(this.map);
      
      this.userLocationMarker.bindTooltip('Your Location', { direction: 'top', offset: [0, -10] });
    },
    */
    addMarkerFromInput() {
      const input = this.newMarkerLatLng.trim();
      if (!input) return;
      
      // Parse "lat,lng" or "lat lng"
      const parts = input.split(/[,\s]+/).map(s => parseFloat(s.trim()));
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        this.addCustomMarker(parts[0], parts[1]);
        this.newMarkerLatLng = '';
      }
    },
    
    addMarkerFromGPS() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.addCustomMarker(latitude, longitude);
          },
          (error) => console.error("Geolocation error:", error)
        );
      }
    },
    
    addCustomMarker(lat, lng, color) {
      if (!this.map || !this.mapReady) return;
      
      color = color || this.markerColors[this.userMarkers.length % this.markerColors.length];
      const id = this.nextMarkerId++;
      
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:14px;height:14px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7]
      });
      
      const marker = L.marker([lat, lng], { 
        icon, 
        pane: 'markersPane',
        draggable: true 
      }).addTo(this.map);
      
      marker.bindTooltip(`${lat.toFixed(4)}, ${lng.toFixed(4)}`, {
        direction: 'top',
        offset: [0, -8]
      });
      
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        const m = this.userMarkers.find(m => m.id === id);
        if (m) {
          m.lat = pos.lat;
          m.lng = pos.lng;
          marker.setTooltipContent(`${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)}`);
        }
      });
      
      this.userMarkers.push({ id, lat, lng, color, leafletMarker: marker });
    },
    
    removeMarker(index) {
      const marker = this.userMarkers[index];
      if (marker.leafletMarker) {
        // Unbind tooltip before removing to prevent animation errors
        marker.leafletMarker.unbindTooltip();
        this.safeRemoveLayer(marker.leafletMarker);
        marker.leafletMarker = null;
      }
      this.userMarkers.splice(index, 1);
    },
    
    panToMarker(marker) {
      if (this.map && marker.lat && marker.lng) {
        this.map.setView([marker.lat, marker.lng], this.map.getZoom());
      }
    },
    
    copyMarkerCoords(marker) {
      const coords = `${marker.lat.toFixed(4)}, ${marker.lng.toFixed(4)}`;
      navigator.clipboard.writeText(coords).then(() => {
        // Optional: brief visual feedback could be added here
      }).catch(err => {
        console.error('Failed to copy coordinates:', err);
      });
    },
    
    goToUserLocation() {
      if (this.userLocation) {
        this.map.setView([this.userLocation.lat, this.userLocation.lng], 8);
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.userLocation = { lat: latitude, lng: longitude };
            this.map.setView([latitude, longitude], 8);
            this.addUserLocationMarker(latitude, longitude);
          }
        );
      }
    },
    getModelDisplayName(modelId) {
      return this.modelNames[modelId] || modelId;
    },
    
    getElementDisplayName(element) {
      return this.elementNames[element] || element;
    },
    
    onModelChange() {
      // Reset element to first available when model changes
      const elements = this.availableElements;
      this.newLayerElement = elements.length > 0 ? elements[0] : '';
    },
    
    addLayer() {
      if (!this.newLayerElement) return;
      
      const layer = {
        id: this.nextLayerId++,
        model: this.newLayerModel,
        element: this.newLayerElement,
        visible: true,
        opacity: 0.7,
        leafletLayer: null
      };
      
      this.activeLayers.push(layer);
      this.createLeafletLayer(layer);
      // Auto-expand new layer's legend
      this.expandedLegends[layer.id] = true;
      this.updateAllLegends();
      this.showAddLayerModal = false;
      // Restart animation if playing to include new layer
      this.restartAnimationIfPlaying();
    },
    
    removeLayer(index) {
      const layer = this.activeLayers[index];
      this.safeRemoveLayer(layer.leafletLayer);
      layer.leafletLayer = null;
      delete this.expandedLegends[layer.id];
      this.activeLayers.splice(index, 1);
      this.updateAllLegends();
      // Restart animation if playing to reflect removed layer
      this.restartAnimationIfPlaying();
    },
    
    toggleLayerVisibility(layer) {
      if (!this.map || !this.mapReady) return;
      
      if (layer.visible) {
        if (!layer.leafletLayer) {
          this.createLeafletLayer(layer);
        } else {
          layer.leafletLayer.addTo(this.map);
        }
        // Auto-expand when made visible
        this.expandedLegends[layer.id] = true;
      } else {
        // Remove and nullify the layer reference to avoid stale references
        this.safeRemoveLayer(layer.leafletLayer);
        layer.leafletLayer = null;
      }
      this.updateAllLegends();
      // Restart animation if playing to reflect visibility change
      this.restartAnimationIfPlaying();
    },
    
    toggleLegendExpand(layerId) {
      this.expandedLegends[layerId] = !this.expandedLegends[layerId];
    },
    
    createLeafletLayer(layer) {
      if (!this.map || !this.mapReady) return;
      
      // Don't attempt to load tiles without a valid API key
      if (!this.hasValidApiKey) {
        console.warn('Cannot load weather tiles: No API key available');
        return;
      }
      
      // Remove existing layer if present
      if (layer.leafletLayer) {
        this.safeRemoveLayer(layer.leafletLayer);
      }
      
      // Clear any previous errors for this layer
      delete this.layerErrors[layer.id];

      // --- Alerts: use interactive GeoJSON vector layer ---
      if (layer.element === 'alerts') {
        const self = this;
        layer.isVectorAlert = true;
        layer.leafletLayer = createAlertVectorLayer(L, {
          key: this.weatherApiKey,
          serverRoot: this.baseApiUrl,
          opacity: layer.opacity,
          unitGroup: this.effectiveUnitGroup,
          pane: 'weatherPane',
          getDateTimeFn: () => self.currentTime,
        });
        layer.leafletLayer.addTo(this.map);
        return;
      }
      
      const tileUrl = this.buildTileUrl(layer.element, layer.model, this.currentTime);
      const self = this;
      
      layer.leafletLayer = L.tileLayer(tileUrl, {
        opacity: layer.opacity,
        pane: 'weatherPane',
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.visualcrossing.com">Visual Crossing</a>'
      }).addTo(this.map);
      
      // Add error handler for tile loading failures
      layer.leafletLayer.on('tileerror', function(error) {
        self.handleTileError(error, layer);
      });
      
      // Track successful tile loads to clear errors if tiles start working
      layer.leafletLayer.on('tileload', function() {
        // If we had errors but now tiles are loading, clear the layer error
        if (self.layerErrors[layer.id]) {
          delete self.layerErrors[layer.id];
          // Recalculate total error count
          self.tileErrorCount = Object.values(self.layerErrors).reduce((sum, count) => sum + count, 0);
        }
      });
    },
    
    updateLayerOpacity(layer) {
      if (layer.leafletLayer) {
        if (layer.isVectorAlert && layer.leafletLayer.setAlertOpacity) {
          layer.leafletLayer.setAlertOpacity(layer.opacity);
        } else {
          layer.leafletLayer.setOpacity(layer.opacity);
        }
      }
    },
    
    /**
     * Update the opacity of the quick-select layer(s) when slider changes
     */
    updateQuickSelectOpacity() {
      // Update all quick-select layers (supports multi-layer mode)
      const quickLayers = this.activeLayers.filter(l => l.isQuickSelect);
      quickLayers.forEach(layer => {
        layer.opacity = this.quickSelectOpacity;
        if (layer.leafletLayer) {
          layer.leafletLayer.setOpacity(this.quickSelectOpacity);
        }
      });
    },

    updateLayerTiles(layer, time) {
      if (!layer.leafletLayer || !this.map || !this.mapReady) return;

      // Vector alert layers manage their own refresh
      if (layer.isVectorAlert && layer.leafletLayer.setAlertTime) {
        layer.leafletLayer.setAlertTime(time);
        return;
      }
      
      const tileUrl = this.buildTileUrl(layer.element, layer.model, time);
      layer.leafletLayer.setUrl(tileUrl);
    },
    
    /**
     * Safely remove a Leaflet layer with proper cleanup.
     * Prevents errors when removing layers during animations or after map destruction.
     */
    safeRemoveLayer(layer) {
      if (!layer) return;
      
      try {
        // Remove any event listeners the layer might have
        layer.off();
        
        // Check if layer has a map reference and the map exists
        if (this.map && layer._map) {
          this.map.removeLayer(layer);
        }
      } catch (e) {
        // Silently handle errors during cleanup - layer may already be removed
        console.debug('Layer cleanup warning:', e.message);
      }
    },
    
    buildTileUrl(element, model, time, cacheBuster = null) {
      // Map virtual element names to server element names
      const serverElement = element === 'alertswebp' ? 'alerts' : element;
      const sourceParam = model && model !== 'default' ? `&source=${model}` : '';
      const cacheBusterParam = cacheBuster ? `&_cb=${cacheBuster}` : '';
      const refreshParam = this.forceRefresh ? '&refresh=true' : '';
      const roundedTime = this.roundTimeForElement(time, element);
      return `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
             `${serverElement}/{z}/{x}/{y}.webp` +
             `?key=${this.weatherApiKey}` +
             `&time=${this.formatFullDateTime(roundedTime)}` +
             `&unitGroup=${this.effectiveUnitGroup}` +
             `&strict=${this.strictMode}` +
             `${sourceParam}${cacheBusterParam}${refreshParam}`;
    },
    
    refreshAllLayers() {
      if (!this.map || !this.mapReady) return;
      
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          if (layer.leafletLayer) {
            // Layer exists, just update tiles
            this.updateLayerTiles(layer, this.currentTime);
          } else if (this.hasValidApiKey) {
            // Layer was added but not created (no API key at the time) - create it now
            this.createLeafletLayer(layer);
          }
        }
      });
      this.updateAllLegends();
    },
    
    changeBaseMap() {
      if (!this.map) return;
      
      this.safeRemoveLayer(this.baseLayer);
      this.baseLayer = null;
      
      const config = this.baseMaps[this.selectedBaseMap];
      this.baseLayer = L.tileLayer(config.url, {
        subdomains: 'abcd',
        maxZoom: 19,
        attribution: config.attribution
      }).addTo(this.map);
    },
    
    toggleLabels() {
      if (!this.map || !this.mapReady) return;
      
      // Labels toggle now controls whether location names are shown on weather markers
      // This requires Wx (showGeoJson) to be enabled - refresh the GeoJSON tiles to apply
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
    },
    
    toggleTileGrid() {
      if (!this.map || !this.mapReady) return;
      
      if (this.showTileGrid) {
        this.addTileGridLayer();
      } else {
        this.safeRemoveLayer(this.tileGridLayer);
        this.tileGridLayer = null;
      }
    },
    
    addTileGridLayer() {
      if (!this.map || !this.mapReady) return;
      
      this.safeRemoveLayer(this.tileGridLayer);
      this.tileGridLayer = null;
      
      // Create a dedicated pane for tile grid if it doesn't exist
      if (!this.map.getPane('tileGridPane')) {
        this.map.createPane('tileGridPane');
        this.map.getPane('tileGridPane').style.zIndex = 650;
        this.map.getPane('tileGridPane').style.pointerEvents = 'none';
      }
      
      // Create a custom GridLayer using canvas tiles
      const TileGridLayer = L.GridLayer.extend({
        createTile: function(coords) {
          const canvas = document.createElement('canvas');
          const tileSize = this.getTileSize();
          canvas.width = tileSize.x;
          canvas.height = tileSize.y;
          
          const ctx = canvas.getContext('2d');
          
          // Draw border (subtle)
          ctx.strokeStyle = 'rgba(128, 128, 128, 0.4)';
          ctx.lineWidth = 1;
          ctx.strokeRect(0.5, 0.5, tileSize.x - 1, tileSize.y - 1);
          
          // Draw label background
          const label = `z${coords.z} / ${coords.x} / ${coords.y}`;
          ctx.font = '11px monospace';
          const textWidth = ctx.measureText(label).width;
          
          ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
          ctx.fillRect(4, 4, textWidth + 12, 18);
          
          // Draw label text
          ctx.fillStyle = '#fff';
          ctx.fillText(label, 10, 17);
          
          return canvas;
        }
      });
      
      this.tileGridLayer = new TileGridLayer({
        pane: 'tileGridPane'
      }).addTo(this.map);
    },
    
    addLabelsLayer() {
      if (!this.map || !this.mapReady) return;
      
      this.safeRemoveLayer(this.labelLayer);
      this.labelLayer = null;
      
      const isDark = this.selectedBaseMap === 'carto-dark';
      const labelsUrl = isDark 
        ? 'https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png'
        : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png';
      
      this.labelLayer = L.tileLayer(labelsUrl, {
        subdomains: 'abcd',
        maxZoom: 19,
        pane: 'labelsPane',
        attribution: '&copy; <a href="https://carto.com">CARTO</a>'
      }).addTo(this.map);
    },
    
    async updateAllLegends() {
      const visibleLayers = this.activeLayers.filter(l => l.visible && l.leafletLayer);
      
      if (visibleLayers.length === 0) {
        this.visibleLayerLegends = [];
        return;
      }
      
      const legendPromises = visibleLayers.map(async (layer) => {
        const legendUrl = `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/legend/` +
                          `${layer.element}?key=${this.weatherApiKey}&unitGroup=${this.effectiveUnitGroup}`;
        
        try {
          const response = await fetch(legendUrl);
          const raw = await response.json();

          // Handle both single array of bins and array of arrays of bins
          let binGroups;
          if (Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0])) {
            // Response is array of arrays - keep as separate groups
            binGroups = raw;
          } else {
            // Single array - wrap in array for consistent handling
            binGroups = [raw];
          }
          
          // Cache flattened bins for color lookups
          this.elementBinCache[layer.element] = binGroups.flat();
          
          let html = '';
          binGroups.forEach((bins, groupIndex) => {
            if (groupIndex > 0) {
              html += '<div style="margin-top:8px;border-top:1px solid #eee;padding-top:8px;"></div>';
            }
            bins.forEach((bin, index) => {
              if (!bin || !bin.color) return;
              const hasLabel = !!bin.label;
              const label = bin.label || (bin.min ?? '');
              
              if (hasLabel) {
                const textColor = this.getContrastingTextColor(bin.color);
                html += `<div style="display:inline-flex;align-items:center;margin:2px 4px 2px 0;background:${bin.color};color:${textColor};padding:2px 8px;border-radius:10px;font-size:11px;font-weight:500;">${label}</div>`;
              } else {
                html += `<div style="display:inline-block;text-align:center;margin-right:5px;vertical-align:top;">
                          <div style="background-color:${bin.color};width:20px;height:20px;"></div>
                          ${index % Math.max(1, Math.ceil(bins.length / 10)) === 0 ? `<span style="font-size:10px;">${label}</span>` : ''}
                        </div>`;
              }
              
            });
          });
          
          // Use quickSelectModelId if available (the nice model ID like 'ecmwf'),
          // otherwise fall back to layer.model
          const modelId = layer.quickSelectModelId || layer.model;
          const modelDisplayName = this.getModelDisplayName(modelId);
          const elementDisplayName = this.getElementDisplayName(layer.element);
          
          // Get model run time if available
          const modelConfig = this.modelOptions[modelId];
          let modelRunInfo = '';
          if (modelConfig?.modelRunTimeEpoch) {
            const runDate = new Date(modelConfig.modelRunTimeEpoch * 1000);
            const runTimeStr = runDate.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
            modelRunInfo = `<div style="font-size:10px;color:#888;margin-top:2px;">Run: ${runTimeStr}</div>`;
          }
          
          return {
            layerId: layer.id,
            title: `${modelDisplayName} - ${elementDisplayName}`,
            modelRunInfo: modelRunInfo,
            html: html
          };
        } catch (error) {
          console.error('Error loading legend for', layer.element, error);
          return null;
        }
      });
      
      const results = await Promise.all(legendPromises);
      this.visibleLayerLegends = results.filter(r => r !== null);
    },
    
    toggleGeoJsonLayer() {
      if (!this.map || !this.mapReady) return;
      
      if (this.showGeoJson) {
        if (!this.geoJsonLayer) {
          this.addGeoJsonTileLayer();
        } else {
          const pane = this.map.getPane('citiesPane');
          if (pane) pane.style.display = '';
          this.refreshGeoJsonTiles();
        }
      } else {
        // Abort pending requests first
        this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
        this.geoJsonAbortControllers.clear();
        
        // Clean up tile groups
        Object.keys(this.geoJsonTiles).forEach(key => {
          this.safeRemoveLayer(this.geoJsonTiles[key]);
        });
        this.geoJsonTiles = {};
        
        // Remove and destroy the GeoJSON layer completely to prevent stale animation handlers
        if (this.geoJsonLayer) {
          this.geoJsonLayer.off();
          if (this.geoJsonLayer._map) {
            this.map.removeLayer(this.geoJsonLayer);
          }
          this.geoJsonLayer = null;
        }
      }
    },
    
     addGeoJsonTileLayer() {
      if (!this.map || !this.mapReady || this.geoJsonLayer) return;
      
      const self = this;
      const GeoJSONGridLayer = L.GridLayer.extend({
        createTile: function(coords, done) {
          const tile = document.createElement('div');
          const key = `${coords.z}/${coords.x}/${coords.y}`;
          
          // Check if map is still valid
          if (!self.map || !self.mapReady) {
            done(null, tile);
            return tile;
          }
          
          if (self.geoJsonAbortControllers.has(key)) {
            self.geoJsonAbortControllers.get(key).abort();
          }
          
          if (self.geoJsonTiles[key]) {
            done(null, tile);
            return tile;
          }
          
          const controller = new AbortController();
          self.geoJsonAbortControllers.set(key, controller);
          
          const url = `${self.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
                      `current/${coords.z}/${coords.x}/${coords.y}.geojson` +
                      `?key=${self.weatherApiKey}&unitGroup=${self.effectiveUnitGroup}&strict=${self.strictMode}${self.forceRefresh ? '&refresh=true' : ''}`;
          
          fetch(url, { signal: controller.signal })
            .then(r => r.json())
            .then(fc => {
              // Double-check map validity before adding layers
              if (!fc || !fc.features || !self.showGeoJson || !self.map || !self.mapReady) return;
              
              if (self.geoJsonTiles[key]) {
                self.safeRemoveLayer(self.geoJsonTiles[key]);
              }
              
              const tileGroup = L.layerGroup();
              const tileMarkers = []; // Store marker data for this tile
              
              fc.features.forEach(feature => {
                if (feature.geometry.type !== 'Point') return;
                
                const [lng, lat] = feature.geometry.coordinates;
                const props = feature.properties || {};
                const cc = props.data?.currentConditions || {};
                
                // Get display value based on selected element
                const displayInfo = self.getGeoJsonDisplayInfo(cc);
                if (!displayInfo) return;
                
                const cityName = props.resolvedAddress || `${lat.toFixed(2)},${lng.toFixed(2)}`;
                
                // Build tooltip text
                const titleText = `${cityName}\nTemp: ${cc.temp ?? 'N/A'}°\nPrecip: ${cc.precip ?? 'N/A'}\nWind: ${cc.windspeed ?? 'N/A'} ${self.getWindDirectionLabel(cc.winddir)}`;
                
                // Shorten city name for display (first part before comma, max 12 chars)
                let shortCityName = (props.resolvedAddress || '').split(',')[0].substring(0, 12);
                if (shortCityName.length === 12) shortCityName += '…';
                
                let markerHtml;
                let markerWidth = displayInfo.width;
                let markerHeight = 28;
                
                if (displayInfo.isWind) {
                  // Wind marker with arrow
                  const arrowRotation = displayInfo.winddir;
                  if (self.showLabels && shortCityName) {
                    // Integrated: city name with wind value in rounded rect
                    markerHtml = `
                      <div class="wind-marker-labeled" title="${titleText.replace(/"/g, '&quot;')}" style="display:flex;align-items:center;background:rgba(255,255,255,0.92);border-radius:6px;padding:3px 4px 3px 8px;gap:6px;box-shadow:0 1px 3px rgba(0,0,0,0.25);">
                        <span style="font-size:12px;color:#333;white-space:nowrap;font-weight:400;">${shortCityName}</span>
                        <div style="position:relative;width:40px;height:32px;flex-shrink:0;">
                          <div style="position:absolute;top:2px;left:6px;background:${displayInfo.fill};color:${displayInfo.textColor};width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;">${displayInfo.label}</div>
                          <svg style="position:absolute;top:-2px;left:4px;width:32px;height:32px;transform:rotate(${arrowRotation}deg);" viewBox="0 0 32 32">
                            <path d="M16 2 L20 8 L16 5 L12 8 Z" fill="${displayInfo.textColor}" stroke="${displayInfo.fill}" stroke-width="1"/>
                          </svg>
                        </div>
                      </div>`;
                    markerWidth = 130;
                    markerHeight = 38;
                  } else {
                    markerHtml = `
                      <div class="wind-marker" title="${titleText.replace(/"/g, '&quot;')}" style="position:relative;width:44px;height:44px;">
                        <div style="position:absolute;top:8px;left:8px;background:${displayInfo.fill};color:${displayInfo.textColor};width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;">${displayInfo.label}</div>
                        <svg style="position:absolute;top:0;left:0;width:44px;height:44px;transform:rotate(${arrowRotation}deg);" viewBox="0 0 44 44">
                          <path d="M22 1 L27 10 L22 7 L17 10 Z" fill="${displayInfo.textColor}" stroke="${displayInfo.fill}" stroke-width="1"/>
                        </svg>
                      </div>`;
                    markerWidth = 44;
                    markerHeight = 44;
                  }
                } else {
                  if (self.showLabels && shortCityName) {
                    // Estimate width based on city name length
                    const estimatedNameWidth = shortCityName.length * 7 + 16;
                    const valueWidth = 40;
                    markerWidth = estimatedNameWidth + valueWidth;
                    
                    // Integrated pill: white background with city name, colored rounded rect value on right
                    markerHtml = `
                      <div class="marker-with-label" title="${titleText.replace(/"/g, '&quot;')}" style="display:inline-flex;align-items:stretch;background:rgba(255,255,255,0.92);border-radius:6px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.25);">
                        <span style="font-size:12px;color:#333;white-space:nowrap;font-weight:400;padding:4px 6px 4px 8px;display:flex;align-items:center;">${shortCityName}</span>
                        <div style="background:${displayInfo.fill};color:${displayInfo.textColor};display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;padding:4px 8px;min-width:32px;">${displayInfo.label}</div>
                      </div>`;
                    markerHeight = 28;
                  } else {
                    markerHtml = `<div class="circle-marker" title="${titleText.replace(/"/g, '&quot;')}" style="background:${displayInfo.fill};color:${displayInfo.textColor};height:28px;border-radius:6px;display:flex;align-items:center;justify-content:center;text-align:center;cursor:pointer;font-size:13px;font-weight:600;padding:0 8px;white-space:nowrap;">${displayInfo.label}</div>`;
                  }
                }
                
                const icon = L.divIcon({
                  className: 'city-marker-icon',
                  html: markerHtml,
                  iconSize: [markerWidth, markerHeight],
                  iconAnchor: [markerWidth / 2, markerHeight / 2]
                });
                
                const marker = L.marker([lat, lng], { icon, pane: 'citiesPane', interactive: false });
                marker._markerWidth = markerWidth;
                marker._markerHeight = markerHeight;
                
                tileGroup.addLayer(marker);
                tileMarkers.push({ lat, lng, width: markerWidth, height: markerHeight, marker, tileKey: key });
              });
              
              // Add tile markers to global registry and trigger declutter
              self.geoJsonMarkerData = self.geoJsonMarkerData.filter(m => m.tileKey !== key);
              self.geoJsonMarkerData.push(...tileMarkers);
              
              self.geoJsonTiles[key] = tileGroup;
              if (self.map && self.mapReady && self.showGeoJson) {
                tileGroup.addTo(self.map);
                self.scheduleGeoJsonDeclutter();
              }
              done(null, tile);
            })
            .catch(err => {
              if (err.name !== 'AbortError') console.warn('GeoJSON tile error:', err);
              done(err, tile);
            })
            .finally(() => {
              self.geoJsonAbortControllers.delete(key);
            });
          
          return tile;
        },
        
        _removeTile: function(key) {
          // Clean up markers when tile is removed
          // Check if tiles object still exists (may be null during destruction)
          const tileData = this._tiles ? this._tiles[key] : null;
          const coords = tileData?.coords;
          if (coords) {
            const tileKey = `${coords.z}/${coords.x}/${coords.y}`;
            if (self.geoJsonTiles[tileKey]) {
              self.safeRemoveLayer(self.geoJsonTiles[tileKey]);
              delete self.geoJsonTiles[tileKey];
            }
            // Remove markers from global registry
            self.geoJsonMarkerData = self.geoJsonMarkerData.filter(m => m.tileKey !== tileKey);
            
            if (self.geoJsonAbortControllers.has(tileKey)) {
              self.geoJsonAbortControllers.get(tileKey).abort();
              self.geoJsonAbortControllers.delete(tileKey);
            }
          }
          // Only call parent if layer still has map reference
          if (this._map) {
            L.GridLayer.prototype._removeTile.call(this, key);
          }
        }
        // Note: Animation method overrides removed - now handled by global L.GridLayer prototype patches
      });
      
      this.geoJsonLayer = new GeoJSONGridLayer({
        minZoom: 5,
        maxZoom: 18,
        updateWhenIdle: true,
        updateWhenZooming: false,
        keepBuffer: 2
      });
      
      this.geoJsonLayer.addTo(this.map);
    },

    getGeoJsonDisplayInfo(cc) {
      const element = this.geoJsonElement;
      
      if (element === 'temp') {
        const temp = cc.temp;
        if (temp == null) return null;
        const fill = this.getColor(temp, 'temp');
        const textColor = this.getContrastingTextColor(fill);
        return { label: Math.round(temp) + '°', fill, textColor, width: 28, isWind: false };
      } 
      else if (element === 'precip') {
        const precip = cc.precip;
        if (precip == null) return null;
        // Use a blue gradient for precip
        const fill = precip > 0 ? this.getPrecipColor(precip) : '#e0e0e0';
        const textColor = this.getContrastingTextColor(fill);
        return { label: precip.toFixed(1), fill, textColor, width: 36, isWind: false };
      } 
      else if (element === 'wind') {
        const windspeed = cc.windspeed;
        const winddir = cc.winddir;
        if (windspeed == null) return null;
        const fill = this.getWindColor(windspeed);
        const textColor = this.getContrastingTextColor(fill);
        return { 
          label: Math.round(windspeed), 
          fill, 
          textColor, 
          width: 36, 
          isWind: true,
          winddir: winddir ?? 0
        };
      }
      
      return null;
    },
    
    getWindDirectionLabel(degrees) {
      if (degrees == null) return '';
      const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      const index = Math.round(degrees / 45) % 8;
      return directions[index];
    },
    
    getPrecipColor(precip) {
      // Simple precipitation color scale
      if (precip <= 0) return '#e0e0e0';
      if (precip < 0.5) return '#a8d5e5';
      if (precip < 2) return '#5fb3d4';
      if (precip < 5) return '#3498db';
      if (precip < 10) return '#2471a3';
      return '#1a4971';
    },
    
    getWindColor(windspeed) {
      // Wind speed color scale (assuming metric m/s or us mph based on unitGroup)
      if (windspeed < 5) return '#c8e6c9';
      if (windspeed < 10) return '#81c784';
      if (windspeed < 15) return '#ffd54f';
      if (windspeed < 25) return '#ff9800';
      if (windspeed < 40) return '#f44336';
      return '#9c27b0';
    },
    refreshGeoJsonTiles() {
      if (!this.geoJsonLayer || !this.showGeoJson || !this.map || !this.mapReady) return;
      
      Object.keys(this.geoJsonTiles).forEach(key => {
        this.safeRemoveLayer(this.geoJsonTiles[key]);
      });
      this.geoJsonTiles = {};
      this.geoJsonMarkerData = []; // Clear marker registry
      this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
      this.geoJsonAbortControllers.clear();
      this.geoJsonLayer.redraw();
    },
    
    /**
     * Schedule a declutter pass with debouncing
     */
    scheduleGeoJsonDeclutter() {
      if (this.geoJsonDeclutterTimeout) {
        clearTimeout(this.geoJsonDeclutterTimeout);
      }
      
      // Use short timeout to debounce and ensure DOM is ready
      this.geoJsonDeclutterTimeout = setTimeout(() => {
        this.geoJsonDeclutterTimeout = null;
        this.declutterGeoJsonMarkers();
      }, 50);
    },
    
    /**
     * Perform global decluttering across all tile markers
     */
    declutterGeoJsonMarkers() {
      if (!this.map || !this.mapReady || !this.showGeoJson) return;
      if (this.geoJsonMarkerData.length === 0) return;
      
      const padding = 4;
      const occupiedRegions = [];
      
      // Sort markers by latitude (north to south) for consistent priority
      const sortedMarkers = [...this.geoJsonMarkerData].sort((a, b) => b.lat - a.lat);
      
      sortedMarkers.forEach(({ lat, lng, width, height, marker }) => {
        const point = this.map.latLngToContainerPoint([lat, lng]);
        const x = point.x;
        const y = point.y;
        
        // Check if marker is in viewport
        const mapSize = this.map.getSize();
        const inView = x > -width && x < mapSize.x + width && y > -height && y < mapSize.y + height;
        
        if (!inView) {
          // Hide markers outside viewport
          if (marker._icon) marker._icon.style.display = 'none';
          return;
        }
        
        // Check for overlap
        const newRect = {
          left: x - width / 2 - padding,
          right: x + width / 2 + padding,
          top: y - height / 2 - padding,
          bottom: y + height / 2 + padding
        };
        
        let overlaps = false;
        for (const rect of occupiedRegions) {
          if (!(newRect.right < rect.left || newRect.left > rect.right ||
                newRect.bottom < rect.top || newRect.top > rect.bottom)) {
            overlaps = true;
            break;
          }
        }
        
        if (overlaps) {
          if (marker._icon) marker._icon.style.display = 'none';
        } else {
          occupiedRegions.push(newRect);
          if (marker._icon) marker._icon.style.display = '';
        }
      });
    },
    
    toggleMoviePlay() {
      if (this.isPlaying) {
        this.pauseMovie();
      } else if (this.canvasFrameCache.length > 0 || Object.keys(this.animationBuffers).length > 0) {
        // Have cached frames - resume from where we paused
        this.resumeMovie();
      } else {
        // No cached frames - start fresh
        this.startMovie();
      }
    },
    
    /**
     * Toggle the timeline panel visibility.
     * Generates time keys if they haven't been generated yet.
     */
    toggleTimelinePanel() {
      this.showTimelinePanel = !this.showTimelinePanel;
      // If showing timeline and no time keys yet, generate them
      if (this.showTimelinePanel && this.animationTimeKeys.length === 0) {
        this.animationTimeKeys = this.generateAnimationTimeKeys();
      }
    },
    
    /**
     * Reset the custom loop steps to use auto-calculated values
     */
    resetLoopWindow() {
      this.customStepsBack = null;
      this.customStepsForward = null;
    },
    
    /**
     * Invalidate the animation cache so next play will regenerate frames.
     * Called when loop settings change while paused.
     */
    invalidateAnimationCache() {
      // Don't invalidate while actively playing - the watcher will call restartAnimationIfPlaying
      if (this.isPlaying || this.isPreloadingFrames) {
        return;
      }
      // Clear cached frames so next play uses new settings
      this.canvasFrameCache = [];
      this.animationTimeKeys = [];
      this.animationBuffers = {};
    },
    
    /**
     * Start the animation with double-buffered frame preloading.
     * Routes to either canvas-based or tile-layer-based animation.
     */
    async startMovie() {
      // console.log('startMovie called, useCanvasAnimation:', this.useCanvasAnimation);
      
      // If already preloading, don't start another
      if (this.isPreloadingFrames) {
        // console.log('Already preloading, returning');
        return;
      }
      
      // Stop any existing animation first
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      
      // Route to appropriate animation mode
      if (this.useCanvasAnimation) {
        // console.log('Using canvas animation mode');
        await this.startCanvasMovie();
      } else {
        // console.log('Using tile layer animation mode');
        await this.startTileLayerMovie();
      }
    },
    
    /**
     * Start animation using TRUE double-buffering.
     * Uses only 2 tile layers per element (front + back buffers).
     * Preloads next frame in back buffer while displaying front buffer.
     * Swaps buffers when back buffer is loaded.
     */
    async startTileLayerMovie() {
      // console.log('startTileLayerMovie called');
      
      // Generate time keys for animation
      this.animationTimeKeys = this.generateAnimationTimeKeys();
      // console.log('Generated time keys:', this.animationTimeKeys.length);
      
      if (this.animationTimeKeys.length === 0) {
        // console.log('No time keys, falling back to simple movie');
        this.startSimpleMovie();
        return;
      }
      
      // Show preload indicator
      this.isPreloadingFrames = true;
      this.preloadProgress = 0;
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      // console.log('Visible layers:', visibleLayers.length);
      
      if (visibleLayers.length === 0) {
        // console.log('No visible layers');
        this.isPreloadingFrames = false;
        this.startSimpleMovie();
        return;
      }
      
      // Initialize double buffers (this loads the first two frames)
      this.preloadProgress = 10;
      // console.log('Initializing double buffers...');
      await this.initializeDoubleBuffers();
      // console.log('Double buffers initialized, animationBuffers:', Object.keys(this.animationBuffers).length);
      
      if (!this.isPreloadingFrames) {
        // console.log('Preloading was cancelled');
        return; // Cancelled
      }
      
      this.preloadProgress = 100;
      this.isPreloadingFrames = false;
      this.isPlaying = true;
      this.animationFrameIndex = this.findCurrentFrameIndex();
      // console.log('Starting playback at frame index:', this.animationFrameIndex);
      
      // Show the front buffer
      this.showBuffer('front');
      
      // Mark next frame as ready (back buffer is already loaded)
      this.nextFrameReady = true;
      
      // Start the animation loop
      this.movieInterval = setInterval(() => {
        this.advanceDoubleBufferFrame();
      }, 1000 / this.playbackSpeed);
      
      // console.log('Animation started with interval');
    },
    
    /**
     * Start animation using canvas rasterization (lower memory approach).
     * Renders each frame to a canvas image and animates via canvas drawing.
     * Uses progressive loading: shows slow preview while remaining frames load.
     */
    async startCanvasMovie() {
      // Preload and rasterize frames with progressive preview
      await this.preloadCanvasFramesProgressive();
      
      // If preload was cancelled or no frames, fall back to simple animation
      if (this.canvasFrameCache.length === 0) {
        this.startSimpleMovie();
        return;
      }
      
      // Animation may already be playing from progressive preview
      if (!this.isPlaying) {
        this.isPlaying = true;
        this.animationFrameIndex = 0;
        this.showCanvasOverlay();
      }
      
      // End oscillation preview mode - now we have all frames
      this.previewOscillating = false;
      
      // Pre-compute all optical flow fields for morph/motion modes (avoids pauses during playback)
      if (this.smoothAnimationMode === 'morph' || this.smoothAnimationMode === 'motion') {
        await this.precomputeAllMotionFields();
      }
      
      // Reset to normal speed
      this.previewSpeedMultiplier = 1;
      
      // Ensure we're showing frame 0 to start fresh
      this.animationFrameIndex = 0;
      this.crossfadeProgress = 0;
      this.showCanvasFrame(this.animationFrameIndex, 0);
      
      // Use requestAnimationFrame with crossfade for smooth animation
      this.startCrossfadeLoop();
    },
    
    /**
     * Fallback simple animation without double-buffering (for when preload fails)
     */
    startSimpleMovie() {
      // Clear any existing interval
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      
      this.isPlaying = true;
      this.movieInterval = setInterval(() => {
        this.incrementTime();
      }, 1000 / this.playbackSpeed);
    },
    
    stopMovie() {
      this.isPlaying = false;
      this.isPreloadingFrames = false;
      this.preloadProgress = 0;
      this.nextFrameReady = false;
      this.previewSpeedMultiplier = 1; // Reset preview speed
      this.previewOscillating = false; // Reset oscillation mode
      this.previewLastFrameIndex = 0; // Reset last frame index for preview
      this.progressiveMaxFrame = 0; // Reset progressive loading max frame
      
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      
      // Restore main layers and clear animation caches
      this.restoreMainLayers();
      this.clearDoubleBuffers();
      this.clearCanvasCache();
      this.hideCanvasOverlay();
    },
    
    /**
     * Initialize double buffers (front + back) for each visible layer.
     * Front buffer is loaded with the starting frame, back with the next frame.
     */
    async initializeDoubleBuffers() {
      // console.log('initializeDoubleBuffers: starting');
      
      // Don't attempt to load tiles without a valid API key
      if (!this.hasValidApiKey) {
        console.warn('Cannot initialize animation buffers: No API key available');
        return;
      }
      
      this.clearDoubleBuffers();
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      // console.log('initializeDoubleBuffers: visibleLayers count:', visibleLayers.length);
      
      if (visibleLayers.length === 0 || this.animationTimeKeys.length === 0) {
        // console.log('initializeDoubleBuffers: early return - no layers or no time keys');
        return;
      }
      
      // Get starting frame indices
      const frontIndex = this.findCurrentFrameIndex();
      const backIndex = (frontIndex + 1) % this.animationTimeKeys.length;
      
      const frontTimeKey = this.animationTimeKeys[frontIndex];
      const backTimeKey = this.animationTimeKeys[backIndex];
      // console.log('initializeDoubleBuffers: frontIndex:', frontIndex, 'backIndex:', backIndex);
      
      const loadPromises = [];
      
      for (const layer of visibleLayers) {
        // Vector alert layers manage their own time/rendering — skip them here
        if (layer.isVectorAlert) continue;
        // console.log('initializeDoubleBuffers: processing layer:', layer.id, layer.element, layer.model);
        // Build URLs for front and back buffers
        const frontUrl = this.buildTileUrl(layer.element, layer.model, new Date(frontTimeKey));
        const backUrl = this.buildTileUrl(layer.element, layer.model, new Date(backTimeKey));
        // console.log('initializeDoubleBuffers: frontUrl:', frontUrl.substring(0, 100) + '...');
        
        // Create front buffer layer with actual URL (starts invisible)
        const frontLayer = L.tileLayer(frontUrl, {
          opacity: 0,
          pane: 'weatherPane',
          maxZoom: 18,
          attribution: ''
        }).addTo(this.map);
        
        // Create back buffer layer with actual URL (starts invisible)
        const backLayer = L.tileLayer(backUrl, {
          opacity: 0,
          pane: 'weatherPane',
          maxZoom: 18,
          attribution: ''
        }).addTo(this.map);
        
        this.animationBuffers[layer.id] = {
          front: frontLayer,
          back: backLayer,
          frontTimeKey: frontTimeKey,
          backTimeKey: backTimeKey,
          targetOpacity: layer.opacity
        };
        // console.log('initializeDoubleBuffers: created buffer for layer:', layer.id, 'animationBuffers keys:', Object.keys(this.animationBuffers));
        
        // Wait for front layer to load
        const frontPromise = new Promise((resolve) => {
          let resolved = false;
          const onLoad = () => {
            if (!resolved) {
              resolved = true;
              frontLayer.off('load', onLoad);
              resolve();
            }
          };
          frontLayer.on('load', onLoad);
          setTimeout(onLoad, 3000);
        });
        loadPromises.push(frontPromise);
        
        // Wait for back layer to load  
        const backPromise = new Promise((resolve) => {
          let resolved = false;
          const onLoad = () => {
            if (!resolved) {
              resolved = true;
              backLayer.off('load', onLoad);
              resolve();
            }
          };
          backLayer.on('load', onLoad);
          setTimeout(onLoad, 3000);
        });
        loadPromises.push(backPromise);
        
        // Hide the main layer during animation
        if (layer.leafletLayer) {
          layer.leafletLayer.setOpacity(0);
        }
      }
      
      // Wait for all buffers to load
      await Promise.all(loadPromises);
    },
    
    /**
     * Load a specific frame into a buffer (front or back).
     * Returns a promise that resolves when the tiles are loaded.
     */
    async loadFrameToBuffer(bufferName, frameIndex) {
      if (frameIndex < 0 || frameIndex >= this.animationTimeKeys.length) return;
      
      const timeKey = this.animationTimeKeys[frameIndex];
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      const loadPromises = [];
      
      for (const layer of visibleLayers) {
        const buffer = this.animationBuffers[layer.id];
        if (!buffer) continue;
        
        const bufferLayer = buffer[bufferName];
        if (!bufferLayer) continue;
        
        // Check if this buffer already has this frame loaded
        if (buffer[bufferName + 'TimeKey'] === timeKey) {
          continue; // Already loaded
        }
        
        // Build URL for this frame
        const tileUrl = this.buildTileUrl(layer.element, layer.model, new Date(timeKey));
        
        // Create load promise BEFORE calling setUrl to avoid race condition
        const loadPromise = new Promise((resolve) => {
          let resolved = false;
          const onLoad = () => {
            if (!resolved) {
              resolved = true;
              bufferLayer.off('load', onLoad);
              resolve();
            }
          };
          // Attach listener BEFORE triggering load
          bufferLayer.on('load', onLoad);
          // Timeout after 2 seconds
          setTimeout(onLoad, 2000);
        });
        
        loadPromises.push(loadPromise);
        
        // Now trigger the tile loading
        bufferLayer.setUrl(tileUrl);
        buffer[bufferName + 'TimeKey'] = timeKey;
      }
      
      if (loadPromises.length > 0) {
        await Promise.all(loadPromises);
      }
    },
    
    /**
     * Show a specific buffer (front or back), hiding the other.
     */
    showBuffer(bufferName) {
      const otherBuffer = bufferName === 'front' ? 'back' : 'front';
      
      for (const layerId in this.animationBuffers) {
        const buffer = this.animationBuffers[layerId];
        if (!buffer) continue;
        
        // Show the active buffer with its opacity
        if (buffer[bufferName]) {
          buffer[bufferName].setOpacity(buffer.targetOpacity);
        }
        
        // Hide the other buffer
        if (buffer[otherBuffer]) {
          buffer[otherBuffer].setOpacity(0);
        }
      }
      
      // Update currentTime based on which buffer is showing
      const timeKey = this.animationBuffers[Object.keys(this.animationBuffers)[0]]?.[bufferName + 'TimeKey'];
      if (timeKey) {
        this.currentTime = new Date(timeKey);
        // Note: Don't call updateAllLegends() here - legends don't change during animation
      }
    },

    /**
     * Preload the next frame in the back buffer.
     */
    async preloadNextFrame() {
      if (!this.isPlaying) return;
      
      const nextIndex = (this.animationFrameIndex + 1) % this.animationTimeKeys.length;
      
      // Determine which buffer is currently back (not showing)
      // We alternate: if front is showing, load into back
      await this.loadFrameToBuffer('back', nextIndex);
      this.nextFrameReady = true;
    },
    
    /**
     * Advance to next frame using double-buffer swap.
     */
    advanceDoubleBufferFrame() {
      if (!this.isPlaying) return;
      
      // Advance frame index
      this.animationFrameIndex = (this.animationFrameIndex + 1) % this.animationTimeKeys.length;
      
      // Swap buffers: back becomes front, front becomes back
      this.swapBuffers();
      
      // Show the new front buffer
      this.showBuffer('front');
      
      // Start preloading the next frame into the back buffer
      this.nextFrameReady = false;
      this.preloadNextFrame();
    },
    
    /**
     * Swap front and back buffers.
     */
    swapBuffers() {
      for (const layerId in this.animationBuffers) {
        const buffer = this.animationBuffers[layerId];
        if (!buffer) continue;
        
        // Swap the layer references
        const tempLayer = buffer.front;
        buffer.front = buffer.back;
        buffer.back = tempLayer;
        
        // Swap the time keys
        const tempTimeKey = buffer.frontTimeKey;
        buffer.frontTimeKey = buffer.backTimeKey;
        buffer.backTimeKey = tempTimeKey;
      }
    },
    
    /**
     * Clear double buffers and remove buffer layers from map.
     */
    clearDoubleBuffers() {
      for (const layerId in this.animationBuffers) {
        const buffer = this.animationBuffers[layerId];
        if (!buffer) continue;
        
        this.safeRemoveLayer(buffer.front);
        this.safeRemoveLayer(buffer.back);
      }
      
      this.animationBuffers = {};
      // NOTE: Don't clear animationTimeKeys here - they are managed by startTileLayerMovie
      this.animationFrameIndex = 0;
      this.nextFrameReady = false;
    },
    
    /**
     * Generate the list of time keys for the animation loop.
     * Returns array of ISO time strings for each frame.
     * Uses step-based window: stepsBack from current time, stepsForward from current time.
     * 
     * If the model has temporalAvailability, uses variable intervals based on
     * time offset from model run time. Otherwise uses fixed currentTimeSkip.
     */
    generateAnimationTimeKeys() {
      const { stepsBack, stepsForward } = this.effectiveLoopSteps;
      const centerTime = this.currentTime;
      const modelConfig = this.primaryModelConfig;
      const timeRange = this.modelTimeRange;
      
      const timeKeys = [];
      
      // Generate steps back from center time
      let t = new Date(centerTime);
      for (let i = 0; i < stepsBack; i++) {
        // Get the interval for the previous step
        const intervalMinutes = this.getTimeIntervalMinutes(t, modelConfig, -1);
        t = new Date(t.getTime() - intervalMinutes * 60 * 1000);
        
        // Check if time is within model's valid range
        if (timeRange && t < timeRange.minTime) break;
        
        timeKeys.unshift(t.toISOString());
      }
      
      // Add center time
      timeKeys.push(centerTime.toISOString());
      
      // Generate steps forward from center time
      t = new Date(centerTime);
      for (let i = 0; i < stepsForward; i++) {
        // Get the interval for the next step
        const intervalMinutes = this.getTimeIntervalMinutes(t, modelConfig, 1);
        t = new Date(t.getTime() + intervalMinutes * 60 * 1000);
        
        // Check if time is within model's valid range
        if (timeRange && t > timeRange.maxTime) break;
        
        timeKeys.push(t.toISOString());
      }
      
      // Limit to max frames
      return timeKeys.slice(0, this.maxAnimationFrames);
    },
    
    /**
     * Find the frame index closest to the current time.
     */
    findCurrentFrameIndex() {
      if (this.animationTimeKeys.length === 0) return 0;
      
      const currentIso = this.currentTime.toISOString();
      const idx = this.animationTimeKeys.indexOf(currentIso);
      return idx >= 0 ? idx : 0;
    },
    
    /**
     * Restore the main layers after animation stops.
     */
    restoreMainLayers() {
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      
      for (const layer of visibleLayers) {
        if (!layer.leafletLayer) continue;
        
        // Vector alert layers use setAlertOpacity, not setOpacity
        if (layer.isVectorAlert) {
          if (layer.leafletLayer.setAlertOpacity) {
            layer.leafletLayer.setAlertOpacity(layer.opacity);
          }
        } else {
          layer.leafletLayer.setOpacity(layer.opacity);
        }
        
        // Update main layer to current time
        this.updateLayerTiles(layer, this.currentTime);
      }
    },
    
    /**
     * Restart animation if currently playing.
     * Used when settings change that require regenerating frames (loop window, time skip, etc.)
     * Uses debouncing to avoid rapid restarts during slider drags.
     */
    async restartAnimationIfPlaying() {
      if (this.isPlaying || this.isPreloadingFrames) {
        // Stop current animation immediately
        this.stopMovie();
        
        // Clear any pending restart
        if (this.animationRestartTimer) {
          clearTimeout(this.animationRestartTimer);
        }
        
        // Debounce the restart (wait 300ms for user to finish adjusting sliders)
        this.animationRestartTimer = setTimeout(async () => {
          await this.startMovie();
        }, 300);
      }
    },
    
    /**
     * Handle map movement (pan/zoom) during animation.
     * Stops animation because tiles need to be reloaded for new viewport.
     */
    handleMapMoveForAnimation() {
      // Check if we have cached frames (either playing or paused with animation)
      const hasAnimationState = this.canvasFrameCache.length > 0;
      
      if (hasAnimationState) {
        // Clear any pending restart timer
        if (this.animationRestartTimer) {
          clearTimeout(this.animationRestartTimer);
          this.animationRestartTimer = null;
        }
        // Stop animation completely - user is interacting with the map
        // This clears the cache, hides the canvas, and restores normal layers
        this.stopMovie();
      }
    },

    adjustPlaybackSpeed() {
      if (this.isPlaying) {
        // For crossfade/morph/motion modes, the requestAnimationFrame loop handles timing
        // based on playbackSpeed automatically - no need to restart anything
        if (this.smoothAnimationMode !== 'off') {
          // Speed change takes effect automatically in the animate() loop
          return;
        }
        
        // For 'off' mode, restart the interval with the new speed
        if (this.movieInterval) {
          clearInterval(this.movieInterval);
        }
        
        // Use the appropriate advance method based on animation mode
        if (this.useCanvasAnimation) {
          this.movieInterval = setInterval(() => {
            this.advanceCanvasFrame();
          }, 1000 / this.playbackSpeed);
        } else {
          this.movieInterval = setInterval(() => {
            this.advanceDoubleBufferFrame();
          }, 1000 / this.playbackSpeed);
        }
      }
    },
    
    /**
     * Set the animation smoothness mode.
     * @param {string} mode - 'off', 'crossfade', 'morph', or 'motion'
     */
    onForceRefreshChange() {
      this.saveSettings();
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
    },
    
    setSmoothMode(mode) {
      this.smoothAnimationMode = mode;
      // Update crossfadeEnabled based on mode
      this.crossfadeEnabled = mode !== 'off';
      // Clear optical flow cache when switching modes
      this.opticalFlowCache = {};
      // Clear WebGL texture cache
      this.webglTextures = {};
      // Reset WebGL initialization to pick up any changes
      this.webglInitialized = false;
    },
    
    /**
     * Toggle morph debug mode on/off.
     */
    toggleMorphDebug() {
      this.morphDebugMode = !this.morphDebugMode;
      if (this.morphDebugMode) {
        // Stop any playing animation
        if (this.isPlaying) {
          this.pauseMovie();
        }
        // Initialize debug state
        this.morphDebugFrameIndex = 0;
        this.morphDebugT = 0;
        // Show the canvas overlay if not already shown
        if (!this.animationCanvas && this.canvasFrameCache.length > 0) {
          this.showCanvasOverlay();
        }
        this.renderMorphDebugFrame();
      }
    },
    
    /**
     * Set debug t value and render.
     */
    setMorphDebugT(t) {
      this.morphDebugT = t;
      this.renderMorphDebugFrame();
    },
    
    /**
     * Get motion info for current debug frame pair.
     */
    getMorphDebugMotionInfo() {
      const cacheKey = `field_${this.morphDebugFrameIndex}`;
      const field = this.opticalFlowCache[cacheKey];
      if (!field) return 'No motion data';
      
      // Calculate average motion
      let sumVx = 0, sumVy = 0, count = 0;
      for (let i = 0; i < field.vectors.length; i += 2) {
        sumVx += field.vectors[i];
        sumVy += field.vectors[i + 1];
        count++;
      }
      const avgVx = (sumVx / count).toFixed(1);
      const avgVy = (sumVy / count).toFixed(1);
      return `avg (${avgVx}, ${avgVy}) px`;
    },
    
    /**
     * Render a debug frame at the current debug settings.
     */
    renderMorphDebugFrame() {
      if (!this.animationCanvas || this.canvasFrameCache.length < 2) return;
      
      const frameIndex = this.morphDebugFrameIndex;
      const nextIndex = (frameIndex + 1) % this.canvasFrameCache.length;
      const t = this.morphDebugT;
      
      const frame1 = this.canvasFrameCache[frameIndex];
      const frame2 = this.canvasFrameCache[nextIndex];
      
      if (!frame1?.img?.complete || !frame2?.img?.complete) return;
      
      const ctx = this.animationCanvas.getContext('2d');
      const width = this.animationCanvas.width;
      const height = this.animationCanvas.height;
      
      // Get or create offscreen canvas
      if (!this.offscreenCanvas) {
        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCanvas.width = width;
        this.offscreenCanvas.height = height;
      }
      const offCtx = this.offscreenCanvas.getContext('2d');
      offCtx.clearRect(0, 0, width, height);
      
      // Temporarily set animationFrameIndex for renderMorphedFrame
      const savedIndex = this.animationFrameIndex;
      this.animationFrameIndex = frameIndex;
      
      this.renderMorphedFrame(offCtx, frame1.img, frame2.img, t, width, height);
      
      this.animationFrameIndex = savedIndex;
      
      // Copy to visible canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(this.offscreenCanvas, 0, 0);
      
      // Draw debug overlay text
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillRect(10, 10, 200, 60);
      ctx.fillStyle = 'white';
      ctx.font = '12px monospace';
      ctx.fillText(`Frame: ${frameIndex} → ${nextIndex}`, 20, 28);
      ctx.fillText(`t = ${t.toFixed(2)}`, 20, 44);
      ctx.fillText(`Showing: img1 warped (t=${t.toFixed(2)})`, 20, 60);
    },
    
    /**
     * Show the frame pair boundary side by side:
     * Left half: current pair at t=1 (img1 fully warped)
     * Right half: next pair at t=0 (next img1 at original position)
     */
    showFramePairBoundary() {
      if (!this.animationCanvas || this.canvasFrameCache.length < 2) return;
      
      const frameIndex = this.morphDebugFrameIndex;
      const nextPairIndex = (frameIndex + 1) % this.canvasFrameCache.length;
      
      const ctx = this.animationCanvas.getContext('2d');
      const width = this.animationCanvas.width;
      const height = this.animationCanvas.height;
      const halfWidth = Math.floor(width / 2);
      
      // Left side: current pair at t=1
      const frame1 = this.canvasFrameCache[frameIndex];
      const frame2 = this.canvasFrameCache[(frameIndex + 1) % this.canvasFrameCache.length];
      
      // Right side: next pair at t=0 (which is just frame2 unwarped)
      const nextFrame1 = this.canvasFrameCache[nextPairIndex];
      
      if (!frame1?.img?.complete || !frame2?.img?.complete || !nextFrame1?.img?.complete) return;
      
      // Render left half: current pair at t=1
      if (!this.offscreenCanvas) {
        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCanvas.width = width;
        this.offscreenCanvas.height = height;
      }
      const offCtx = this.offscreenCanvas.getContext('2d');
      offCtx.clearRect(0, 0, width, height);
      
      const savedIndex = this.animationFrameIndex;
      this.animationFrameIndex = frameIndex;
      this.renderMorphedFrame(offCtx, frame1.img, frame2.img, 1.0, width, height);
      this.animationFrameIndex = savedIndex;
      
      // Clear main canvas and draw comparison
      ctx.clearRect(0, 0, width, height);
      
      // Left half: t=1 of current pair
      ctx.drawImage(this.offscreenCanvas, 0, 0, halfWidth, height, 0, 0, halfWidth, height);
      
      // Right half: t=0 of next pair (just the next frame1 unwarped)
      ctx.drawImage(nextFrame1.img, 0, 0, halfWidth, height, halfWidth, 0, halfWidth, height);
      
      // Draw divider line
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(halfWidth, 0);
      ctx.lineTo(halfWidth, height);
      ctx.stroke();
      
      // Labels
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillRect(10, 10, 150, 40);
      ctx.fillRect(halfWidth + 10, 10, 150, 40);
      ctx.fillStyle = 'white';
      ctx.font = '12px monospace';
      ctx.fillText(`Pair ${frameIndex} @ t=1`, 20, 28);
      ctx.fillText(`(img1 warped full)`, 20, 44);
      ctx.fillText(`Pair ${nextPairIndex} @ t=0`, halfWidth + 20, 28);
      ctx.fillText(`(img1 original)`, halfWidth + 20, 44);
    },
    
    // ========================================
    // Canvas-based Animation Methods
    // ========================================
    
    /**
     * Preload frames progressively using interleaved loading order.
     * Loads: first, last, middle, then binary subdivisions.
     * Animation starts after 5 frames to avoid oscillation artifacts.
     */
    async preloadCanvasFramesProgressive() {
      if (!this.map || !this.mapReady) return;
      if (!this.hasValidApiKey) {
        console.warn('Cannot preload animation frames: No API key available');
        return;
      }
      
      this.isPreloadingFrames = true;
      this.preloadProgress = 0;
      this.canvasFrameCache = [];
      this.previewSpeedMultiplier = 1;
      this.progressiveMaxFrame = 0; // Track highest loaded frame for progressive playback
      this.previewOscillating = false; // Not using oscillation anymore
      
      // Generate time keys for animation
      this.animationTimeKeys = this.generateAnimationTimeKeys();
      
      if (this.animationTimeKeys.length === 0) {
        this.isPreloadingFrames = false;
        return;
      }
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      if (visibleLayers.length === 0) {
        this.isPreloadingFrames = false;
        return;
      }
      
      const totalFrames = this.animationTimeKeys.length;
      
      // Get map container dimensions
      const mapContainer = this.map.getContainer();
      const width = mapContainer.offsetWidth;
      const height = mapContainer.offsetHeight;
      
      // Create temporary canvas for rasterization
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const ctx = tempCanvas.getContext('2d');
      
      // Helper function to load a single frame
      const loadFrame = async (frameIndex) => {
        if (!this.isPreloadingFrames) return null;
        
        const timeKey = this.animationTimeKeys[frameIndex];
        const frameTime = new Date(timeKey);
        
        const tempLayers = [];
        const loadPromises = [];
        const cacheBuster = `cors_${Date.now()}_${frameIndex}`;
        
        // Note: Keep existing layers visible during loading
        // They will be hidden when animation starts (showCanvasOverlay)
        // Temp layers are created with opacity 0 so they don't interfere
        
        for (const layer of visibleLayers) {
          // Vector alert layers are not rasterizable tile layers — skip
          if (layer.isVectorAlert) continue;
          const tileUrl = this.buildTileUrl(layer.element, layer.model, frameTime, cacheBuster);
          
          // Create temp layer with opacity 0 - invisible during loading
          const tempLayer = L.tileLayer(tileUrl, {
            opacity: 0,
            pane: 'weatherPane',
            maxZoom: 18,
            attribution: '',
            crossOrigin: 'anonymous'
          }).addTo(this.map);
          
          tempLayers.push({ layer: tempLayer, opacity: layer.opacity });
          
          const loadPromise = new Promise((resolve) => {
            let resolved = false;
            const onLoad = () => {
              if (!resolved) { resolved = true; resolve(); }
            };
            tempLayer.on('load', onLoad);
            setTimeout(onLoad, 3000);
          });
          
          loadPromises.push(loadPromise);
        }
        
        await Promise.all(loadPromises);
        
        if (!this.isPreloadingFrames) {
          tempLayers.forEach(({ layer }) => this.safeRemoveLayer(layer));
          return null;
        }
        
        await new Promise(resolve => setTimeout(resolve, 100));
        
        let frameData = null;
        try {
          await this.rasterizeWeatherPane(ctx, width, height, tempLayers);
          const imageData = tempCanvas.toDataURL('image/png');
          const img = new Image();
          img.src = imageData;
          frameData = { timeKey, imageData, img, frameIndex };
        } catch (err) {
          console.error('Failed to rasterize frame:', timeKey, err.message);
        }
        
        tempLayers.forEach(({ layer }) => this.safeRemoveLayer(layer));
        
        return frameData;
      };
      
      // Generate loading order: evenly-spaced keyframes first, then fill in gaps
      // For 20 frames with 5 keyframes: [0, 5, 10, 15, 19], then [2, 7, 12, 17], then remaining
      const generateKeyframeOrder = (total, numKeyframes = 5) => {
        if (total <= 0) return [];
        if (total <= numKeyframes) {
          // Few frames - just load sequentially
          return Array.from({ length: total }, (_, i) => i);
        }
        
        const order = [];
        const added = new Set();
        
        // Phase 1: Load evenly-spaced keyframes
        const keyframeStep = (total - 1) / (numKeyframes - 1);
        for (let i = 0; i < numKeyframes; i++) {
          const idx = Math.round(i * keyframeStep);
          if (!added.has(idx)) {
            order.push(idx);
            added.add(idx);
          }
        }
        
        // Phase 2: Fill gaps with midpoints (binary subdivision)
        const queue = [];
        for (let i = 0; i < order.length - 1; i++) {
          queue.push([order[i], order[i + 1]]);
        }
        
        while (queue.length > 0 && order.length < total) {
          const [start, end] = queue.shift();
          if (end - start <= 1) continue;
          
          const mid = Math.floor((start + end) / 2);
          if (!added.has(mid)) {
            order.push(mid);
            added.add(mid);
          }
          
          if (mid - start > 1) queue.push([start, mid]);
          if (end - mid > 1) queue.push([mid, end]);
        }
        
        // Fill any remaining
        for (let i = 0; i < total; i++) {
          if (!added.has(i)) {
            order.push(i);
            added.add(i);
          }
        }
        
        return order;
      };
      
      // Initialize frame cache with null placeholders
      this.canvasFrameCache = new Array(totalFrames).fill(null);
      this.progressiveTotalFrames = totalFrames;
      this.progressiveLoadedCount = 0;
      
      // Calculate number of keyframes (5 for most animations, fewer for short ones)
      const numKeyframes = Math.min(5, totalFrames);
      const loadOrder = generateKeyframeOrder(totalFrames, numKeyframes);
      
      // Load frames in keyframe-first order
      for (const frameIndex of loadOrder) {
        if (!this.isPreloadingFrames) return;
        
        const frame = await loadFrame(frameIndex);
        if (!frame || !this.isPreloadingFrames) return;
        
        this.canvasFrameCache[frameIndex] = frame;
        this.progressiveLoadedCount++;
        this.preloadProgress = Math.round((this.progressiveLoadedCount / totalFrames) * 100);
        
        // Start animation once we have the initial keyframes
        if (!this.isPlaying && this.progressiveLoadedCount >= numKeyframes) {
          this.isPlaying = true;
          this.animationFrameIndex = 0;
          this.crossfadeProgress = 0;
          this.progressiveMaxFrame = totalFrames - 1; // Can play full range with interpolation
          this.showCanvasOverlay();
          this.showCanvasFrame(0, 0, true);
          this.startCrossfadeLoop();
        }
      }
      
      if (!this.isPreloadingFrames) return;
      
      this.preloadProgress = 100;
      this.isPreloadingFrames = false;
      this.previewSpeedMultiplier = 1;
      this.progressiveLoadedCount = totalFrames;
      
      // All frames loaded - now compute motion fields in background
      // This enables smooth morphing after the initial loading phase
      if (this.smoothAnimationMode === 'morph' || this.smoothAnimationMode === 'motion') {
        // Use setTimeout to let UI update first, then compute
        setTimeout(() => {
          if (this.isPlaying) {
            this.precomputeAllMotionFields();
          }
        }, 500);
      }
    },
    
    /**
     * Preload frames by rasterizing tile layers to canvas images.
     * More memory-efficient than keeping multiple L.tileLayer objects.
     */
    async preloadCanvasFrames() {
      // console.log('preloadCanvasFrames: starting');
      if (!this.map || !this.mapReady) {
        // console.log('preloadCanvasFrames: map not ready');
        return;
      }
      
      // Don't attempt to load tiles without a valid API key
      if (!this.hasValidApiKey) {
        console.warn('Cannot preload animation frames: No API key available');
        return;
      }
      
      this.isPreloadingFrames = true;
      this.preloadProgress = 0;
      this.canvasFrameCache = [];
      
      // Generate time keys for animation
      this.animationTimeKeys = this.generateAnimationTimeKeys();
      
      if (this.animationTimeKeys.length === 0) {
        this.isPreloadingFrames = false;
        return;
      }
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      if (visibleLayers.length === 0) {
        this.isPreloadingFrames = false;
        return;
      }
      
      const totalFrames = this.animationTimeKeys.length;
      
      // Get map container dimensions
      const mapContainer = this.map.getContainer();
      const width = mapContainer.offsetWidth;
      const height = mapContainer.offsetHeight;
      
      // Create temporary canvas for rasterization
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const ctx = tempCanvas.getContext('2d');
      
      // For each time key, create temporary tile layers, wait for load, rasterize, then remove
      for (let i = 0; i < this.animationTimeKeys.length; i++) {
        if (!this.isPreloadingFrames) return; // Check for cancellation
        
        const timeKey = this.animationTimeKeys[i];
        const frameTime = new Date(timeKey);
        
        // Create temporary tile layers for this frame
        const tempLayers = [];
        const loadPromises = [];
        
        // Use a cache-buster to force fresh CORS requests (avoids tainted canvas from cached non-CORS tiles)
        const cacheBuster = `cors_${Date.now()}_${i}`;
        
        // Hide existing layers to prevent their tiles from tainting the canvas
        const hiddenLayers = [];
        for (const layer of this.activeLayers) {
          if (layer.leafletLayer && layer.visible) {
            if (layer.isVectorAlert) {
              if (layer.leafletLayer.setAlertOpacity) layer.leafletLayer.setAlertOpacity(0);
            } else {
              layer.leafletLayer.setOpacity(0);
            }
            hiddenLayers.push(layer);
          }
        }
        
        for (const layer of visibleLayers) {
          // Vector alert layers are not rasterizable tile layers — skip
          if (layer.isVectorAlert) continue;
          const tileUrl = this.buildTileUrl(layer.element, layer.model, frameTime, cacheBuster);
          
          // Create temp layer with opacity 0 - invisible during loading
          const tempLayer = L.tileLayer(tileUrl, {
            opacity: 0,
            pane: 'weatherPane',
            maxZoom: 18,
            attribution: '',
            crossOrigin: 'anonymous' // Required for canvas rasterization
          }).addTo(this.map);
          
          tempLayers.push({ layer: tempLayer, opacity: layer.opacity });
          
          // Wait for tiles to load
          const loadPromise = new Promise((resolve) => {
            let resolved = false;
            const onLoad = () => {
              if (!resolved) {
                resolved = true;
                resolve();
              }
            };
            tempLayer.on('load', onLoad);
            setTimeout(onLoad, 3000); // Timeout after 3s
          });
          
          loadPromises.push(loadPromise);
        }
        
        // Wait for all layers of this frame to load
        await Promise.all(loadPromises);
        
        if (!this.isPreloadingFrames) {
          // Cancelled - clean up temp layers
          tempLayers.forEach(({ layer }) => this.safeRemoveLayer(layer));
          return;
        }
        
        // Small delay to ensure tiles are rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Rasterize the weather pane to canvas
        try {
          // console.log('preloadCanvasFrames: rasterizing frame', i, 'of', this.animationTimeKeys.length);
          await this.rasterizeWeatherPane(ctx, width, height, tempLayers);
          
          // Store the frame as a data URL and preload the Image object
          // console.log('preloadCanvasFrames: calling toDataURL');
          const imageData = tempCanvas.toDataURL('image/png');
          // console.log('preloadCanvasFrames: got imageData, length:', imageData.length);
          
          // Preload the image for faster playback
          const img = new Image();
          img.src = imageData;
          this.canvasFrameCache.push({ timeKey, imageData, img });
        } catch (err) {
          console.error('Failed to rasterize frame:', timeKey, err.message, err);
        }
        
        // Remove temporary layers
        tempLayers.forEach(({ layer }) => this.safeRemoveLayer(layer));
        
        // Restore hidden layers
        for (const layer of hiddenLayers) {
          if (layer.leafletLayer) {
            if (layer.isVectorAlert) {
              if (layer.leafletLayer.setAlertOpacity) layer.leafletLayer.setAlertOpacity(layer.opacity);
            } else {
              layer.leafletLayer.setOpacity(layer.opacity);
            }
          }
        }
        
        // Update progress
        this.preloadProgress = Math.round(((i + 1) / totalFrames) * 100);
      }
      
      if (!this.isPreloadingFrames) return;
      
      this.preloadProgress = 100;
      this.isPreloadingFrames = false;
    },
    
    /**
     * Rasterize the weather tile layers to a canvas.
     * Uses the actual tile images from the DOM.
     * Only draws tiles with crossOrigin='anonymous' to avoid tainting the canvas.
     */
    async rasterizeWeatherPane(ctx, width, height, tempLayers) {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
      
      // Get the weather pane element
      const weatherPane = this.map.getPane('weatherPane');
      if (!weatherPane) return;
      
      // Get the map container's bounding rect as our reference point
      const mapContainer = this.map.getContainer();
      const containerRect = mapContainer.getBoundingClientRect();
      
      // Build a map of layer opacities from tempLayers
      // tempLayers contains { layer, opacity } objects
      const layerOpacities = new Map();
      for (const { layer, opacity } of tempLayers) {
        const container = layer.getContainer();
        if (container) {
          layerOpacities.set(container, opacity);
        }
      }
      
      // Get all tile containers within the weather pane
      const tileContainers = weatherPane.querySelectorAll('.leaflet-tile-container');
      
      let drawnCount = 0;
      let skippedCount = 0;
      
      for (const container of tileContainers) {
        // Get the layer container (parent of tile container)
        const layerContainer = container.closest('.leaflet-layer');
        
        // Skip layers not in our temp layers list
        if (!layerContainer || !layerOpacities.has(layerContainer)) {
          continue;
        }
        
        const layerOpacity = layerOpacities.get(layerContainer);
        
        const tiles = container.querySelectorAll('img.leaflet-tile');
        
        for (const tile of tiles) {
          if (!tile.complete || tile.naturalWidth === 0) continue;
          
          // CRITICAL: Only draw tiles with crossOrigin set to avoid tainting the canvas
          // Tiles from original layers (without CORS) will be skipped
          if (tile.crossOrigin !== 'anonymous') {
            skippedCount++;
            continue;
          }
          
          // Get tile position using getBoundingClientRect for accurate screen position
          // This accounts for all transforms in the hierarchy (pane, container, tile)
          const tileRect = tile.getBoundingClientRect();
          
          // Calculate position relative to the map container
          const x = tileRect.left - containerRect.left;
          const y = tileRect.top - containerRect.top;
          
          // Use the layer opacity from our tempLayers, not from DOM
          // (Leaflet applies opacity to container, not individual tiles)
          ctx.globalAlpha = layerOpacity;
          try {
            ctx.drawImage(tile, x, y);
            drawnCount++;
          } catch (err) {
            console.error('rasterizeWeatherPane: drawImage failed:', err.message);
          }
        }
      }
      
      // console.log('rasterizeWeatherPane: drew', drawnCount, 'tiles, skipped', skippedCount, 'non-CORS tiles');
      ctx.globalAlpha = 1;
    },
    
    /**
     * Create and show the animation canvas overlay.
     */
    showCanvasOverlay() {
      if (this.animationCanvas) return;
      
      const mapContainer = this.map.getContainer();
      const canvas = document.createElement('canvas');
      canvas.id = 'animation-canvas-overlay';
      canvas.width = mapContainer.offsetWidth;
      canvas.height = mapContainer.offsetHeight;
      canvas.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        z-index: 450;
        pointer-events: none;
      `;
      
      mapContainer.appendChild(canvas);
      this.animationCanvas = canvas;
      
      // Hide the main weather layers during canvas animation
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      for (const layer of visibleLayers) {
        if (layer.leafletLayer) {
          if (layer.isVectorAlert) {
            if (layer.leafletLayer.setAlertOpacity) layer.leafletLayer.setAlertOpacity(0);
          } else {
            layer.leafletLayer.setOpacity(0);
          }
        }
      }
    },
    
    /**
     * Hide and remove the animation canvas overlay.
     */
    hideCanvasOverlay() {
      // Cancel any pending async motion field computation
      if (this._motionFieldComputeId) {
        clearTimeout(this._motionFieldComputeId);
        this._motionFieldComputeId = null;
      }
      if (this.animationCanvas) {
        this.animationCanvas.remove();
        this.animationCanvas = null;
      }
      // Clean up cached context reference
      this._animationCtx = null;
      // Clean up offscreen canvas
      this.offscreenCanvas = null;
      // Clean up WebGL resources
      this.clearWebGLTextures();
      if (this.webglCanvas) {
        this.webglCanvas = null;
        this.webglContext = null;
        this.webglProgram = null;
        this.webglUniforms = null;
        this.webglInitialized = false;
      }
    },
    
    /**
     * Show a crossfade preview between two specific frames (for loading preview).
     * Used during progressive loading to show first→last frame oscillation.
     */
    showPreviewCrossfade(frame1, frame2, blendFactor) {
      if (!this.animationCanvas) return;
      
      const ctx = this.animationCanvas.getContext('2d');
      const width = this.animationCanvas.width;
      const height = this.animationCanvas.height;
      
      // Get or create offscreen canvas for double-buffering, resize if needed
      if (!this.offscreenCanvas) {
        this.offscreenCanvas = document.createElement('canvas');
      }
      if (this.offscreenCanvas.width !== width || this.offscreenCanvas.height !== height) {
        this.offscreenCanvas.width = width;
        this.offscreenCanvas.height = height;
      }
      const offCtx = this.offscreenCanvas.getContext('2d');
      offCtx.clearRect(0, 0, width, height);
      
      const img1 = frame1.img;
      const img2 = frame2.img;
      
      if (img1 && img1.complete) {
        offCtx.globalAlpha = 1 - blendFactor;
        offCtx.drawImage(img1, 0, 0);
      }
      if (img2 && img2.complete) {
        offCtx.globalAlpha = blendFactor;
        offCtx.drawImage(img2, 0, 0);
      }
      offCtx.globalAlpha = 1;
      
      // Copy to visible canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(this.offscreenCanvas, 0, 0);
      
      // Interpolate time display
      const time1Ms = new Date(frame1.timeKey).getTime();
      const time2Ms = new Date(frame2.timeKey).getTime();
      const interpolatedMs = time1Ms + (time2Ms - time1Ms) * blendFactor;
      this.currentTime = new Date(interpolatedMs);
    },
    
    /**
     * Display a specific canvas frame by index, with interpolation based on mode.
     * @param {number} frameIndex - Current frame index
     * @param {number} blendFactor - 0-1, progress toward next frame
     * @param {boolean} forceSimpleFade - If true, use simple crossfade regardless of mode (for loop transitions)
     */
    showCanvasFrame(frameIndex, blendFactor = 0, forceSimpleFade = false) {
      if (!this.animationCanvas || frameIndex < 0 || frameIndex >= this.canvasFrameCache.length) return;
      
      const totalFrames = this.canvasFrameCache.length;
      
      // Find the actual frame to show - if requested frame not loaded, find nearest
      let actualFrame = this.canvasFrameCache[frameIndex];
      let actualIndex = frameIndex;
      let frameDistance = 0; // How far from requested frame (for blur calculation)
      
      if (!actualFrame) {
        // Frame not loaded - find nearest loaded frame
        let searchDist = 1;
        const maxSearch = Math.ceil(totalFrames / 2);
        while (searchDist <= maxSearch) {
          // Check before
          const beforeIdx = (frameIndex - searchDist + totalFrames) % totalFrames;
          if (this.canvasFrameCache[beforeIdx]) {
            actualFrame = this.canvasFrameCache[beforeIdx];
            actualIndex = beforeIdx;
            frameDistance = searchDist;
            break;
          }
          // Check after
          const afterIdx = (frameIndex + searchDist) % totalFrames;
          if (this.canvasFrameCache[afterIdx]) {
            actualFrame = this.canvasFrameCache[afterIdx];
            actualIndex = afterIdx;
            frameDistance = searchDist;
            break;
          }
          searchDist++;
        }
      }
      
      if (!actualFrame) return; // No frames loaded at all
      
      // Cache the 2D context to avoid repeated getContext calls
      if (!this._animationCtx || this._animationCtx.canvas !== this.animationCanvas) {
        this._animationCtx = this.animationCanvas.getContext('2d');
      }
      const ctx = this._animationCtx;
      const width = this.animationCanvas.width;
      const height = this.animationCanvas.height;
      
      // Use preloaded image if available
      const currentImg = actualFrame.img && actualFrame.img.complete ? actualFrame.img : null;
      
      if (!currentImg) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = actualFrame.imageData;
        this.currentTime = new Date(actualFrame.timeKey);
        return;
      }
      
      // Get or create offscreen canvas for double-buffering, resize if needed
      if (!this.offscreenCanvas) {
        this.offscreenCanvas = document.createElement('canvas');
      }
      if (this.offscreenCanvas.width !== width || this.offscreenCanvas.height !== height) {
        this.offscreenCanvas.width = width;
        this.offscreenCanvas.height = height;
      }
      const offCtx = this.offscreenCanvas.getContext('2d');
      offCtx.clearRect(0, 0, width, height);
      
      // Find next loaded frame for blending
      let nextIndex = (actualIndex + 1) % totalFrames;
      let nextFrame = this.canvasFrameCache[nextIndex];
      
      // If next frame not loaded, search forward
      if (!nextFrame && blendFactor > 0) {
        for (let i = 1; i < totalFrames; i++) {
          const searchIdx = (actualIndex + i) % totalFrames;
          if (this.canvasFrameCache[searchIdx]) {
            nextFrame = this.canvasFrameCache[searchIdx];
            nextIndex = searchIdx;
            break;
          }
        }
      }
      
      const nextImg = nextFrame?.img?.complete ? nextFrame.img : null;
      
      // Determine effective animation mode (force simple fade for loop transitions)
      const effectiveMode = forceSimpleFade ? 'crossfade' : this.smoothAnimationMode;
      
      if (effectiveMode === 'off' || blendFactor <= 0 || !nextImg) {
        // No interpolation - just draw current frame
        offCtx.drawImage(currentImg, 0, 0);
      } else if (effectiveMode === 'crossfade' || forceSimpleFade) {
        // Simple alpha crossfade - no motion, just blend
        offCtx.globalAlpha = 1 - blendFactor;
        offCtx.drawImage(currentImg, 0, 0);
        offCtx.globalAlpha = blendFactor;
        offCtx.drawImage(nextImg, 0, 0);
        offCtx.globalAlpha = 1;
      } else if (effectiveMode === 'morph' || effectiveMode === 'motion') {
        // WebGL-based morphing or motion interpolation
        this.renderMorphedFrame(offCtx, currentImg, nextImg, blendFactor, width, height);
      }
      
      // Calculate blur based on loading progress
      // Blur decreases as more frames load: 8px when only keyframes, 0px when fully loaded
      const loadProgress = this.progressiveTotalFrames > 0 
        ? this.progressiveLoadedCount / this.progressiveTotalFrames 
        : 1;
      const maxBlur = 8;
      const blurAmount = Math.max(0, maxBlur * (1 - loadProgress));
      
      // Apply blur via CSS filter (more efficient than canvas blur)
      if (blurAmount > 0.5) {
        this.animationCanvas.style.filter = `blur(${blurAmount.toFixed(1)}px)`;
      } else {
        this.animationCanvas.style.filter = '';
      }
      
      // Copy to visible canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(this.offscreenCanvas, 0, 0);
      
      // Update time display - use the original requested frame time if available
      // Otherwise use the actual (substitute) frame time
      const displayFrame = this.canvasFrameCache[frameIndex] || actualFrame;
      const currentTimeMs = displayFrame.timeMsCache || (displayFrame.timeMsCache = new Date(displayFrame.timeKey).getTime());
      
      // Only interpolate if nextFrame exists
      if (nextFrame && blendFactor > 0) {
        const nextTimeMs = nextFrame.timeMsCache || (nextFrame.timeMsCache = new Date(nextFrame.timeKey).getTime());
        const interpolatedTimeMs = currentTimeMs + (nextTimeMs - currentTimeMs) * blendFactor;
        this.currentTime = new Date(interpolatedTimeMs);
      } else {
        this.currentTime = new Date(currentTimeMs);
      }
    },
    
    /**
     * Show a frame during progressive loading, with smooth crossfade between two specific loaded frames.
     * This avoids the jumpiness of trying to show unloaded frames.
     */
    showProgressiveFrame(currentIdx, nextIdx, blendFactor) {
      if (!this.animationCanvas) return;
      
      const frame1 = this.canvasFrameCache?.[currentIdx];
      const frame2 = this.canvasFrameCache?.[nextIdx];
      
      if (!frame1) return;
      
      // Cache the 2D context to avoid repeated getContext calls
      if (!this._animationCtx || this._animationCtx.canvas !== this.animationCanvas) {
        this._animationCtx = this.animationCanvas.getContext('2d');
      }
      const ctx = this._animationCtx;
      const width = this.animationCanvas.width;
      const height = this.animationCanvas.height;
      
      const img1 = frame1.img && frame1.img.complete ? frame1.img : null;
      const img2 = frame2?.img?.complete ? frame2.img : null;
      
      if (!img1) return;
      
      // Get or create offscreen canvas, resize if needed
      if (!this.offscreenCanvas) {
        this.offscreenCanvas = document.createElement('canvas');
      }
      if (this.offscreenCanvas.width !== width || this.offscreenCanvas.height !== height) {
        this.offscreenCanvas.width = width;
        this.offscreenCanvas.height = height;
      }
      const offCtx = this.offscreenCanvas.getContext('2d');
      offCtx.clearRect(0, 0, width, height);
      
      if (!img2 || blendFactor <= 0) {
        // No blending needed
        offCtx.drawImage(img1, 0, 0);
      } else {
        // Check if we have a motion field for this pair (enables morphing)
        const cacheKey = `field_${currentIdx}_${nextIdx}`;
        const field = this.opticalFlowCache[cacheKey];
        
        if (field && (this.smoothAnimationMode === 'morph' || this.smoothAnimationMode === 'motion')) {
          // Use WebGL morphing for smooth interpolation
          this.renderMorphedFrameWithField(offCtx, img1, img2, blendFactor, width, height, field, cacheKey);
        } else {
          // Simple crossfade fallback
          offCtx.globalAlpha = 1 - blendFactor;
          offCtx.drawImage(img1, 0, 0);
          offCtx.globalAlpha = blendFactor;
          offCtx.drawImage(img2, 0, 0);
          offCtx.globalAlpha = 1;
        }
      }
      
      // Calculate blur based on loading progress
      const loadProgress = this.progressiveTotalFrames > 0 
        ? this.progressiveLoadedCount / this.progressiveTotalFrames 
        : 1;
      const maxBlur = 6;
      const blurAmount = Math.max(0, maxBlur * (1 - loadProgress));
      
      if (blurAmount > 0.3) {
        this.animationCanvas.style.filter = `blur(${blurAmount.toFixed(1)}px)`;
      } else {
        this.animationCanvas.style.filter = '';
      }
      
      // Copy to visible canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(this.offscreenCanvas, 0, 0);
      
      // Interpolate time between the two frames
      const time1Ms = frame1.timeMsCache || (frame1.timeMsCache = new Date(frame1.timeKey).getTime());
      if (frame2 && blendFactor > 0) {
        const time2Ms = frame2.timeMsCache || (frame2.timeMsCache = new Date(frame2.timeKey).getTime());
        this.currentTime = new Date(time1Ms + (time2Ms - time1Ms) * blendFactor);
      } else {
        this.currentTime = new Date(time1Ms);
      }
    },
    
    /**
     * Render a morphed frame using a pre-computed motion field.
     * Similar to renderMorphedFrame but uses a specific field and cache key.
     */
    renderMorphedFrameWithField(ctx, img1, img2, t, width, height, field, cacheKey) {
      // Initialize WebGL if needed
      if (!this.webglInitialized) {
        this.initWebGL(width, height);
      }
      
      if (!this.webglContext || !field) {
        // Fallback to crossfade
        ctx.clearRect(0, 0, width, height);
        ctx.globalAlpha = 1 - t;
        ctx.drawImage(img1, 0, 0);
        ctx.globalAlpha = t;
        ctx.drawImage(img2, 0, 0);
        ctx.globalAlpha = 1;
        return;
      }
      
      const gl = this.webglContext;
      const uniforms = this.webglUniforms;
      
      // Resize if needed
      if (this.webglCanvas.width !== width || this.webglCanvas.height !== height) {
        this.webglCanvas.width = width;
        this.webglCanvas.height = height;
        gl.viewport(0, 0, width, height);
      }
      
      // Clear to fully transparent before rendering
      gl.clearColor(0.0, 0.0, 0.0, 0.0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      
      // Use the shader program
      gl.useProgram(this.webglProgram);
      
      // Create/cache textures
      const tex1Key = `progressive_tex1_${cacheKey}`;
      const tex2Key = `progressive_tex2_${cacheKey}`;
      const motionKey = `progressive_motion_${cacheKey}`;
      
      // Texture 1
      let tex1 = this.webglTextures[tex1Key];
      if (!tex1) {
        tex1 = this.createTexture(gl, img1, 0);
        this.webglTextures[tex1Key] = tex1;
      } else {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tex1);
      }
      
      // Texture 2
      let tex2 = this.webglTextures[tex2Key];
      if (!tex2) {
        tex2 = this.createTexture(gl, img2, 1);
        this.webglTextures[tex2Key] = tex2;
      } else {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, tex2);
      }
      
      // Motion texture
      let motionTex = this.webglTextures[motionKey];
      if (!motionTex) {
        motionTex = this.createMotionTexture(gl, field);
        this.webglTextures[motionKey] = motionTex;
      } else {
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, motionTex);
      }
      
      // Set uniforms using correct cached locations
      gl.uniform1i(uniforms.img1, 0);
      gl.uniform1i(uniforms.img2, 1);
      gl.uniform1i(uniforms.motion, 2);
      gl.uniform1f(uniforms.t, t);
      gl.uniform1f(uniforms.mode, 0.0); // morph mode
      gl.uniform2f(uniforms.resolution, width, height);
      
      // Draw using 6 vertices (two triangles)
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      
      // Copy to 2D canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(this.webglCanvas, 0, 0);
    },
    
    /**
     * Render a morphed frame using WebGL for smooth per-pixel interpolation.
     * Supports both 'morph' (face-to-face) and 'motion' (pure extrapolation) modes.
     */
    renderMorphedFrame(ctx, img1, img2, t, width, height) {
      // Initialize WebGL if needed
      if (!this.webglInitialized) {
        this.initWebGL(width, height);
      }
      
      // Get pre-computed motion field for this frame pair
      const cacheKey = `field_${this.animationFrameIndex}`;
      let field = this.opticalFlowCache[cacheKey];
      
      if (!field || !this.webglContext) {
        // No motion field or no WebGL - simple crossfade fallback
        ctx.clearRect(0, 0, width, height);
        ctx.globalAlpha = 1 - t;
        ctx.drawImage(img1, 0, 0);
        ctx.globalAlpha = t;
        ctx.drawImage(img2, 0, 0);
        ctx.globalAlpha = 1;
        return;
      }
      
      // Use WebGL for smooth per-pixel morphing
      try {
        this.renderWebGLMorph(ctx, img1, img2, t, width, height, field);
      } catch (e) {
        console.warn('WebGL morph failed, falling back to crossfade:', e);
        ctx.clearRect(0, 0, width, height);
        ctx.globalAlpha = 1 - t;
        ctx.drawImage(img1, 0, 0);
        ctx.globalAlpha = t;
        ctx.drawImage(img2, 0, 0);
        ctx.globalAlpha = 1;
      }
    },
    
    /**
     * Initialize WebGL context and shaders for morphing
     */
    initWebGL(width, height) {
      try {
        // Create offscreen WebGL canvas
        this.webglCanvas = document.createElement('canvas');
        this.webglCanvas.width = width;
        this.webglCanvas.height = height;
        
        const gl = this.webglCanvas.getContext('webgl', { 
          premultipliedAlpha: false,
          preserveDrawingBuffer: true 
        });
        
        if (!gl) {
          console.warn('WebGL not available');
          this.webglInitialized = true; // Mark as initialized to avoid retrying
          return;
        }
        
        this.webglContext = gl;
        
        // Vertex shader - simple fullscreen quad
        const vsSource = `
          attribute vec2 a_position;
          attribute vec2 a_texCoord;
          varying vec2 v_texCoord;
          void main() {
            gl_Position = vec4(a_position, 0.0, 1.0);
            v_texCoord = a_texCoord;
          }
        `;
        
        // Fragment shader - optical flow morphing with smooth alpha transitions
        const fsSource = `
          precision mediump float;
          uniform sampler2D u_img1;
          uniform sampler2D u_img2;
          uniform sampler2D u_motion;
          uniform float u_t;
          uniform float u_mode; // 0 = morph, 1 = motion
          uniform vec2 u_resolution;
          varying vec2 v_texCoord;
          
          void main() {
            // Sample original pixels at current position
            vec4 orig1 = texture2D(u_img1, v_texCoord);
            vec4 orig2 = texture2D(u_img2, v_texCoord);
            
            // Sample motion vector
            vec4 motion = texture2D(u_motion, v_texCoord);
            float mvx = (motion.r - 0.5) * 100.0;
            float mvy = (motion.g - 0.5) * 100.0;
            vec2 uvOffset = vec2(mvx, mvy) / u_resolution;
            
            // Use LINEAR time - no easing, constant velocity throughout
            float t = u_t;
            
            // Sample warped positions with linear motion
            vec2 uv1 = clamp(v_texCoord - uvOffset * t, vec2(0.0), vec2(1.0));
            vec2 uv2 = clamp(v_texCoord + uvOffset * (1.0 - t), vec2(0.0), vec2(1.0));
            vec4 warped1 = texture2D(u_img1, uv1);
            vec4 warped2 = texture2D(u_img2, uv2);
            
            // Use continuous alpha-weighted blending - no hard thresholds
            float alpha1 = warped1.a;
            float alpha2 = warped2.a;
            
            // Linear time-based weights
            float w1 = (1.0 - t);
            float w2 = t;
            
            // Alpha-weighted contribution from each frame
            float weightedAlpha1 = alpha1 * w1;
            float weightedAlpha2 = alpha2 * w2;
            float totalWeight = weightedAlpha1 + weightedAlpha2 + 0.0001;
            
            // Blend colors weighted by both alpha and time
            vec3 blendedColor = (warped1.rgb * weightedAlpha1 + warped2.rgb * weightedAlpha2) / totalWeight;
            
            // Output alpha is the sum of weighted alphas
            float blendedAlpha = min(weightedAlpha1 + weightedAlpha2, 1.0);
            
            // For motion mode, bias toward frame1's warped position more strongly
            if (u_mode > 0.5) {
              float motionBias = smoothstep(0.8, 1.0, t);
              blendedColor = mix(warped1.rgb, blendedColor, motionBias);
              blendedAlpha = mix(warped1.a * w1 + warped2.a * w2 * 0.3, blendedAlpha, motionBias);
            }
            
            gl_FragColor = vec4(blendedColor, blendedAlpha);
          }
        `;
        
        // Compile shaders
        const vs = this.compileShader(gl, gl.VERTEX_SHADER, vsSource);
        const fs = this.compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
        
        if (!vs || !fs) {
          console.warn('Shader compilation failed');
          this.webglInitialized = true;
          return;
        }
        
        // Create program
        const program = gl.createProgram();
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);
        
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
          console.warn('Program link failed:', gl.getProgramInfoLog(program));
          this.webglInitialized = true;
          return;
        }
        
        this.webglProgram = program;
        
        // Create fullscreen quad
        const positions = new Float32Array([
          -1, -1,  1, -1,  -1, 1,
          -1, 1,   1, -1,   1, 1
        ]);
        // Standard texture coordinates (UNPACK_FLIP_Y handles image orientation)
        const texCoords = new Float32Array([
          0, 0,  1, 0,  0, 1,
          0, 1,  1, 0,  1, 1
        ]);
        
        const posBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
        
        const posLoc = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(posLoc);
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
        
        const texBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, texBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);
        
        const texLoc = gl.getAttribLocation(program, 'a_texCoord');
        gl.enableVertexAttribArray(texLoc);
        gl.vertexAttribPointer(texLoc, 2, gl.FLOAT, false, 0, 0);
        
        // Cache uniform locations for performance
        this.webglUniforms = {
          img1: gl.getUniformLocation(program, 'u_img1'),
          img2: gl.getUniformLocation(program, 'u_img2'),
          motion: gl.getUniformLocation(program, 'u_motion'),
          t: gl.getUniformLocation(program, 'u_t'),
          mode: gl.getUniformLocation(program, 'u_mode'),
          resolution: gl.getUniformLocation(program, 'u_resolution')
        };
        
        this.webglInitialized = true;
        console.log('WebGL morph initialized successfully');
        
      } catch (e) {
        console.warn('WebGL initialization failed:', e);
        this.webglInitialized = true; // Avoid retrying
      }
    },
    
    /**
     * Compile a WebGL shader
     */
    compileShader(gl, type, source) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.warn('Shader compile error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    },
    
    /**
     * Create or update a WebGL texture from an image
     */
    createTexture(gl, image, textureUnit) {
      const texture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0 + textureUnit);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      // Flip Y-axis to match WebGL coordinate system
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      // Don't premultiply alpha - prevents grey artifacts with transparent areas
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      return texture;
    },
    
    /**
     * Create motion field texture from optical flow data
     * Uses grid resolution (small) - GPU bilinear filtering handles upscaling
     * Y-axis is flipped to match WebGL coordinate system
     */
    createMotionTexture(gl, field) {
      // Use grid resolution - much smaller than full image!
      // GPU's LINEAR filtering will smoothly interpolate
      const gridW = field.gridW;
      const gridH = field.gridH;
      const data = new Uint8Array(gridW * gridH * 4);
      
      for (let gy = 0; gy < gridH; gy++) {
        for (let gx = 0; gx < gridW; gx++) {
          // Flip Y-axis to match image textures (which use UNPACK_FLIP_Y)
          const srcGy = gridH - 1 - gy;
          const idx = (srcGy * gridW + gx) * 2;
          const vx = field.vectors[idx];
          const vy = field.vectors[idx + 1];
          
          const pixelIdx = (gy * gridW + gx) * 4;
          // Encode motion: 0.5 = no motion, <0.5 = negative, >0.5 = positive
          // Negate Y to account for WebGL coordinate system
          data[pixelIdx] = Math.max(0, Math.min(255, Math.round((vx / 100 + 0.5) * 255)));
          data[pixelIdx + 1] = Math.max(0, Math.min(255, Math.round((-vy / 100 + 0.5) * 255)));
          data[pixelIdx + 2] = 0;
          data[pixelIdx + 3] = 255;
        }
      }
      
      const texture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, field.gridW, field.gridH, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      
      return texture;
    },
    
    /**
     * Render using WebGL morph shader
     */
    renderWebGLMorph(ctx, img1, img2, t, width, height, field) {
      const gl = this.webglContext;
      const program = this.webglProgram;
      const uniforms = this.webglUniforms;
      
      // Resize canvas if needed
      if (this.webglCanvas.width !== width || this.webglCanvas.height !== height) {
        this.webglCanvas.width = width;
        this.webglCanvas.height = height;
        gl.viewport(0, 0, width, height);
      }
      
      // Clear to fully transparent before rendering
      gl.clearColor(0.0, 0.0, 0.0, 0.0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      
      gl.useProgram(program);
      
      // Cache frame textures - only recreate when frame changes
      const frameKey1 = `frame_${this.animationFrameIndex}`;
      const frameKey2 = `frame_${(this.animationFrameIndex + 1) % this.canvasFrameCache.length}`;
      
      let tex1 = this.webglTextures[frameKey1];
      if (!tex1) {
        tex1 = this.createTexture(gl, img1, 0);
        this.webglTextures[frameKey1] = tex1;
      } else {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tex1);
      }
      
      let tex2 = this.webglTextures[frameKey2];
      if (!tex2) {
        tex2 = this.createTexture(gl, img2, 1);
        this.webglTextures[frameKey2] = tex2;
      } else {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, tex2);
      }
      
      // Get cached motion texture for this frame pair
      const motionCacheKey = `motionTex_${this.animationFrameIndex}`;
      let motionTex = this.webglTextures[motionCacheKey];
      if (!motionTex) {
        motionTex = this.createMotionTexture(gl, field);
        this.webglTextures[motionCacheKey] = motionTex;
      } else {
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, motionTex);
      }
      
      // Set uniforms using cached locations
      gl.uniform1i(uniforms.img1, 0);
      gl.uniform1i(uniforms.img2, 1);
      gl.uniform1i(uniforms.motion, 2);
      gl.uniform1f(uniforms.t, t);
      gl.uniform1f(uniforms.mode, this.smoothAnimationMode === 'motion' ? 1.0 : 0.0);
      gl.uniform2f(uniforms.resolution, width, height);
      
      // Draw
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      
      // Pre-cache next frame pair textures AND motion to avoid pause at boundary
      const nextFrameIdx = (this.animationFrameIndex + 1) % this.canvasFrameCache.length;
      const nextNextFrameIdx = (this.animationFrameIndex + 2) % this.canvasFrameCache.length;
      
      // Pre-cache next frame texture
      const nextFrameKey = `frame_${nextFrameIdx}`;
      if (!this.webglTextures[nextFrameKey]) {
        const nextFrame = this.canvasFrameCache[nextFrameIdx];
        if (nextFrame?.img?.complete) {
          this.webglTextures[nextFrameKey] = this.createTexture(gl, nextFrame.img, 0);
        }
      }
      
      // Pre-cache the frame after that
      const nextNextFrameKey = `frame_${nextNextFrameIdx}`;
      if (!this.webglTextures[nextNextFrameKey]) {
        const nextNextFrame = this.canvasFrameCache[nextNextFrameIdx];
        if (nextNextFrame?.img?.complete) {
          this.webglTextures[nextNextFrameKey] = this.createTexture(gl, nextNextFrame.img, 0);
        }
      }
      
      // Pre-cache next motion texture
      const nextMotionKey = `motionTex_${nextFrameIdx}`;
      if (!this.webglTextures[nextMotionKey]) {
        const nextField = this.opticalFlowCache[`field_${nextFrameIdx}`];
        if (nextField) {
          this.webglTextures[nextMotionKey] = this.createMotionTexture(gl, nextField);
        }
      }
      
      // Copy WebGL result to 2D canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(this.webglCanvas, 0, 0);
    },

    /**
     * Compute motion fields for a specific set of frame indices (for progressive loading).
     * Only computes fields between consecutive frames in the provided list.
     */
    computeMotionFieldsForSnapshot(frameIndices) {
      if (!frameIndices || frameIndices.length < 2) return;
      
      const firstFrame = this.canvasFrameCache[frameIndices[0]];
      if (!firstFrame?.img) return;
      
      const width = firstFrame.img.width || this.animationCanvas?.width || 800;
      const height = firstFrame.img.height || this.animationCanvas?.height || 600;
      
      // Compute motion field for each consecutive pair in the snapshot
      for (let i = 0; i < frameIndices.length; i++) {
        const idx1 = frameIndices[i];
        const idx2 = frameIndices[(i + 1) % frameIndices.length];
        
        const frame1 = this.canvasFrameCache[idx1];
        const frame2 = this.canvasFrameCache[idx2];
        
        if (!frame1?.img?.complete || !frame2?.img?.complete) continue;
        
        // Use a cache key that identifies this specific pair
        const cacheKey = `field_${idx1}_${idx2}`;
        
        // Skip if already computed
        if (this.opticalFlowCache[cacheKey]) continue;
        
        const field = this.computeFramePairMotion(frame1.img, frame2.img, width, height);
        if (field) {
          this.opticalFlowCache[cacheKey] = field;
        }
      }
    },
    
    /**
     * Compute motion fields asynchronously, one at a time with yields.
     * This prevents blocking the animation during progressive loading.
     */
    computeMotionFieldsAsync(frameIndices) {
      if (!frameIndices || frameIndices.length < 2) return;
      
      // Cancel any previous async computation
      if (this._motionFieldComputeId) {
        clearTimeout(this._motionFieldComputeId);
      }
      
      const firstFrame = this.canvasFrameCache[frameIndices[0]];
      if (!firstFrame?.img) return;
      
      const width = firstFrame.img.width || this.animationCanvas?.width || 800;
      const height = firstFrame.img.height || this.animationCanvas?.height || 600;
      
      let pairIndex = 0;
      
      const computeNext = () => {
        // Stop if animation stopped or we've finished
        if (!this.isPlaying || pairIndex >= frameIndices.length) {
          this._motionFieldComputeId = null;
          return;
        }
        
        const idx1 = frameIndices[pairIndex];
        const idx2 = frameIndices[(pairIndex + 1) % frameIndices.length];
        
        const frame1 = this.canvasFrameCache[idx1];
        const frame2 = this.canvasFrameCache[idx2];
        
        pairIndex++;
        
        if (!frame1?.img?.complete || !frame2?.img?.complete) {
          // Skip this pair, try next
          this._motionFieldComputeId = setTimeout(computeNext, 10);
          return;
        }
        
        const cacheKey = `field_${idx1}_${idx2}`;
        
        // Skip if already computed
        if (this.opticalFlowCache[cacheKey]) {
          this._motionFieldComputeId = setTimeout(computeNext, 10);
          return;
        }
        
        // Compute one field
        const field = this.computeFramePairMotion(frame1.img, frame2.img, width, height);
        if (field) {
          this.opticalFlowCache[cacheKey] = field;
        }
        
        // Yield to let animation render, then compute next
        this._motionFieldComputeId = setTimeout(computeNext, 50);
      };
      
      // Start computing after a short delay to let animation start
      this._motionFieldComputeId = setTimeout(computeNext, 100);
    },

    /**
     * Pre-compute all optical flow motion fields before animation starts.
     * This eliminates pauses during playback when transitioning between frames.
     */
    async precomputeAllMotionFields() {
      if (this.canvasFrameCache.length < 2) return;
      
      // Find first loaded frame to get dimensions
      const firstLoadedFrame = this.canvasFrameCache.find(f => f && f.img);
      if (!firstLoadedFrame) return;
      
      const width = firstLoadedFrame.img?.width || this.animationCanvas?.width || 800;
      const height = firstLoadedFrame.img?.height || this.animationCanvas?.height || 600;
      
      console.log('Pre-computing optical flow for', this.canvasFrameCache.length, 'frame pairs...');
      
      // Clear existing caches (including WebGL textures since frames may have changed)
      this.opticalFlowCache = {};
      this.clearWebGLTextures();
      
      // Compute motion field for each consecutive frame pair
      for (let i = 0; i < this.canvasFrameCache.length; i++) {
        const frame1 = this.canvasFrameCache[i];
        const frame2 = this.canvasFrameCache[(i + 1) % this.canvasFrameCache.length];
        
        // Skip if either frame not loaded yet
        if (!frame1 || !frame2) continue;
        
        const img1 = frame1.img;
        const img2 = frame2.img;
        
        if (img1?.complete && img2?.complete) {
          const cacheKey = `field_${i}`;
          const field = this.computeFramePairMotion(img1, img2, width, height);
          this.opticalFlowCache[cacheKey] = field;
          
          // Also pre-compute the tile cache for this frame
          if (field) {
            const tilesCacheKey = `tiles_${i}`;
            const drawTileSize = 48;
            const tilesX = Math.ceil(width / drawTileSize);
            const tilesY = Math.ceil(height / drawTileSize);
            const tilesCache = [];
            
            for (let ty = 0; ty < tilesY; ty++) {
              for (let tx = 0; tx < tilesX; tx++) {
                const srcX = tx * drawTileSize;
                const srcY = ty * drawTileSize;
                const srcW = Math.min(drawTileSize, width - srcX);
                const srcH = Math.min(drawTileSize, height - srcY);
                
                if (srcW <= 0 || srcH <= 0) continue;
                
                const cx = srcX + srcW / 2;
                const cy = srcY + srcH / 2;
                const gx = cx / field.gridSize;
                const gy = cy / field.gridSize;
                
                const gx0 = Math.max(0, Math.min(Math.floor(gx), field.gridW - 1));
                const gy0 = Math.max(0, Math.min(Math.floor(gy), field.gridH - 1));
                const gx1 = Math.min(gx0 + 1, field.gridW - 1);
                const gy1 = Math.min(gy0 + 1, field.gridH - 1);
                
                const fx = Math.max(0, Math.min(1, gx - gx0));
                const fy = Math.max(0, Math.min(1, gy - gy0));
                
                const idx00 = (gy0 * field.gridW + gx0) * 2;
                const idx01 = (gy0 * field.gridW + gx1) * 2;
                const idx10 = (gy1 * field.gridW + gx0) * 2;
                const idx11 = (gy1 * field.gridW + gx1) * 2;
                
                const vx = (1-fx)*(1-fy)*field.vectors[idx00] + fx*(1-fy)*field.vectors[idx01] +
                           (1-fx)*fy*field.vectors[idx10] + fx*fy*field.vectors[idx11];
                const vy = (1-fx)*(1-fy)*field.vectors[idx00+1] + fx*(1-fy)*field.vectors[idx01+1] +
                           (1-fx)*fy*field.vectors[idx10+1] + fx*fy*field.vectors[idx11+1];
                
                tilesCache.push({ srcX, srcY, srcW, srcH, vx, vy });
              }
            }
            
            this.opticalFlowCache[tilesCacheKey] = tilesCache;
          }
        }
        
        // Yield to UI every few frames to show progress
        if (i % 3 === 0) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }
      
      console.log('Optical flow pre-computation complete');
    },
    
    /**
     * Compute motion field between a specific pair of frames.
     * Motion vectors point from frame1 to frame2 (where content moved TO).
     */
    computeFramePairMotion(img1, img2, width, height) {
      const scale = 0.25;
      const sw = Math.floor(width * scale);
      const sh = Math.floor(height * scale);
      
      const gridSize = 48;
      const gridW = Math.ceil(width / gridSize);
      const gridH = Math.ceil(height / gridSize);
      const vectors = new Float32Array(gridW * gridH * 2);
      
      const scaledGridSize = Math.floor(gridSize * scale);
      
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = sw;
      tempCanvas.height = sh;
      const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
      
      tempCtx.drawImage(img1, 0, 0, sw, sh);
      const data1 = tempCtx.getImageData(0, 0, sw, sh).data;
      
      tempCtx.clearRect(0, 0, sw, sh);
      tempCtx.drawImage(img2, 0, 0, sw, sh);
      const data2 = tempCtx.getImageData(0, 0, sw, sh).data;
      
      // Compute motion for each grid cell
      // We find where the patch from frame1 appears in frame2
      for (let gy = 0; gy < gridH; gy++) {
        for (let gx = 0; gx < gridW; gx++) {
          const cx = Math.floor((gx + 0.5) * scaledGridSize);
          const cy = Math.floor((gy + 0.5) * scaledGridSize);
          
          if (cx >= sw || cy >= sh) continue;
          
          // Check if this region has any content (non-transparent pixels)
          let hasContent = false;
          const patchSize = Math.floor(scaledGridSize / 2);
          for (let py = -patchSize; py <= patchSize && !hasContent; py += 3) {
            for (let px = -patchSize; px <= patchSize && !hasContent; px += 3) {
              const x = cx + px;
              const y = cy + py;
              if (x >= 0 && x < sw && y >= 0 && y < sh) {
                const idx = (y * sw + x) * 4;
                // Check alpha in both frames
                if (data1[idx + 3] > 30 || data2[idx + 3] > 30) {
                  hasContent = true;
                }
              }
            }
          }
          
          // Skip empty regions - no motion needed
          if (!hasContent) {
            const idx = gy * gridW + gx;
            vectors[idx * 2] = 0;
            vectors[idx * 2 + 1] = 0;
            continue;
          }
          
          const searchRadius = 8; // Increased search radius
          let bestDx = 0, bestDy = 0, bestSSD = Infinity;
          
          for (let dy = -searchRadius; dy <= searchRadius; dy++) {
            for (let dx = -searchRadius; dx <= searchRadius; dx++) {
              let ssd = 0, count = 0;
              
              for (let py = -patchSize; py <= patchSize; py += 2) {
                for (let px = -patchSize; px <= patchSize; px += 2) {
                  const x1 = cx + px;
                  const y1 = cy + py;
                  const x2 = cx + px + dx;
                  const y2 = cy + py + dy;
                  
                  if (x1 < 0 || x1 >= sw || y1 < 0 || y1 >= sh ||
                      x2 < 0 || x2 >= sw || y2 < 0 || y2 >= sh) continue;
                  
                  const idx1 = (y1 * sw + x1) * 4;
                  const idx2 = (y2 * sw + x2) * 4;
                  
                  // Include alpha in comparison for radar data
                  const dr = data1[idx1] - data2[idx2];
                  const dg = data1[idx1 + 1] - data2[idx2 + 1];
                  const db = data1[idx1 + 2] - data2[idx2 + 2];
                  const da = data1[idx1 + 3] - data2[idx2 + 3];
                  ssd += dr * dr + dg * dg + db * db + da * da;
                  count++;
                }
              }
              
              if (count > 0 && ssd / count < bestSSD) {
                bestSSD = ssd / count;
                bestDx = dx;
                bestDy = dy;
              }
            }
          }
          
          const idx = gy * gridW + gx;
          // Store motion as-is: (dx, dy) means content moves by this offset
          vectors[idx * 2] = bestDx / scale;
          vectors[idx * 2 + 1] = bestDy / scale;
        }
      }
      
      // Light smoothing to reduce noise while preserving motion direction
      const smoothed = new Float32Array(vectors.length);
      for (let gy = 0; gy < gridH; gy++) {
        for (let gx = 0; gx < gridW; gx++) {
          let sumX = 0, sumY = 0, weight = 0;
          
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const nx = gx + dx;
              const ny = gy + dy;
              if (nx >= 0 && nx < gridW && ny >= 0 && ny < gridH) {
                const idx = ny * gridW + nx;
                // Center pixel has more weight
                const w = (dx === 0 && dy === 0) ? 2.0 : 1.0;
                sumX += vectors[idx * 2] * w;
                sumY += vectors[idx * 2 + 1] * w;
                weight += w;
              }
            }
          }
          
          const idx = gy * gridW + gx;
          smoothed[idx * 2] = sumX / weight;
          smoothed[idx * 2 + 1] = sumY / weight;
        }
      }
      
      return {
        vectors: smoothed,
        gridSize,
        gridW,
        gridH
      };
    },
    
    /**
     * Attempt smoother step function for more natural easing.
     */
    smootherstep(t) {
      return t * t * t * (t * (t * 6 - 15) + 10);
    },
    
    /**
     * Compute global dominant motion between two frames.
     * Uses feature-based tracking to find where weather patterns moved.
     */
    computeGlobalMotion(img1, img2, width, height) {
      // Downsample for speed
      const scale = 0.2;
      const sw = Math.floor(width * scale);
      const sh = Math.floor(height * scale);
      
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = sw;
      tempCanvas.height = sh;
      const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
      
      // Get downsampled data
      tempCtx.drawImage(img1, 0, 0, sw, sh);
      const data1 = tempCtx.getImageData(0, 0, sw, sh).data;
      
      tempCtx.clearRect(0, 0, sw, sh);
      tempCtx.drawImage(img2, 0, 0, sw, sh);
      const data2 = tempCtx.getImageData(0, 0, sw, sh).data;
      
      // Find regions with significant features (high gradient/contrast)
      // These are the "weather" areas we want to track
      const gradients1 = this.computeGradientMagnitude(data1, sw, sh);
      
      // Find feature points in img1 (high gradient locations)
      const features = [];
      const gridSize = 8;
      for (let gy = 1; gy < Math.floor(sh / gridSize) - 1; gy++) {
        for (let gx = 1; gx < Math.floor(sw / gridSize) - 1; gx++) {
          const cx = gx * gridSize + Math.floor(gridSize / 2);
          const cy = gy * gridSize + Math.floor(gridSize / 2);
          
          // Check gradient magnitude at this point
          const grad = gradients1[cy * sw + cx];
          if (grad > 15) { // Threshold for "interesting" features
            features.push({ x: cx, y: cy, grad });
          }
        }
      }
      
      // Sort by gradient strength and take top features
      features.sort((a, b) => b.grad - a.grad);
      const topFeatures = features.slice(0, 50);
      
      if (topFeatures.length < 5) {
        // Not enough features - use default westerly motion
        return { dx: 8 / scale, dy: 0, confidence: 0 };
      }
      
      // Track each feature to find where it moved in img2
      const motionVotes = [];
      const searchRadius = 8; // In downsampled space (8 * 5 = 40 pixels max motion in full res)
      const patchSize = 5;
      
      for (const feat of topFeatures) {
        let bestDx = 0;
        let bestDy = 0;
        let bestSSD = Infinity;
        
        // Search for this patch in img2
        for (let dy = -searchRadius; dy <= searchRadius; dy += 2) {
          for (let dx = -searchRadius; dx <= searchRadius; dx += 2) {
            if (dx === 0 && dy === 0) continue; // Skip zero motion
            
            let ssd = 0;
            let count = 0;
            
            for (let py = -patchSize; py <= patchSize; py++) {
              for (let px = -patchSize; px <= patchSize; px++) {
                const x1 = feat.x + px;
                const y1 = feat.y + py;
                const x2 = feat.x + px + dx;
                const y2 = feat.y + py + dy;
                
                if (x1 < 0 || x1 >= sw || y1 < 0 || y1 >= sh ||
                    x2 < 0 || x2 >= sw || y2 < 0 || y2 >= sh) continue;
                
                const idx1 = (y1 * sw + x1) * 4;
                const idx2 = (y2 * sw + x2) * 4;
                
                // Compare RGB
                const dr = data1[idx1] - data2[idx2];
                const dg = data1[idx1 + 1] - data2[idx2 + 1];
                const db = data1[idx1 + 2] - data2[idx2 + 2];
                ssd += dr * dr + dg * dg + db * db;
                count++;
              }
            }
            
            if (count > 0 && ssd / count < bestSSD) {
              bestSSD = ssd / count;
              bestDx = dx;
              bestDy = dy;
            }
          }
        }
        
        // Only accept if we found a reasonably good match
        if (bestSSD < 2000) {
          motionVotes.push({ dx: bestDx, dy: bestDy, weight: feat.grad / (bestSSD + 1) });
        }
      }
      
      if (motionVotes.length < 3) {
        // Not enough consistent motion - use default
        return { dx: 8 / scale, dy: 0, confidence: 0 };
      }
      
      // Use weighted median for robust motion estimation
      // (median is more robust to outliers than mean)
      motionVotes.sort((a, b) => a.dx - b.dx);
      const medianDx = motionVotes[Math.floor(motionVotes.length / 2)].dx;
      motionVotes.sort((a, b) => a.dy - b.dy);
      const medianDy = motionVotes[Math.floor(motionVotes.length / 2)].dy;
      
      // Scale to full resolution: multiply detected motion by inverse of scale
      // We searched on an image 5x smaller, so motion is 5x larger in full res
      // But wait - if we found a 4 pixel shift at 0.2 scale, that's 4 * (1/0.2) = 20 pixels
      // Actually the motion in downsampled space IS the motion in full space scaled down
      // So we need to multiply by 1/scale to get full resolution motion
      const fullResDx = medianDx / scale;
      const fullResDy = medianDy / scale;
      
      return {
        dx: fullResDx,
        dy: fullResDy,
        confidence: motionVotes.length / topFeatures.length
      };
    },
    
    /**
     * Compute gradient magnitude for each pixel (edge detection).
     */
    computeGradientMagnitude(data, width, height) {
      const gradients = new Float32Array(width * height);
      
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const idx = (y * width + x) * 4;
          const idxL = (y * width + x - 1) * 4;
          const idxR = (y * width + x + 1) * 4;
          const idxU = ((y - 1) * width + x) * 4;
          const idxD = ((y + 1) * width + x) * 4;
          
          // Sobel-like gradient using green channel
          const gx = (data[idxR + 1] - data[idxL + 1]) / 2;
          const gy = (data[idxD + 1] - data[idxU + 1]) / 2;
          
          gradients[y * width + x] = Math.sqrt(gx * gx + gy * gy);
        }
      }
      
      return gradients;
    },

    /**
     * Start the crossfade animation loop using requestAnimationFrame.
     * Uses continuous time-based progression for seamless animation without pauses.
     * At the last frame, pauses briefly then fades (not morphs) back to first frame.
     */
    startCrossfadeLoop() {
      this.crossfadeProgress = 0;
      this.crossfadeLastTime = performance.now();
      this.lastRenderTime = 0;
      this.loopPauseRemaining = 0; // Track pause time at end of loop
      
      const animate = (timestamp) => {
        if (!this.isPlaying) {
          this.animationFrameRequestId = null;
          return;
        }
        
        // Calculate time delta based on actual elapsed time
        let deltaMs = timestamp - this.crossfadeLastTime;
        // Cap deltaMs to prevent huge jumps if there's major lag (e.g., tab switch)
        // Use a fixed cap regardless of speed to avoid jumps when speed changes mid-animation
        deltaMs = Math.min(deltaMs, 50);
        
        // Handle pause at end of loop
        if (this.loopPauseRemaining > 0) {
          this.loopPauseRemaining -= deltaMs;
          if (this.loopPauseRemaining <= 0) {
            // Pause complete, start fade to first frame
            this.loopPauseRemaining = 0;
            this.isLoopFading = true;
            this.crossfadeProgress = 0;
          }
          this.crossfadeLastTime = timestamp;
          this.animationFrameRequestId = requestAnimationFrame(animate);
          return;
        }
        
        // Frame duration controls how fast we progress through DATA frames
        // Base duration of 400ms at 1x speed gives smooth but not too slow animation
        // Apply previewSpeedMultiplier during progressive loading (slower preview)
        const effectiveSpeed = this.playbackSpeed * this.previewSpeedMultiplier;
        let baseFrameDurationMs;
        
        if (this.smoothAnimationMode === 'morph' || this.smoothAnimationMode === 'motion') {
          // Morph/Motion: 400ms base, faster with speed multiplier
          baseFrameDurationMs = Math.max(150, 400 / effectiveSpeed);
        } else if (this.smoothAnimationMode === 'crossfade') {
          // Crossfade: same timing
          baseFrameDurationMs = Math.max(150, 400 / effectiveSpeed);
        } else {
          // Off mode: fast discrete frames
          baseFrameDurationMs = 200 / effectiveSpeed;
        }
        
        // Progressive loading mode: only animate between loaded frames
        // IMPORTANT: Only update the frame list at loop boundaries to prevent mid-loop jumps
        const isProgressiveMode = this.isPreloadingFrames;
        const totalFrames = this.canvasFrameCache.length;
        
        // Use snapshot of loaded frames - only updated at loop start
        let loadedIndices = this._loadedFrameIndices;
        
        // Initialize snapshot if needed (first time or after reset)
        if (!loadedIndices || loadedIndices.length === 0) {
          loadedIndices = [];
          for (let i = 0; i < totalFrames; i++) {
            if (this.canvasFrameCache[i]) loadedIndices.push(i);
          }
          this._loadedFrameIndices = loadedIndices;
          this._snapshotLoadedCount = this.progressiveLoadedCount;
          
          // NOTE: Don't compute motion fields during progressive loading
          // The computation blocks the main thread and causes visible pauses
          // Motion fields will be computed after all frames are loaded
        }
        
        // Safety check
        if (loadedIndices.length === 0) {
          this.crossfadeLastTime = timestamp;
          this.animationFrameRequestId = requestAnimationFrame(animate);
          return;
        }
        
        // In progressive mode, use loaded frame snapshot; otherwise use all frames
        const effectiveFrameCount = isProgressiveMode ? loadedIndices.length : totalFrames;
        const maxFrameIdx = effectiveFrameCount - 1;
        
        // Map animationFrameIndex to actual frame index
        const getActualFrameIndex = (idx) => {
          if (!isProgressiveMode) return idx;
          return loadedIndices[idx % loadedIndices.length] || 0;
        };
        
        const currentActualIdx = getActualFrameIndex(this.animationFrameIndex);
        const nextActualIdx = getActualFrameIndex((this.animationFrameIndex + 1) % effectiveFrameCount);
        
        // Calculate frame gap for adaptive timing
        // When frames are sparse (e.g., 0→5), extend the blend duration proportionally
        let frameGap = 1;
        if (isProgressiveMode && loadedIndices.length > 1) {
          // Calculate how many actual data frames we're spanning
          if (nextActualIdx > currentActualIdx) {
            frameGap = nextActualIdx - currentActualIdx;
          } else {
            // Wrapping from end to start
            frameGap = (totalFrames - currentActualIdx) + nextActualIdx;
          }
        }
        
        // Adaptive frame duration: longer blends for larger gaps (feels more natural)
        // Cap at 3x to prevent too-slow animation with very sparse keyframes
        const gapMultiplier = Math.min(3, Math.sqrt(frameGap));
        const frameDurationMs = baseFrameDurationMs * gapMultiplier;
        
        // Update crossfade progress
        this.crossfadeProgress += deltaMs / frameDurationMs;
        
        const isLastFrame = this.animationFrameIndex >= maxFrameIdx;
        
        // Special handling for last frame: don't morph to first, instead pause and fade
        // But only do this when NOT in progressive mode (all frames loaded)
        if (isLastFrame && !this.isLoopFading && !isProgressiveMode) {
          // Clamp progress - don't blend past the last frame
          if (this.crossfadeProgress >= 1) {
            this.crossfadeProgress = 0;
            this.loopPauseRemaining = 500 / effectiveSpeed; // 500ms pause at end
          }
          // Show last frame without blending to next (which would be first frame via morph)
          this.showCanvasFrame(currentActualIdx, 0);
          this.crossfadeLastTime = timestamp;
          this.animationFrameRequestId = requestAnimationFrame(animate);
          return;
        }
        
        // Check if we need to advance to next frame
        // Only advance one frame at a time to prevent jumping (smoother animation)
        if (this.crossfadeProgress >= 1) {
          if (this.isLoopFading) {
            // Fade from last to first complete, reset to first frame
            this.animationFrameIndex = 0;
            this.isLoopFading = false;
          } else {
            // Advance to next frame
            const nextPos = (this.animationFrameIndex + 1) % effectiveFrameCount;
            
            // If we're looping back to start, update the loaded frame snapshot
            // This is where new frames get added to the animation
            if (nextPos === 0 && isProgressiveMode) {
              const newLoadedIndices = [];
              for (let i = 0; i < totalFrames; i++) {
                if (this.canvasFrameCache[i]) newLoadedIndices.push(i);
              }
              
              // Only update if new frames were loaded
              if (newLoadedIndices.length > loadedIndices.length) {
                this._loadedFrameIndices = newLoadedIndices;
                this._snapshotLoadedCount = this.progressiveLoadedCount;
                
                // Update loadedIndices for this frame to use new snapshot immediately
                loadedIndices = newLoadedIndices;
                
                // NOTE: Don't compute motion fields here - it blocks the main thread
                // Motion fields will be computed after all frames are loaded
              }
            }
            
            this.animationFrameIndex = nextPos;
          }
          this.crossfadeProgress -= 1; // Keep fractional part for seamless transition
          // Cap the remaining progress to prevent accumulating lag
          this.crossfadeProgress = Math.min(this.crossfadeProgress, 0.5);
        }
        
        // Get actual frame indices for rendering
        const renderCurrentIdx = getActualFrameIndex(this.animationFrameIndex);
        const renderNextIdx = getActualFrameIndex((this.animationFrameIndex + 1) % effectiveFrameCount);
        
        // Apply smooth easing to blend factor for more natural transitions
        // smoothstep provides acceleration/deceleration at boundaries
        const rawBlend = this.crossfadeProgress;
        const blendFactor = rawBlend * rawBlend * (3 - 2 * rawBlend); // smoothstep
        
        // For loop fade (last→first), force simple crossfade
        // Progressive mode uses morphing when motion fields are available (computed async)
        const useSimpleFade = this.isLoopFading;
        
        // Pass the actual frame indices for progressive mode rendering
        if (isProgressiveMode) {
          this.showProgressiveFrame(renderCurrentIdx, renderNextIdx, blendFactor);
        } else {
          this.showCanvasFrame(renderCurrentIdx, blendFactor, useSimpleFade);
        }
        
        this.crossfadeLastTime = timestamp;
        this.animationFrameRequestId = requestAnimationFrame(animate);
      };
      
      this.animationFrameRequestId = requestAnimationFrame(animate);
    },
    
    /**
     * Stop the crossfade animation loop.
     */
    stopCrossfadeLoop() {
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      // Cancel any pending async motion field computation
      if (this._motionFieldComputeId) {
        clearTimeout(this._motionFieldComputeId);
        this._motionFieldComputeId = null;
      }
      this.crossfadeProgress = 0;
      this.loopPauseRemaining = 0;
      this.isLoopFading = false;
      // Clear progressive loading snapshot
      this._loadedFrameIndices = null;
      this._snapshotLoadedCount = 0;
      // Clear optical flow cache
      this.opticalFlowCache = {};
    },
    
    /**
     * Advance to the next canvas frame (legacy, for interval-based animation).
     */
    advanceCanvasFrame() {
      this.animationFrameIndex = (this.animationFrameIndex + 1) % this.canvasFrameCache.length;
      this.showCanvasFrame(this.animationFrameIndex, 0);
    },
    
    /**
     * Clear the canvas frame cache and release memory.
     */
    clearCanvasCache() {
      // Cancel any pending async motion field computation
      if (this._motionFieldComputeId) {
        clearTimeout(this._motionFieldComputeId);
        this._motionFieldComputeId = null;
      }
      // Explicitly clear Image references to help garbage collection
      if (this.canvasFrameCache && this.canvasFrameCache.length > 0) {
        for (let i = 0; i < this.canvasFrameCache.length; i++) {
          const frame = this.canvasFrameCache[i];
          if (frame) {
            if (frame.img) {
              frame.img.src = ''; // Release the data URL reference
              frame.img = null;
            }
            frame.imageData = null;
          }
        }
      }
      this.canvasFrameCache = [];
      // Clear progressive loading state
      this._loadedFrameIndices = null;
      this._snapshotLoadedCount = 0;
      // Clear optical flow cache
      this.opticalFlowCache = {};
      // Clear WebGL textures
      this.clearWebGLTextures();
    },
    
    /**
     * Clear progressive mode textures only (keeps regular frame textures).
     * Called when snapshot updates to free memory from old progressive textures.
     */
    clearProgressiveTextures() {
      if (!this.webglContext || !this.webglTextures) return;
      
      const gl = this.webglContext;
      const keysToDelete = [];
      
      for (const key in this.webglTextures) {
        // Progressive textures use keys like: progressive_tex1_field_X_Y, progressive_motion_field_X_Y
        if (key.startsWith('progressive_')) {
          if (this.webglTextures[key]) {
            gl.deleteTexture(this.webglTextures[key]);
          }
          keysToDelete.push(key);
        }
      }
      
      for (const key of keysToDelete) {
        delete this.webglTextures[key];
      }
    },
    
    /**
     * Clear and dispose of all WebGL textures.
     */
    clearWebGLTextures() {
      if (this.webglContext && this.webglTextures) {
        const gl = this.webglContext;
        for (const key in this.webglTextures) {
          if (this.webglTextures[key]) {
            gl.deleteTexture(this.webglTextures[key]);
          }
        }
      }
      this.webglTextures = {};
    },
    
    // ========================================
    // End Canvas Animation Methods
    // ========================================

    increaseTimeSkip() {
      const currentIndex = this.timeSkipPresets.indexOf(this.manualTimeSkip);
      if (currentIndex < this.timeSkipPresets.length - 1) {
        this.manualTimeSkip = this.timeSkipPresets[currentIndex + 1];
      } else if (this.manualTimeSkip < 120) {
        this.manualTimeSkip = Math.min(120, this.manualTimeSkip + 30);
      }
    },
    
    decreaseTimeSkip() {
      const currentIndex = this.timeSkipPresets.indexOf(this.manualTimeSkip);
      if (currentIndex > 0) {
        this.manualTimeSkip = this.timeSkipPresets[currentIndex - 1];
      } else if (this.manualTimeSkip > 1) {
        this.manualTimeSkip = Math.max(1, this.manualTimeSkip - 1);
      }
    },
    
    incrementTime() {
      // If we have animation frames cached (playing or paused), navigate within them
      if (this.canvasFrameCache.length > 0) {
        this.navigateToNextAnimationFrame();
        return;
      }
      
      // Get the interval for the current time and model
      const intervalMinutes = this.getTimeIntervalMinutes(this.currentTime, this.primaryModelConfig, 1);
      const newTime = this.calculateNewTime(this.currentTime, intervalMinutes);
      
      // Check if new time is within model's valid range
      const timeRange = this.modelTimeRange;
      if (timeRange && newTime > timeRange.maxTime) {
        // Don't go beyond max time
        return;
      }
      
      this.currentTime = this.clampToLoopWindow(newTime, true);
      this.refreshAllLayers();
    },
    
    decrementTime() {
      // If we have animation frames cached (playing or paused), navigate within them
      if (this.canvasFrameCache.length > 0) {
        this.navigateToPrevAnimationFrame();
        return;
      }
      
      // Get the interval for the current time and model (looking backward)
      const intervalMinutes = this.getTimeIntervalMinutes(this.currentTime, this.primaryModelConfig, -1);
      const newTime = this.calculateNewTime(this.currentTime, -intervalMinutes);
      
      // Check if new time is within model's valid range
      const timeRange = this.modelTimeRange;
      if (timeRange && newTime < timeRange.minTime) {
        // Don't go beyond min time
        return;
      }
      
      this.currentTime = this.clampToLoopWindow(newTime, false);
      this.refreshAllLayers();
    },
    
    /**
     * Navigate to the next frame in the animation cache.
     * Used when stepping through frames while animation is paused.
     */
    navigateToNextAnimationFrame() {
      if (this.canvasFrameCache.length === 0) return;
      
      // Pause if playing
      if (this.isPlaying) {
        this.pauseMovie();
      }
      
      // Advance to next frame (wrapping to start)
      this.animationFrameIndex = (this.animationFrameIndex + 1) % this.canvasFrameCache.length;
      this.crossfadeProgress = 0;
      
      // Update display
      this.showCanvasFrame(this.animationFrameIndex, 0);
      
      // Update currentTime to match this frame
      const frame = this.canvasFrameCache[this.animationFrameIndex];
      if (frame && frame.timeKey) {
        this.currentTime = new Date(frame.timeKey);
      }
    },
    
    /**
     * Navigate to the previous frame in the animation cache.
     * Used when stepping through frames while animation is paused.
     */
    navigateToPrevAnimationFrame() {
      if (this.canvasFrameCache.length === 0) return;
      
      // Pause if playing
      if (this.isPlaying) {
        this.pauseMovie();
      }
      
      // Go to previous frame (wrapping to end)
      this.animationFrameIndex = (this.animationFrameIndex - 1 + this.canvasFrameCache.length) % this.canvasFrameCache.length;
      this.crossfadeProgress = 0;
      
      // Update display
      this.showCanvasFrame(this.animationFrameIndex, 0);
      
      // Update currentTime to match this frame
      const frame = this.canvasFrameCache[this.animationFrameIndex];
      if (frame && frame.timeKey) {
        this.currentTime = new Date(frame.timeKey);
      }
    },
    
    resetToInitialTime() {
      const now = this.getLastInterval();
      this.currentTime = this.clampToLoopWindow(now, true);
      this.refreshAllLayers();
    },
    
    calculateNewTime(time, deltaMinutes) {
      const newTime = new Date(time);
      newTime.setUTCMinutes(newTime.getUTCMinutes() + deltaMinutes);
      return newTime;
    },
    
    /**
     * For step-based loop window, manual time navigation is unrestricted.
     * Animation frames are generated relative to currentTime, so no clamping needed.
     * @param {Date} time - The time to return
     * @param {boolean} forward - Unused, kept for API compatibility
     * @returns {Date} - The same time, unchanged
     */
    clampToLoopWindow(time, forward) {
      return time;
    },
    
    getLastInterval() {
      const now = new Date();
      const timeSkip = this.currentTimeSkip;
      const currentMinutes = now.getUTCMinutes();
      
      // Round down to nearest interval
      const roundedMinutes = Math.floor(currentMinutes / timeSkip) * timeSkip;
      
      // Calculate how far through the current interval we are (0-1)
      const progressThroughInterval = (currentMinutes - roundedMinutes) / timeSkip;
      
      // If less than 50% through the interval, go back one more step
      // Example: 10 min interval, minute 12 (20% through) -> 10 -> 0
      //          10 min interval, minute 17 (70% through) -> 10 (stays)
      let finalMinutes = roundedMinutes;
      if (progressThroughInterval < 0.5) {
        finalMinutes = roundedMinutes - timeSkip;
      }
      
      now.setUTCMinutes(finalMinutes, 0, 0);
      return now;
    },
    
    /**
     * Snap a given time to the nearest interval based on the current timeSkip or model's temporal availability.
     * Used when switching elements/models with different time intervals.
     * 
     * @param {Date} time - The time to snap
     * @param {number} [newTimeSkip] - Optional specific interval in minutes (defaults to currentTimeSkip)
     * @param {Object} [modelConfig] - Optional model config for model-specific snapping
     * @returns {Date} - The snapped time
     */
    snapTimeToInterval(time, newTimeSkip, modelConfig) {
      if (!time) return time;
      
      // If autoTimeSkip is enabled and model config has temporal availability, use model-specific snapping
      const config = modelConfig || this.primaryModelConfig;
      if (this.autoTimeSkip && config?.temporalAvailability?.intervals && config.modelRunTimeEpoch) {
        return snapTimeToModelInterval(time, config, 'nearest');
      }
      
      const timeSkip = newTimeSkip || this.currentTimeSkip;
      const snapped = new Date(time);
      
      // For intervals >= 60 minutes, also round hours
      if (timeSkip >= 60) {
        const hoursInInterval = timeSkip / 60;
        const currentHours = snapped.getUTCHours();
        const roundedHours = Math.round(currentHours / hoursInInterval) * hoursInInterval;
        snapped.setUTCHours(roundedHours, 0, 0, 0);
      } else {
        // For minute-based intervals, round minutes
        const currentMinutes = snapped.getUTCMinutes();
        const roundedMinutes = Math.round(currentMinutes / timeSkip) * timeSkip;
        snapped.setUTCMinutes(roundedMinutes, 0, 0);
      }
      
      return snapped;
    },

    /**
     * Get the time interval in minutes for a given time and model.
     * Uses the model's temporalAvailability if available and autoTimeSkip is enabled,
     * otherwise falls back to currentTimeSkip (which uses manualTimeSkip when auto is disabled).
     * 
     * @param {Date} time - The current time
     * @param {Object} modelConfig - The model configuration (from modelOptions)
     * @param {number} direction - 1 for forward, -1 for backward (used to look ahead/behind for interval changes)
     * @returns {number} - Interval in minutes
     */
    getTimeIntervalMinutes(time, modelConfig, direction = 1) {
      // If autoTimeSkip is disabled, always use the manual time skip
      if (!this.autoTimeSkip) {
        return this.manualTimeSkip;
      }
      
      // If no model config or no temporal availability, use the default currentTimeSkip
      if (!modelConfig?.temporalAvailability?.intervals || !modelConfig.modelRunTimeEpoch) {
        return this.currentTimeSkip;
      }
      
      const modelRunMs = modelConfig.modelRunTimeEpoch * 1000;
      const timeMs = time.getTime();
      const hoursFromRun = (timeMs - modelRunMs) / (3600 * 1000);
      
      // Get the interval in seconds for this time offset
      const intervalSeconds = getIntervalForHoursOffset(modelConfig, hoursFromRun);
      
      console.log('getTimeIntervalMinutes using model:', {
        modelRunMs,
        hoursFromRun,
        intervalSeconds,
        intervalMinutes: Math.round(intervalSeconds / 60)
      });
      
      return Math.round(intervalSeconds / 60);
    },

    /**
     * Reset the current time to now (rounded to current time skip interval)
     */
    resetToNow() {
      // If we have cached animation frames (paused animation), stop it completely
      // so the new time takes effect
      if (this.canvasFrameCache.length > 0 && !this.isPlaying) {
        this.stopMovie();
      }
      this.currentTime = this.getLastInterval();
      this.refreshAllLayers();
      // If animation is playing, restart to regenerate frames from new time
      this.restartAnimationIfPlaying();
    },
    
    onDateTimeInput(e) {
      this.tempDateTimeValue = e.target.value;
    },
    
    applyDateTime() {
      if (this.tempDateTimeValue) {
        // If we have cached animation frames (paused animation), stop it completely
        // so the new time takes effect
        if (this.canvasFrameCache.length > 0 && !this.isPlaying) {
          this.stopMovie();
        }
        const date = new Date(this.tempDateTimeValue + ':00Z');
        this.currentTime = date;
        this.refreshAllLayers();
        // If animation is playing, restart to regenerate frames from new time
        this.restartAnimationIfPlaying();
      }
      this.showDateTimePicker = false;
    },
    
    formatFullDateTime(date) {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:00`;
    },

    /**
     * Round a Date to the nearest interval based on the tile element.
     * precip / precipcomposite → nearest 10 minutes; all others → nearest hour.
     */
    roundTimeForElement(date, element) {
      const d = new Date(date.getTime());
      const el = (element || '').toLowerCase();
      if (el === 'precip' || el === 'precipcomposite' || el === 'precip-composite') {
        const mins = d.getUTCMinutes();
        const rounded = Math.round(mins / 10) * 10;
        d.setUTCMinutes(rounded, 0, 0);
      } else {
        const mins = d.getUTCMinutes();
        if (mins >= 30) {
          d.setUTCHours(d.getUTCHours() + 1);
        }
        d.setUTCMinutes(0, 0, 0);
      }
      return d;
    },
    
    /**
     * Format a date for the datetime-local input (YYYY-MM-DDTHH:mm)
     */
    formatDateTimeLocal(date) {
      if (!date) return '';
      const pad = n => String(n).padStart(2, '0');
      const y = date.getUTCFullYear();
      const m = pad(date.getUTCMonth() + 1);
      const d = pad(date.getUTCDate());
      const h = pad(date.getUTCHours());
      const min = pad(date.getUTCMinutes());
      return `${y}-${m}-${d}T${h}:${min}`;
    },
    
    /**
     * Format a short date/time string (MM/DD HH:mm) for display
     */
    formatShortDateTime(date) {
      if (!date) return '';
      const pad = n => String(n).padStart(2, '0');
      const month = pad(date.getUTCMonth() + 1);
      const day = pad(date.getUTCDate());
      const hours = pad(date.getUTCHours());
      const minutes = pad(date.getUTCMinutes());
      return `${month}/${day} ${hours}:${minutes}`;
    },
    
    /**
     * Format a compact date/time string for slider labels.
     * Shows YYYY-MM-DDTHH:mm format (ISO-like but readable)
     */
    formatDateTimeCompact(date) {
      if (!date) return '--';
      const pad = n => String(n).padStart(2, '0');
      const y = date.getUTCFullYear();
      const m = pad(date.getUTCMonth() + 1);
      const d = pad(date.getUTCDate());
      const h = pad(date.getUTCHours());
      const min = pad(date.getUTCMinutes());
      return `${y}-${m}-${d}T${h}${min}`;
    },
    
    /**
     * Format a time step value (in minutes) for display.
     * <2min = seconds, <60min = minutes, >=60min = hours
     */
    formatTimeStepLabel(minutes) {
      if (minutes < 2) {
        return `${Math.round(minutes * 60)}s`;
      } else if (minutes < 60) {
        return `${minutes}m`;
      } else {
        const hours = minutes / 60;
        return `${hours}h`;
      }
    },
    
    /**
     * Format a short time string (HH:mm) from an ISO date string.
     * Shows date prefix if different from today.
     */
    formatShortTime(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      const pad = n => String(n).padStart(2, '0');
      const hours = pad(date.getUTCHours());
      const minutes = pad(date.getUTCMinutes());
      
      // Check if it's the same day as current time
      const now = this.currentTime || new Date();
      const sameDay = date.getUTCFullYear() === now.getUTCFullYear() &&
                      date.getUTCMonth() === now.getUTCMonth() &&
                      date.getUTCDate() === now.getUTCDate();
      
      if (sameDay) {
        return `${hours}:${minutes}`;
      } else {
        // Include month/day for different days
        const month = pad(date.getUTCMonth() + 1);
        const day = pad(date.getUTCDate());
        return `${month}/${day} ${hours}:${minutes}`;
      }
    },
    
    /**
     * Seek to a specific frame in the animation.
     * Called when user drags the timeline slider.
     */
    seekToFrame(frameIndex) {
      if (frameIndex < 0 || frameIndex >= this.animationTimeKeys.length) return;
      
      this.animationFrameIndex = frameIndex;
      
      // Update currentTime to match the frame
      const timeKey = this.animationTimeKeys[frameIndex];
      if (timeKey) {
        this.currentTime = new Date(timeKey);
      }
      
      // Update display based on animation mode
      if (this.useCanvasAnimation && this.canvasFrameCache.length > frameIndex && this.animationCanvas) {
        // Canvas mode with valid frame - show it directly
        this.showCanvasFrame(frameIndex, 0);
      } else if (Object.keys(this.animationBuffers).length > 0) {
        // Tile layer mode with buffers - update the buffers
        this.updateBuffersForFrame(frameIndex);
      } else {
        // No animation system active - refresh the regular tile layers
        this.refreshAllLayers();
      }
    },
    
    /**
     * Called when user starts dragging the timeline.
     * Pauses animation during drag.
     */
    onTimelineDragStart() {
      this.isTimelineDragging = true;
      this.wasPlayingBeforeDrag = this.isPlaying;
      if (this.isPlaying) {
        this.pauseMovie();
      }
    },
    
    /**
     * Called when user stops dragging the timeline.
     * Resumes animation if it was playing before.
     */
    onTimelineDragEnd() {
      this.isTimelineDragging = false;
      if (this.wasPlayingBeforeDrag) {
        // Small delay to let the frame settle before resuming
        setTimeout(() => {
          if (!this.isPlaying && !this.isTimelineDragging) {
            this.resumeMovie();
          }
        }, 100);
      }
    },
    
    /**
     * Update tile layer buffers to show a specific frame.
     * Used when seeking via timeline slider in tile layer mode.
     */
    updateBuffersForFrame(frameIndex) {
      if (Object.keys(this.animationBuffers).length === 0) return;
      
      const timeKey = this.animationTimeKeys[frameIndex];
      if (!timeKey) return;
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      
      for (const layer of visibleLayers) {
        const buffer = this.animationBuffers[layer.id];
        if (!buffer) continue;
        
        const newUrl = this.buildTileUrl(layer.element, layer.model, new Date(timeKey));
        
        // Update front buffer with new URL and show it
        buffer.front.setUrl(newUrl);
        buffer.front.setOpacity(buffer.targetOpacity);
        buffer.frontTimeKey = timeKey;
        
        // Update back buffer with next frame
        const nextIndex = (frameIndex + 1) % this.animationTimeKeys.length;
        const nextTimeKey = this.animationTimeKeys[nextIndex];
        const nextUrl = this.buildTileUrl(layer.element, layer.model, new Date(nextTimeKey));
        buffer.back.setUrl(nextUrl);
        buffer.back.setOpacity(0);
        buffer.backTimeKey = nextTimeKey;
      }
      
      this.nextFrameReady = true;
    },
    
    /**
     * Pause the movie without clearing state.
     * Snaps to nearest actual frame when pausing on interpolated position.
     */
    pauseMovie() {
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      
      // Reset loop transition state
      this.loopPauseRemaining = 0;
      this.isLoopFading = false;
      
      // Snap to nearest actual frame if we're mid-interpolation
      if (this.crossfadeProgress > 0.5 && this.canvasFrameCache.length > 0) {
        // Closer to next frame - advance to it
        this.animationFrameIndex = (this.animationFrameIndex + 1) % this.canvasFrameCache.length;
      }
      this.crossfadeProgress = 0;
      
      // Update currentTime to match the snapped frame
      if (this.animationTimeKeys.length > this.animationFrameIndex) {
        const timeKey = this.animationTimeKeys[this.animationFrameIndex];
        if (timeKey) {
          this.currentTime = new Date(timeKey);
        }
      }
      
      // Update display to show the snapped frame
      if (this.animationCanvas && this.canvasFrameCache.length > 0) {
        this.showCanvasFrame(this.animationFrameIndex, 0);
      }
      
      this.isPlaying = false;
    },
    
    /**
     * Resume the movie from current frame.
     */
    resumeMovie() {
      if (this.isPlaying) return;
      
      this.isPlaying = true;
      
      if (this.useCanvasAnimation && this.canvasFrameCache.length > 0) {
        // Resume canvas animation
        this.startCrossfadeLoop();
      } else if (Object.keys(this.animationBuffers).length > 0) {
        // Resume tile layer animation
        this.movieInterval = setInterval(() => {
          this.advanceDoubleBufferFrame();
        }, 1000 / this.playbackSpeed);
      }
    },
    
    /**
     * Update view info with throttling to prevent excessive updates during rapid pan/zoom.
     * Uses trailing edge throttle - updates at end of rapid movements.
     */
    updateViewInfo() {
      if (!this.map) return;
      
      // Throttle: cancel pending update, schedule new one
      if (this.updateViewInfoTimer) {
        clearTimeout(this.updateViewInfoTimer);
      }
      
      this.updateViewInfoTimer = setTimeout(() => {
        if (!this.map) return;
        const c = this.map.getCenter();
        this.viewInfo.lat = Number(c.lat.toFixed(5));
        this.viewInfo.lng = Number(c.lng.toFixed(5));
        this.viewInfo.zoom = this.map.getZoom();
        this.viewInfo.centerPair = `${this.viewInfo.lat},${this.viewInfo.lng}`;
        
        // Store map bounds for intersection checking
        const mapBounds = this.map.getBounds();
        this.viewInfo.bounds = {
          north: mapBounds.getNorth(),
          south: mapBounds.getSouth(),
          east: mapBounds.getEast(),
          west: mapBounds.getWest()
        };
        this.updateViewInfoTimer = null;
        
        // Check if current quick-select model is now out of bounds
        // and auto-switch to an in-bounds model if available
        this.checkAndSwitchModelIfOutOfBounds();
      }, 100); // 100ms throttle
    },
    
    /**
     * Check if current quick-select model is out of bounds and switch to an in-bounds one
     */
    checkAndSwitchModelIfOutOfBounds() {
      if (!this.quickSelectActive) return; // Only matters if a layer is active
      
      if (!this.isModelInBounds(this.quickSelectModel)) {
        const inBoundsModels = this.inBoundsModels;
        if (inBoundsModels.length > 0) {
          this.quickSelectModel = inBoundsModels[0];
          this.applyQuickSelectLayer();
        }
      }
    },
    
    async copyCenterToClipboard() {
      try {
        await navigator.clipboard.writeText(this.viewInfo.centerPair);
      } catch (e) {
        console.warn('Copy failed', e);
      }
    },
    
    getLayerUrlPattern(layer) {
      const sourceParam = layer.model && layer.model !== 'default' ? `&source=${layer.model}` : '';
      const strictParam = this.strictMode ? '' : '&strict=false';
      const refreshParam = this.forceRefresh ? '&refresh=true' : '';
      // Use actual key if custom, otherwise show placeholder
      const keyValue = this.hasCustomApiKey ? this.weatherApiKey : 'YOUR_API_KEY';
      return `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
             `${layer.element}/{z}/{x}/{y}.webp` +
             `?key=${keyValue}` +
             `&time={time}` +
             `&unitGroup=${this.effectiveUnitGroup}` +
             `${strictParam}` +
             `${sourceParam}` +
             `${refreshParam}`;
    },
    
    getLegendUrlPattern(layer) {
      // Use actual key if custom, otherwise show placeholder
      const keyValue = this.hasCustomApiKey ? this.weatherApiKey : 'YOUR_API_KEY';
      return `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/legend/` +
             `${layer.element}?key=${keyValue}&unitGroup=${this.effectiveUnitGroup}`;
    },
    
    async copyUrlPattern(layer, type = 'tile') {
      try {
        const url = type === 'legend' 
          ? this.getLegendUrlPattern(layer) 
          : this.getLayerUrlPattern(layer);
        await navigator.clipboard.writeText(url);
      } catch (e) {
        console.warn('Copy failed', e);
      }
    },
    
    getColor(value, element) {
      if (this.elementBinCache[element]) {
        const bins = this.elementBinCache[element];
        for (let bin of bins) {
          if (value >= bin.min && (bin.max == null || value < bin.max)) {
            return bin.color;
          }
        }
      }
      return '#CFCFCF';
    },
    
   getContrastingTextColor(hex) {
      if (!hex) return '#000000';
      const h = hex.replace('#', '');
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return L > 0.5 ? '#000000' : '#FFFFFF';
    },
    
    saveSettings() {
      try {
        const settings = {
          unitGroup: this.effectiveUnitGroup,
          baseApiUrl: this.baseApiUrl,
          selectedBaseMap: this.selectedBaseMap,
          showLabels: this.showLabels,
          showGeoJson: this.showGeoJson,
          geoJsonElement: this.geoJsonElement,
          strictMode: this.strictMode,
          forceRefresh: this.forceRefresh,
          playbackSpeed: this.playbackSpeed,
          autoTimeSkip: this.autoTimeSkip,
          manualTimeSkip: this.manualTimeSkip,
          apiKeyOverride: this.apiKeyOverride,
          activeLayers: this.activeLayers.map(layer => ({
            id: layer.id,
            model: layer.model,
            element: layer.element,
            visible: layer.visible,
            opacity: layer.opacity
          })),
          nextLayerId: this.nextLayerId
        };
        localStorage.setItem('weatherMapSettings', JSON.stringify(settings));
      } catch (e) {
        // If quota exceeded, try clearing old weather map data and retry
        if (e.name === 'QuotaExceededError') {
          try {
            localStorage.removeItem('weatherMapSettings');
            localStorage.removeItem('weatherMapPosition');
            console.warn('localStorage quota exceeded, cleared old data');
          } catch (clearError) {
            // Ignore clear errors
          }
        } else {
          console.warn('Failed to save settings:', e);
        }
      }
    },
    
    loadSettings() {
      try {
        const saved = localStorage.getItem('weatherMapSettings');
        if (!saved) return;
        
        const settings = JSON.parse(saved);
        
        if (settings.unitGroup) this.internalUnitGroup = settings.unitGroup;
        if (settings.baseApiUrl) this.baseApiUrl = settings.baseApiUrl;
        if (settings.selectedBaseMap) this.selectedBaseMap = settings.selectedBaseMap;
        if (typeof settings.showLabels === 'boolean') this.showLabels = settings.showLabels;
        if (typeof settings.showGeoJson === 'boolean') this.showGeoJson = settings.showGeoJson;
        if (settings.geoJsonElement) this.geoJsonElement = settings.geoJsonElement;
        if (typeof settings.strictMode === 'boolean') this.strictMode = settings.strictMode;
        if (typeof settings.forceRefresh === 'boolean') this.forceRefresh = settings.forceRefresh;
        if (settings.playbackSpeed) this.playbackSpeed = settings.playbackSpeed;
        if (typeof settings.autoTimeSkip === 'boolean') this.autoTimeSkip = settings.autoTimeSkip;
        if (settings.manualTimeSkip) this.manualTimeSkip = settings.manualTimeSkip;
        if (settings.nextLayerId) this.nextLayerId = settings.nextLayerId;
        if (settings.apiKeyOverride) this.apiKeyOverride = settings.apiKeyOverride;
        
        if (Array.isArray(settings.activeLayers)) {
          this.activeLayers = settings.activeLayers.map(layer => ({
            ...layer,
            leafletLayer: null // Will be created after map init
          }));
          
          // Sync quick-select dropdowns with loaded layers
          // First check for explicit quick-select layer
          let quickLayer = this.activeLayers.find(l => l.isQuickSelect);
          
          // If no quick-select layer, but there's exactly one visible layer, use it
          if (!quickLayer) {
            const visibleLayers = this.activeLayers.filter(l => l.visible !== false);
            if (visibleLayers.length === 1) {
              quickLayer = visibleLayers[0];
              // Mark it as quick-select so future changes work correctly
              quickLayer.isQuickSelect = true;
            }
          }
          
          if (quickLayer) {
            this.quickSelectElement = quickLayer.element;
            // Use quickSelectModelId if available, otherwise find the model ID from the API model name
            let modelId = quickLayer.quickSelectModelId;
            if (!modelId && quickLayer.model) {
              // Find which quick-select model uses this API model name
              // Check if the model name directly matches a quick-select model
              if (this.quickSelectModels.includes(quickLayer.model)) {
                modelId = quickLayer.model;
              } else {
                // Look for a model where sourceModel matches, or the model ID itself matches
                for (const qsModelId of this.quickSelectModels) {
                  const config = this.modelOptions[qsModelId];
                  const apiModelName = config?.sourceModel || qsModelId;
                  if (apiModelName === quickLayer.model) {
                    modelId = qsModelId;
                    break;
                  }
                }
              }
              // Fallback to the model name if we can't find a match
              if (!modelId) modelId = quickLayer.model;
            }
            this.quickSelectModel = modelId;
            this.quickSelectActive = true;
            console.log('[loadSettings] Synced quick-select to:', quickLayer.element, modelId, '(layer.model:', quickLayer.model + ')');
          }
        }
      } catch (e) {
        console.warn('Failed to load settings:', e);
      }
    },
    
    /**
     * Handle API key override changes
     */
    onApiKeyChange() {
      this.saveSettings();
      this.tileError = null; // Clear any existing error
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
    },
    
    /**
     * Clear API key override and use session key
     */
    clearApiKeyOverride() {
      this.apiKeyOverride = '';
      this.saveSettings();
      this.tileError = null;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
    },
    
    /**
     * Handle tile loading errors
     * Shows error in UI for API key related issues
     */
    handleTileError(error, layer) {
      // Increment error counts
      this.tileErrorCount++;
      if (!this.layerErrors[layer.id]) {
        this.layerErrors[layer.id] = 0;
      }
      this.layerErrors[layer.id]++;
      
      // Clear any existing timeout for the message
      if (this.tileErrorTimeout) {
        clearTimeout(this.tileErrorTimeout);
      }
      
      // Try to get more info about the error
      const tile = error.tile;
      const url = tile?.src || '';
      
      // Check if it's likely an API key issue (401/403 errors)
      // We can't directly access HTTP status, but we can infer from the URL
      if (url.includes('key=')) {
        this.tileError = 'Failed to load map tiles. This may be due to an invalid or expired API key. ' +
                        'Check the API Integration section to set your API key.';
      } else {
        this.tileError = 'Failed to load map tiles. Please check your connection and try again.';
      }
      
      // Don't auto-dismiss if we have many errors - keep showing until user dismisses
      if (this.tileErrorCount < 5) {
        this.tileErrorTimeout = setTimeout(() => {
          this.tileError = null;
        }, 15000);
      }
    },
    
    /**
     * Manually dismiss the tile error banner
     */
    dismissTileError() {
      this.tileError = null;
      this.tileErrorCount = 0;
      this.layerErrors = {};
      if (this.tileErrorTimeout) {
        clearTimeout(this.tileErrorTimeout);
        this.tileErrorTimeout = null;
      }
    }
  }
};
</script>

<style scoped>
.weather-map-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

/* Quick-select layer control - inline dropdowns next to Leaflet zoom controls */
.quick-select-control {
  position: absolute;
  top: 10px;
  left: 60px; /* After zoom and layer toggle buttons */
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.4);
  padding: 6px 8px;
  transition: left 0.15s ease;
}

/* Shift quick-select control right when advanced controls panel is open */
.quick-select-control.controls-panel-open {
  left: 360px; /* After map-controls panel (50px + 300px max-width + 10px gap) */
}

.quick-select-control .quick-select-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.quick-select-control .quick-select-label {
  font-size: 11px;
  color: #666;
  min-width: 48px;
}

.quick-select-control .quick-select-dropdown {
  font-size: 12px;
  padding: 4px 24px 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  min-width: 100px;
  max-width: 160px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23666' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
}

.quick-select-control .quick-select-dropdown:hover {
  border-color: #999;
}

.quick-select-control .quick-select-dropdown:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.15);
}

/* Model button bar for wide screens */
.model-button-bar {
  display: flex;
  flex-wrap: wrap;
  max-width: 30vw;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

.model-button-bar .model-btn {
  font-size: 11px;
  padding: 4px 8px;
  border: none;
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  background: #f8f8f8;
  color: #555;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.model-button-bar .model-btn:last-child {
  border-right: none;
}

.model-button-bar .model-btn:hover {
  background: #eee;
}

.model-button-bar .model-btn.active {
  background: #0d6efd;
  color: white;
}

.model-button-bar .model-btn.out-of-bounds {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f0f0f0;
  color: #999;
}

.model-button-bar .model-btn.out-of-bounds:hover {
  background: #f0f0f0;
}

/* Priority button styling (Hybrid, Radar) - slightly emphasized */
.model-button-bar .model-btn.priority-btn {
  font-weight: 600;
}

/* Multi-layer mode toggle button */
.model-button-bar .model-btn.multi-toggle-btn {
  padding: 4px 6px;
  background: #f0f0f0;
  border-right: 2px solid #bbb;
  position: relative;
}

.model-button-bar .model-btn.multi-toggle-btn:hover {
  background: #e0e0e0;
}

.model-button-bar .model-btn.multi-toggle-btn.active {
  background: #28a745;
  color: white;
}

/* Layer count badge on multi-toggle button */
.layer-count-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #dc3545;
  color: white;
  font-size: 9px;
  font-weight: bold;
  min-width: 14px;
  height: 14px;
  line-height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  text-align: center;
}

/* Multi-layer mode indicator on button bar */
.model-button-bar.multi-mode {
  border-color: #28a745;
  box-shadow: 0 0 0 1px #28a745;
}

.model-button-bar.multi-mode .model-btn.active {
  background: #28a745;
}

/* Separator between priority models and other models */
.model-separator {
  width: 1px;
  height: 20px;
  background: #bbb;
  margin: 0 2px;
  align-self: center;
}

/* Dropdown option styling for out-of-bounds models */
.quick-select-dropdown option.out-of-bounds {
  color: #999;
}

.quick-select-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  border-radius: 4px;
  padding: 0;
  margin-left: 2px;
}

.quick-select-clear-btn:hover {
  background: rgba(211, 47, 47, 0.1);
  color: #d32f2f;
}

/* Opacity control in quick-select row */
.quick-opacity-control {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  color: #666;
}

.quick-opacity-slider {
  width: 60px;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: #ddd;
  border-radius: 2px;
  cursor: pointer;
}

.quick-opacity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  background: #0d6efd;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.quick-opacity-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  background: #0d6efd;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

/* Secondary row for overlay and location - smaller and more subtle */
.quick-select-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 2px;
  border-top: 1px solid #eee;
  margin-top: 2px;
}

.quick-tool-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 3px 6px;
  border: none;
  border-radius: 4px;
  background: #f5f5f5;
  color: #555;
  cursor: pointer;
  transition: background-color 0.15s;
}

.quick-tool-btn:hover {
  background: #e0e0e0;
}

.quick-tool-btn svg {
  flex-shrink: 0;
}

.tool-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  font-size: 9px;
  font-weight: 600;
  line-height: 14px;
  text-align: center;
  color: white;
  background: #1976d2;
  border-radius: 7px;
}

.quick-select-divider {
  width: 1px;
  height: 16px;
  background: #ddd;
}

.quick-select-spacer {
  flex: 1;
}

.quick-error-indicator {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 500;
  color: #f57c00;
  background: rgba(245, 124, 0, 0.1);
  border-radius: 3px;
  cursor: default;
}

.quick-error-indicator.error {
  color: #d32f2f;
  background: rgba(211, 47, 47, 0.1);
  cursor: pointer;
}

.quick-error-indicator.error:hover {
  background: rgba(211, 47, 47, 0.2);
}

.quick-error-indicator svg {
  flex-shrink: 0;
}

.quick-overlay-select {
  font-size: 10px;
  padding: 2px 18px 2px 6px;
  border: none;
  border-radius: 3px;
  background: #f5f5f5;
  color: #555;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' fill='%23888' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 4px center;
}

.quick-overlay-select:hover {
  background-color: #eee;
}

.quick-overlay-select:focus {
  outline: none;
  background-color: #e8e8e8;
}

.quick-location-toggle {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: #666;
  cursor: pointer;
  user-select: none;
}

.quick-location-toggle input {
  width: 12px;
  height: 12px;
  margin: 0;
  cursor: pointer;
}

.quick-location-toggle span {
  line-height: 1;
}

/* Spinning animation for loading indicator */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}

/* Animation preload overlay */
.preload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preload-content {
  background: white;
  border-radius: 12px;
  padding: 24px 32px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  min-width: 200px;
}

.preload-content svg {
  color: #0d6efd;
  margin-bottom: 12px;
}

.preload-text {
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
}

.preload-progress {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.preload-progress-bar {
  height: 100%;
  background: #0d6efd;
  border-radius: 3px;
  transition: width 0.2s ease-out;
}

.preload-percent {
  font-size: 12px;
  color: #666;
}

/* Animation Control Bar */
.animation-control-bar {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: white;
  padding: 6px 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.control-bar-main {
  display: flex;
  align-items: center;
  gap: 4px;
}

.control-bar-timeline {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
  margin-top: 6px;
  border-top: 1px solid #e0e0e0;
  min-width: 280px;
}

.time-display-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  transition: background 0.15s;
}

.time-display-inline:hover {
  background: #f0f0f0;
}

.time-display-inline svg {
  opacity: 0.6;
}

.time-text {
  font-family: monospace;
  font-size: 11px;
}

.playback-buttons {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  padding-left: 8px;
  border-left: 1px solid #e0e0e0;
}

.anim-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #555;
  transition: all 0.15s;
}

.anim-btn:hover:not(:disabled) {
  background: #f0f0f0;
  color: #333;
}

.anim-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.anim-btn.play-btn {
  width: 32px;
  height: 32px;
  background: #0d6efd;
  color: white;
  border-radius: 50%;
}

.anim-btn.play-btn:hover:not(:disabled) {
  background: #0b5ed7;
  color: white;
}

.anim-btn.settings-btn {
  margin-left: 2px;
}

.anim-btn.settings-btn.active,
.anim-btn.expand-btn.active {
  background: #e7f1ff;
  color: #0d6efd;
}

/* Reset to now button (separate) */
.reset-now-btn-separate {
  color: #28a745;
  margin-left: 2px;
}

.reset-now-btn-separate:hover {
  background: #d4edda;
  color: #28a745;
}

/* Timeline Scrubber (legacy, now used in panel) */
.timeline-scrubber {
  position: absolute;
  top: 52px;
  right: 10px;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  min-width: 280px;
}

.timeline-label {
  font-size: 10px;
  font-family: monospace;
  color: #666;
  white-space: nowrap;
  min-width: 40px;
}

.timeline-label.start {
  text-align: right;
}

.timeline-label.end {
  text-align: left;
}

.timeline-slider-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 150px;
}

.timeline-slider {
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: #e0e0e0;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
}

.timeline-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  background: #0d6efd;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: transform 0.1s;
}

.timeline-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.timeline-slider::-webkit-slider-thumb:active {
  cursor: grabbing;
  transform: scale(1.1);
}

.timeline-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  background: #0d6efd;
  border: none;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.timeline-slider::-moz-range-thumb:active {
  cursor: grabbing;
}

.timeline-current-time {
  font-size: 10px;
  font-family: monospace;
  color: #0d6efd;
  font-weight: 600;
  margin-top: 2px;
}

/* Animation Settings Panel */
.animation-settings-panel {
  position: absolute;
  top: 54px;
  right: 10px;
  z-index: 1000;
  width: 280px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  padding: 12px;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.settings-header strong {
  font-size: 13px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.setting-row {
  margin-bottom: 12px;
}

.setting-row:last-child {
  margin-bottom: 0;
}

.setting-row label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #555;
  margin-bottom: 4px;
}

.setting-row .form-range {
  width: 100%;
}

.loop-window-display {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #f8f9fa;
  border-radius: 4px;
  text-align: center;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.slider-label {
  font-size: 11px;
  color: #666;
  width: 35px;
  flex-shrink: 0;
}

.slider-value {
  font-size: 11px;
  color: #333;
  width: 45px;
  text-align: right;
  flex-shrink: 0;
}

.slider-value.slider-time-label {
  width: auto;
  min-width: 120px;
  font-family: monospace;
  font-size: 10px;
}

/* Dual slider row layout */
.dual-slider-row {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.slider-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.slider-col-start {
  align-items: flex-start;
}

.slider-col-end {
  align-items: flex-end;
}

.slider-col input[type="range"] {
  width: 100%;
}

/* Reverse the start slider so dragging left goes further back */
.slider-reversed {
  direction: rtl;
}

.slider-time-label-below {
  font-family: monospace;
  font-size: 9px;
  color: #555;
  margin-top: 2px;
}

.loop-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.loop-debug-info {
  font-size: 8px;
  color: #bbb;
}

.loop-reset-btn {
  font-size: 9px;
  color: #888;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}

.loop-reset-btn:hover {
  color: #555;
}

/* Advanced animation section */
.advanced-section {
  border-top: 1px solid #eee;
  padding-top: 8px;
  margin-top: 4px;
}

.advanced-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: #888;
  font-size: 10px;
  user-select: none;
}

.advanced-toggle:hover {
  color: #555;
}

.advanced-toggle-label {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.advanced-content-anim {
  margin-top: 8px;
}

.setting-row-inner {
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.setting-row-inner:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

/* Tile error banner styling */
.tile-error-banner {
  position: absolute;
  top: 10px;
  right: 10px;
  left: auto;
  z-index: 1100;
  max-width: 400px;
  margin: 0;
  padding: 10px 15px;
  font-size: 13px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}

.tile-error-banner strong {
  display: inline;
}

/* Layer error badge indicator */
.layer-error-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  flex-shrink: 0;
}

/* API key warning in layer list */
.api-key-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  margin-bottom: 4px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 11px;
}

.api-key-warning svg {
  flex-shrink: 0;
  color: #856404;
}

/* Model badge in layer list */
.model-badge {
  display: inline-block;
  background: #6c757d;
  color: white;
  font-size: 9px;
  padding: 1px 4px;
  border-radius: 3px;
  margin-left: 4px;
  vertical-align: middle;
  font-weight: 500;
}

/* Marker list styles */
.marker-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.marker-item:hover {
  background-color: #f0f0f0;
}

.marker-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.marker-coords {
  flex-grow: 1;
  font-size: 12px;
  color: #333;
}

.marker-copy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  margin-left: 4px;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  border-radius: 3px;
  flex-shrink: 0;
}

.marker-copy-btn:hover {
  background-color: #e2e6ea;
  color: #495057;
}

.marker-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  margin-left: 4px;
  background: none;
  border: none;
  color: #dc3545;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  border-radius: 3px;
  flex-shrink: 0;
}

.marker-remove-btn:hover {
  background-color: #fee2e2;
}

.map-view {
  width: 100%;
  height: 100%;
}

.map-controls {
  position: absolute;
  top: 10px;
  left: 50px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  max-width: 300px;
  transition: transform 0.1s ease, opacity 0.1s ease;
}

/* Hide controls when collapsed on desktop */
.map-controls:not(.controls-open) {
  transform: translateX(-110%);
  opacity: 0;
  pointer-events: none;
}

.map-controls.controls-open {
  transform: translateX(0);
  opacity: 1;
  pointer-events: auto;
}

.controls-toggle {
  display: none;
  position: absolute;
  top: 10px;
  left: 10px;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  font-size: 20px;
  z-index: 1001;
  cursor: pointer;
}





.controls-panel {
  padding: 15px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.control-group {
  margin-bottom: 15px;
}

.control-group label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}
.control-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.control-row .control-group {
  margin-bottom: 0;
}

.control-group.compact {
  margin-bottom: 8px;
}

.control-group.compact label {
  margin-bottom: 3px;
}
.layer-list {
  max-height: 250px;
  overflow-y: auto;
}

.layer-item {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.layer-item:last-child {
  border-bottom: none;
}

.layer-controls {
  padding-left: 28px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-range-sm {
  height: 0.5rem;
}

.time-display {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 15px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 6px;
}

.time-display:hover {
  background: white;
}

.datetime-picker-modal,
.add-layer-modal {
  position: fixed;  /* Changed from absolute to fixed */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content-custom {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  min-width: 280px;
  max-width: 90vw;
  position: relative;  /* Ensure content is above backdrop */
  z-index: 2001;
}

.modal-content-custom h6 {
  margin-bottom: 15px;
  font-weight: 600;
}

.advanced-header {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
}

.advanced-header:hover {
  opacity: 0.8;
}

.advanced-content {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
}

.url-pattern-code {
  display: block;
  padding: 8px;
  background: #2d3748;
  color: #e2e8f0;
  border-radius: 4px;
  font-size: 11px;
  word-break: break-all;
  white-space: pre-wrap;
  line-height: 1.4;
}

.url-pattern-item {
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 8px;
}

.url-pattern-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.legend-container {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  max-width: 320px;
  z-index: 1000;
}

.legend-header {
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.legend-collapsed .legend-header {
  border-radius: 8px;
}

.legend-content {
  padding: 5px 0;
  max-height: 300px;
  overflow-y: auto;
}

.legend-item {
  border-bottom: 1px solid #eee;
}

.legend-item:last-child {
  border-bottom: none;
}

.legend-item-header {
  padding: 8px 15px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.legend-item-header:hover {
  background: #f8f9fa;
}

.legend-item-title-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.legend-item-title {
  font-size: 12px;
  font-weight: 500;
}

.legend-model-run {
  font-size: 10px;
  color: #888;
  margin-top: 1px;
}

.legend-model-run div {
  margin-top: 0 !important;
}

.legend-item-content {
  padding: 5px 15px 10px 15px;
}
.time-skip-control {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
}

.time-skip-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.time-skip-display.auto-mode {
  opacity: 0.7;
}

.time-skip-value {
  display: flex;
  align-items: baseline;
  gap: 2px;
  min-width: 60px;
  justify-content: center;
}

.skip-number {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}
.controls-panel::-webkit-scrollbar,
.layer-list::-webkit-scrollbar,
.legend-content::-webkit-scrollbar {
  width: 6px;
}

.controls-panel::-webkit-scrollbar-track,
.layer-list::-webkit-scrollbar-track,
.legend-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.controls-panel::-webkit-scrollbar-thumb,
.layer-list::-webkit-scrollbar-thumb,
.legend-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

/* Mobile collapsible control styles */
.mobile-expand-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  width: 100%;
  border-radius: 4px;
  transition: background 0.15s;
}

.mobile-expand-btn:hover {
  background: #f0f0f0;
}

.mobile-expand-btn.anim-toggle {
  padding: 4px 8px;
}

.mobile-expand-label,
.mobile-time-preview {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-time-preview {
  font-family: monospace;
  font-size: 11px;
}

/* Mobile collapsed state - show only toggle button */
.quick-select-control.mobile-collapsed {
  padding: 2px;
}

.animation-control-bar.mobile-collapsed {
  padding: 2px 4px;
}

@media (max-width: 768px) {
  .controls-toggle {
    display: block;
  }
  
  .map-controls {
    top: 60px;
    left: 10px;
    max-width: 280px;
    z-index: 1100;  /* Lower z-index so modals appear above */
  }
  
  .layers-quick-toggle {
    display: none;
  }
  
  .controls-panel {
    max-height: calc(100vh - 20px);
  }
  
  /* On mobile, shift quick-select below the controls panel when open */
  .quick-select-control.controls-panel-open {
    left: 10px;
    top: auto;
    bottom: 10px;
  }
  
  .time-display {
    font-size: 12px;
    padding: 6px 10px;
  }
  
  .animation-control-bar {
    padding: 4px 8px;
  }
  
  .time-text {
    font-size: 10px;
  }
  
  .anim-btn {
    width: 24px;
    height: 24px;
  }
  
  .anim-btn.play-btn {
    width: 28px;
    height: 28px;
  }
  
  .animation-settings-panel {
    width: 260px;
    right: 5px;
  }
  
  .legend-container {
    bottom: 10px;
    right: 10px;
    max-width: 200px;
  }
  
  .legend-content {
    max-height: 150px;
  }
}

/* Mobile/tablet view at 1000px - where toggle controls appear */
@media (max-width: 1000px) {
  /* Position layer control to the right of zoom buttons */
  .quick-select-control {
    left: 50px; /* Right of zoom buttons */
    top: 10px;
    right: auto;
    max-width: calc(100vw - 60px);
  }
  
  .quick-select-control.mobile-collapsed {
    max-width: fit-content;
  }
  
  /* Animation control at top-right */
  .animation-control-bar {
    right: 10px;
    top: 10px;
    left: auto;
  }
  
  .animation-control-bar.mobile-collapsed {
    max-width: fit-content;
  }
  
  /* When layer expanded, full width but leave room for zoom */
  .quick-select-control:not(.mobile-collapsed) {
    max-width: calc(100vw - 60px);
  }
}

@media (max-width: 480px) {
  .map-controls {
    max-width: calc(100vw - 20px);
  }
  
  .time-display {
    right: 60px;
  }
  
  /* Mobile: position layer control to right of zoom buttons */
  .quick-select-control {
    left: 50px; /* Right of zoom buttons */
    top: 10px;
    max-width: calc(100vw - 60px);
  }
  
  /* When layer control is expanded, full width but leave room for zoom */
  .quick-select-control:not(.mobile-collapsed) {
    max-width: calc(100vw - 60px);
  }
  
  .animation-control-bar {
    right: 10px;
    top: 10px;
    left: auto;
    max-width: calc(100vw - 20px);
  }
  
  /* When animation is collapsed, shrink to fit */
  .animation-control-bar.mobile-collapsed {
    max-width: fit-content;
  }
  
  /* When layer is collapsed, shrink to fit */
  .quick-select-control.mobile-collapsed {
    max-width: fit-content;
  }
  
  .time-display-inline {
    padding: 2px 4px;
  }
  
  .time-text {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .animation-settings-panel {
    width: calc(100vw - 20px);
    right: 10px;
    left: 10px;
  }
  
  /* Mobile secondary row - stack vertically */
  .quick-select-secondary {
    flex-wrap: wrap;
  }
  
  .quick-select-spacer {
    display: none;
  }
}
</style>


<style>
/* Leaflet layers control button - not scoped */
.layers-control-btn {
  width: 30px !important;
  height: 30px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #333;
}

.layers-control-btn:hover {
  background-color: #f4f4f4;
  color: #333;
}

.layers-control-btn svg {
  width: 16px;
  height: 16px;
}
.city-marker-popup .leaflet-popup-content-wrapper {
  padding: 4px 8px;
  border-radius: 6px;
}

.city-marker-popup .leaflet-popup-content {
  margin: 6px;
  font-size: 12px;
}

.city-marker-popup .leaflet-popup-tip-container {
  display: none;
}
</style>